import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import IVFLabPrint from './IVFLabPrint';
import {
  Grid, Card, Box, Typography, Button, IconButton, Breadcrumbs, Link,
  Menu, MenuItem, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary, AccordionDetails, TextField,
  Tooltip, InputAdornment, Select, FormControl, InputLabel, FormHelperText, Divider,
  Checkbox, FormControlLabel, CircularProgress, Radio, RadioGroup,
} from '@mui/material';
import {
  Add as AddIcon, Home as HomeIcon, Science as ScienceIcon, AcUnit as CryoIcon,
  LocalHospital as TransferIcon, MoreVert as MoreVertIcon, ContentCut as BiopsyIcon,
  Settings as ImplementationIcon, Male as AndrologyIcon, Print as PrintIcon,
  ShowChart as ShowChartIcon, ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon,
  NoteAltOutlined as NoteAltOutlinedIcon, Biotech as BiotechIcon, Warning as WarningIcon,
  Save as SaveIcon, CalendarToday as CalendarIcon, Edit as EditIcon, DeleteOutline as DeleteIcon,
  ChildFriendly as OutcomeIcon, Visibility as ViewIcon, PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import MDBox from 'components/MDBox';
import MDDateTimePicker from 'components/MDDateTimePicker';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';

const LAB_CONDITION_OPTIONS = ['INCUBATOR', 'LAB ENVIRONMENT', 'MEDIA', 'NEW DEVICES', 'OTHERS'];

// ── Indicator keys ───────────────────────────────────────────────────
const IK = {
  FOLLICLES_ABOVE_14MM: 'follicles_above_14mm',
  OOCYTE_RETRIEVED: 'oocyte_retrieved',
  OOCYTE_RECOVERY_RATE: 'oocyte_recovery_rate',
  MII_OOCYTE_COUNT: 'mii_oocyte_count',
  MII_OOCYTE_PROPORTION: 'mii_oocyte_proportion',
  POST_WASH_NO_TAIL_SPERM_COUNT: 'post_wash_no_tail_sperm_count',
  POST_WASH_SPERM_COUNT_UOM: 'post_wash_sperm_count_uom',
  POST_WASH_MOTILITY_RATE: 'post_wash_motility_rate',
  PN1_OOCYTE_COUNT: 'pn1_oocyte_count',
  PN2_OOCYTE_COUNT: 'pn2_oocyte_count',
  PN3_OOCYTE_COUNT: 'pn3_oocyte_count',
  OOCYTE_INSEMINATED: 'oocyte_inseminated',
  PN1_RATE: '1pn_rate',
  PN2_RATE: '2pn_rate',
  PN3_RATE: '3pn_rate',
  // DAY 1 — per Insemination Method (Conventional reuses OOCYTE_INSEMINATED
  // above as its base count, keeping older DAY 1 data readable).
  OOCYTE_INJECTED_DAY1: 'oocyte_injected_day1',
  PN0_OOCYTE_COUNT_CONVENTIONAL: 'pn0_oocyte_count_conventional',
  PN1_OOCYTE_COUNT_CONVENTIONAL: 'pn1_oocyte_count_conventional',
  PN2_OOCYTE_COUNT_CONVENTIONAL: 'pn2_oocyte_count_conventional',
  PN3_OOCYTE_COUNT_CONVENTIONAL: 'pn3_oocyte_count_conventional',
  PN0_RATE_CONVENTIONAL: '0pn_rate_conventional',
  PN1_RATE_CONVENTIONAL: '1pn_rate_conventional',
  PN2_RATE_CONVENTIONAL: '2pn_rate_conventional',
  PN3_RATE_CONVENTIONAL: '3pn_rate_conventional',
  PN0_OOCYTE_COUNT_ICSI: 'pn0_oocyte_count_icsi',
  PN1_OOCYTE_COUNT_ICSI: 'pn1_oocyte_count_icsi',
  PN2_OOCYTE_COUNT_ICSI: 'pn2_oocyte_count_icsi',
  PN3_OOCYTE_COUNT_ICSI: 'pn3_oocyte_count_icsi',
  PN0_RATE_ICSI: '0pn_rate_icsi',
  PN1_RATE_ICSI: '1pn_rate_icsi',
  PN2_RATE_ICSI: '2pn_rate_icsi',
  PN3_RATE_ICSI: '3pn_rate_icsi',
  // DAY 1 — combined totals, only meaningful (and only saved) when both
  // Conventional and ICSI were selected on DAY 0.
  TOTAL_OOCYTE_BASE_DAY1: 'total_oocyte_base_day1',
  TOTAL_PN0_OOCYTE_COUNT: 'total_pn0_oocyte_count',
  TOTAL_PN1_OOCYTE_COUNT: 'total_pn1_oocyte_count',
  TOTAL_PN2_OOCYTE_COUNT: 'total_pn2_oocyte_count',
  TOTAL_PN3_OOCYTE_COUNT: 'total_pn3_oocyte_count',
  TOTAL_PN0_RATE: 'total_0pn_rate',
  TOTAL_PN1_RATE: 'total_1pn_rate',
  TOTAL_PN2_RATE: 'total_2pn_rate',
  TOTAL_PN3_RATE: 'total_3pn_rate',
  OOCYTE_INJECTED: 'oocyte_injected',
  OOCYTE_DAMAGED: 'oocyte_damaged',
  OOCYTE_DAMAGE_RATE_ICSI: 'oocyte_damage_rate_icsi',
  INSEMINATION_METHOD_ICSI: 'insemination_method_icsi',
  INSEMINATION_METHOD_CONVENTIONAL: 'insemination_method_conventional',
  SEMEN_USER_TYPE: 'semen_user_type',
  SEMEN_TYPE: 'semen_type',
  SEMEN_CONCENTRATION: 'semen_concentration',
  SEMEN_MOTILITY_RATE: 'semen_motility_rate',
  SEMEN_MORPHOLOGY_RATE: 'semen_morphology_rate',
  SEMEN_PREPARATION_METHOD: 'semen_preparation_method',
  OOCYTE_INSEMINATED_CONVENTIONAL: 'oocyte_inseminated_conventional',
  // Legacy — Oocyte Damage Rate doesn't apply to Conventional insemination
  // (no injection step, so nothing to be "damaged" by it); kept only so any
  // already-saved DAY 0 records with this key still read back correctly.
  OOCYTE_DAMAGED_CONVENTIONAL: 'oocyte_damaged_conventional',
  OOCYTE_DAMAGE_RATE_CONVENTIONAL: 'oocyte_damage_rate_conventional',
  // Legacy (pre insemination-method split) DAY 3/4/5 keys — kept for older
  // saved records, no longer written by save.
  EMBRYO_COUNT: 'embryo_count',
  EMBRYO_FORMATION_RATE: 'embryo_formation_rate',
  D4_DAY3_EMBRYO_COUNT: 'd4_day3_embryo_count',
  COMPACTED_EMBRYO_COUNT: 'compacted_embryo_count',
  COMPACTION_RATE: 'compaction_rate',
  BLASTOCYST_COUNT: 'blastocyst_count',
  D5_DAY3_FORMATION_COUNT: 'd5_day3_formation_count',
  USABLE_BLASTOCYST_COUNT: 'usable_blastocyst_count',
  BLASTOCYST_FORMATION_RATE: 'blastocyst_formation_rate',
  USABLE_BLASTOCYST_FORMATION_RATE: 'usable_blastocyst_formation_rate',
  // DAY 3 — per Insemination Method (base 2PN count is DAY 1's own
  // per-method PN2 count) + combined Total — same sum-then-rate approach
  // as DAY 1's Total, not an average of the two method rates.
  EMBRYO_COUNT_CONVENTIONAL: 'embryo_count_conventional',
  EMBRYO_FORMATION_RATE_CONVENTIONAL: 'embryo_formation_rate_conventional',
  EMBRYO_COUNT_ICSI: 'embryo_count_icsi',
  EMBRYO_FORMATION_RATE_ICSI: 'embryo_formation_rate_icsi',
  TOTAL_EMBRYO_COUNT: 'total_embryo_count',
  TOTAL_EMBRYO_FORMATION_RATE: 'total_embryo_formation_rate',
  // DAY 3 — Good Quality Embryo, per Insemination Method + combined Total.
  // Rate is against that same day/method's own "No of Embryos" count (not
  // the 2PN base), same relationship as Embryo Formation Rate is to its base.
  GOOD_QUALITY_EMBRYO_COUNT_CONVENTIONAL: 'good_quality_embryo_count_conventional',
  GOOD_QUALITY_EMBRYO_RATE_CONVENTIONAL: 'good_quality_embryo_rate_conventional',
  GOOD_QUALITY_EMBRYO_COUNT_ICSI: 'good_quality_embryo_count_icsi',
  GOOD_QUALITY_EMBRYO_RATE_ICSI: 'good_quality_embryo_rate_icsi',
  TOTAL_GOOD_QUALITY_EMBRYO_COUNT: 'total_good_quality_embryo_count',
  TOTAL_GOOD_QUALITY_EMBRYO_RATE: 'total_good_quality_embryo_rate',
  // DAY 4 — per Insemination Method (base is DAY 3's own per-method Good
  // Quality Embryo count) + combined Total.
  D4_DAY3_EMBRYO_COUNT_CONVENTIONAL: 'd4_day3_embryo_count_conventional',
  COMPACTED_EMBRYO_COUNT_CONVENTIONAL: 'compacted_embryo_count_conventional',
  COMPACTION_RATE_CONVENTIONAL: 'compaction_rate_conventional',
  D4_DAY3_EMBRYO_COUNT_ICSI: 'd4_day3_embryo_count_icsi',
  COMPACTED_EMBRYO_COUNT_ICSI: 'compacted_embryo_count_icsi',
  COMPACTION_RATE_ICSI: 'compaction_rate_icsi',
  TOTAL_D4_DAY3_EMBRYO_COUNT: 'total_d4_day3_embryo_count',
  TOTAL_COMPACTED_EMBRYO_COUNT: 'total_compacted_embryo_count',
  TOTAL_COMPACTION_RATE: 'total_compaction_rate',
  // DAY 5 — per Insemination Method (base is DAY 3's own per-method Good
  // Quality Embryo count) + combined Total.
  D5_DAY3_FORMATION_COUNT_CONVENTIONAL: 'd5_day3_formation_count_conventional',
  BLASTOCYST_COUNT_CONVENTIONAL: 'blastocyst_count_conventional',
  BLASTOCYST_FORMATION_RATE_CONVENTIONAL: 'blastocyst_formation_rate_conventional',
  USABLE_BLASTOCYST_COUNT_CONVENTIONAL: 'usable_blastocyst_count_conventional',
  USABLE_BLASTOCYST_FORMATION_RATE_CONVENTIONAL: 'usable_blastocyst_formation_rate_conventional',
  D5_DAY3_FORMATION_COUNT_ICSI: 'd5_day3_formation_count_icsi',
  BLASTOCYST_COUNT_ICSI: 'blastocyst_count_icsi',
  BLASTOCYST_FORMATION_RATE_ICSI: 'blastocyst_formation_rate_icsi',
  USABLE_BLASTOCYST_COUNT_ICSI: 'usable_blastocyst_count_icsi',
  USABLE_BLASTOCYST_FORMATION_RATE_ICSI: 'usable_blastocyst_formation_rate_icsi',
  TOTAL_D5_DAY3_FORMATION_COUNT: 'total_d5_day3_formation_count',
  TOTAL_BLASTOCYST_COUNT: 'total_blastocyst_count',
  TOTAL_BLASTOCYST_FORMATION_RATE: 'total_blastocyst_formation_rate',
  TOTAL_USABLE_BLASTOCYST_COUNT: 'total_usable_blastocyst_count',
  TOTAL_USABLE_BLASTOCYST_FORMATION_RATE: 'total_usable_blastocyst_formation_rate',
  LAB_CONDITION_DEVIATION: 'lab_condition_deviation',
};

// ── Cryo indicator keys ──────────────────────────────────────────────────
const CIK = {
  STORAGE_MEDIA: 'cryo_storage_media',
  STORAGE_LN_LEVEL: 'cryo_storage_ln_level',
  // Repeatable Vitrification entry (replaces the old fixed DAY-0 form).
  //   target = JSON{ dayNo, noPerStraw, reserved, rescued }
  //   indicator_value = type ('Oocyte' | 'Embryo' | 'Blastocyst')
  //   remarks = notes. Percentage (rescued/reserved) is derived, not stored.
  VITRI_ENTRY: 'cryo_vitri_entry',
  // Legacy fixed-key vitrification fields — no longer written by the form
  // (superseded by VITRI_ENTRY above), kept so older saved records display.
  VITRI_D0_OOCYTE: 'cryo_vitri_d0_oocyte',
  VITRI_D0_RESCUED_THAWING: 'cryo_vitri_d0_rescued_thawing',
  VITRI_D0_CRYO_PCT: 'cryo_vitri_d0_cryo_pct',
  VITRI_D34_EMBRYO: 'cryo_vitri_d34_embryo',
  VITRI_D3_EMBRYO_CRYO: 'cryo_vitri_d3_embryo_cryo',
  VITRI_D3_RESCUED: 'cryo_vitri_d3_rescued',
  VITRI_D3_REFROZE: 'cryo_vitri_d3_refroze',
  VITRI_D3_RESCUED_RATE: 'cryo_vitri_d3_rescued_rate',
  VITRI_D4_EMBRYO_CRYO: 'cryo_vitri_d4_embryo_cryo',
  VITRI_D4_RESCUED: 'cryo_vitri_d4_rescued',
  VITRI_D4_REFROZE: 'cryo_vitri_d4_refroze',
  VITRI_D4_RESCUED_RATE: 'cryo_vitri_d4_rescued_rate',
  VITRI_D5_BLASTOCYST: 'cryo_vitri_d5_blastocyst',
  VITRI_D5_EMBRYO_CRYO: 'cryo_vitri_d5_embryo_cryo',
  VITRI_D5_RESCUED: 'cryo_vitri_d5_rescued',
  VITRI_D5_REFROZE: 'cryo_vitri_d5_refroze',
  VITRI_D5_RESCUED_RATE: 'cryo_vitri_d5_rescued_rate',
  // CAN → Canister (1:1) → CANE (many) → Straw (many). Each level is its
  // own repeatable EAV row (real DB id), linked to its parent via a small
  // JSON blob in `target` (a TEXT column — safe from the indicator_value
  // VARCHAR(255) truncation risk a single nested JSON blob would hit).
  //   CAN row:   target = canType ('SELF'|'ART Bank'), indicator_value = canisterNo, remarks = notes
  //   CANE row:  target = JSON{ canId, tag, goblet },  indicator_value = caneNo,     remarks = notes
  //   STRAW row: target = JSON{ caneId, preservedAt, color, embryoCount, used, usedAt, rescuedDay, rescuedCount }, indicator_value = strawNo, remarks = usage notes
  //     rescuedDay/rescuedCount are only set once `used` — which cryo stage
  //     (0/3/4/5) this straw's embryos were frozen at, and how many of its
  //     `embryoCount` survived thawing. Rolled up procedure-wide per day
  //     into a rescue rate (see strawRescueStatsByDay).
  VITRI_D5_CAN_ENTRY: 'cryo_vitri_d5_can_entry',
  VITRI_D5_CANE_ENTRY: 'cryo_vitri_d5_cane_entry',
  VITRI_D5_STRAW_ENTRY: 'cryo_vitri_d5_straw_entry',
  WARMING_OOCYTE: 'cryo_warming_oocyte_pct',
  WARMING_EMBRYO: 'cryo_warming_embryo_pct',
  WARMING_BLASTO: 'cryo_warming_blasto_pct',
  SURVIVAL_OOCYTE: 'cryo_survival_oocyte_pct',
  SURVIVAL_EMBRYO: 'cryo_survival_embryo_pct',
  SURVIVAL_BLASTO: 'cryo_survival_blasto_pct',
  // Optional — set only if the embryologist actually enters them.
  DATE_CRYOPRESERVATION: 'cryo_date_cryopreservation',
  DATE_THAWING: 'cryo_date_thawing',
};
const CRYO_MEDIA_OPTIONS = ['New', 'Regular', 'Others'];
const CRYO_CAN_OPTIONS = ['SELF', 'ART Bank'];
const CRYO_TAG_COLORS = ['Blue', 'Green', 'Orange', 'Red', 'White', 'Ash', 'Pink', 'Yellow', 'Purple'];
const CRYO_COLOR_MAP = { Blue: '#2196f3', Green: '#4caf50', Orange: '#ff9800', Red: '#f44336', White: '#e0e0e0', Ash: '#9e9e9e', Pink: '#e91e63', Yellow: '#fdd835', Purple: '#9c27b0' };
const MAX_STRAW_BATCH = 50;
// Which cryopreservation stage a straw's embryos were frozen at, asked when
// marking the straw used — feeds the rescue-rate rollup (rescued vs stored
// embryos, summed procedure-wide per day) in the Cryopreservation card.
const STRAW_RESCUE_DAY_OPTIONS = [0, 3, 4, 5];

// ── Semen Preservation indicator keys ──────────────────────────────────
// ENTRY → exactly two VIAL children (Vial 1/Vial 2, auto-created together
// with the entry — semen is conventionally split into 2 vials per freeze
// event, unlike embryo/oocyte straws which are a user-chosen batch count).
//   ENTRY row: target = JSON{ canNo, canisterNo, preservedAt, beforeCount, beforeMotility }, indicator_value = mediaUsed, remarks = notes
//     beforeCount/beforeMotility = sperm count (million/ml) + motility (%) measured BEFORE cryopreservation.
//   VIAL row:  target = JSON{ entryId, used, usedAt, prepMethod, postWashCount, afterMotility }, indicator_value = vialNo ('1'|'2'), remarks = usage notes
//     postWashCount doubles as the after-thawing count; afterMotility = motility (%) measured AFTER thawing.
const SPIK = {
  ENTRY: 'semen_pres_entry',
  VIAL: 'semen_pres_vial',
};
const SEMEN_PREP_METHOD_OPTIONS = ['Density Gradient', 'Direct Swimup', 'Wash and Centrifugation'];
// Cell type for a Vitrification entry (Cryopreservation → Vitrification).
const VITRI_TYPE_OPTIONS = ['Oocyte', 'Embryo', 'Blastocyst'];
// Units of measure for DAY 0 PI "Post Wash Motile Sperm Count". The sentinel
// value 'FEW' (stored in the count field itself) means "too few to quantify" —
// no UOM applies then.
const POST_WASH_SPERM_UOM_OPTIONS = ['million/ml', 'thousand/ml'];
const POST_WASH_SPERM_FEW = 'FEW';
const BIK = {
  TUBING_RATE: 'biopsy_tubing_rate',
  PGT_TYPE: 'biopsy_pgt_type',
  EUPLOIDY_RATE: 'biopsy_euploidy_rate',
};
const TIK = {
  EMBRYO_COUNT: 'transfer_embryo_count',
  EMBRYO_DAYS: 'transfer_embryo_days',
  EMBRYO_TYPE: 'transfer_embryo_type',
  EMBRYO_GRADE: 'transfer_embryo_grade',
  TRANSFER_PROCESS: 'transfer_process',
  TRANSFER_TECHNIQUE: 'transfer_technique',
};
const PGT_OPTIONS = ['Invasive', 'Non-Invasive'];
const EMBRYO_COUNT_OPTIONS = ['1', '2', '3'];
const EMBRYO_DAYS_OPTIONS = ['DAY 3', 'DAY 4', 'DAY 5'];
const EMBRYO_TYPE_OPTIONS = ['Fresh', 'Frozen'];
const EMBRYO_GRADE_OPTIONS = ['Grade 1', 'Grade 2'];
const IIK = {
  EMBRYOS_TRANSFERRED: 'impl_embryos_transferred',
  EMBRYOS_IMPLANTED: 'impl_embryos_implanted',
  IMPLEMENTATION: 'impl_implementation',
  REMARKS: 'impl_remarks',
};
const IMPL_OPTIONS = ['Successful', 'Failed', 'Partial'];
const TRANSFER_PROCESS_OPTIONS = ['Smooth', 'Difficult'];
const TRANSFER_TECHNIQUE_MAP = {
  Smooth: ['Quick and Uneventful'],
  Difficult: ['Prolonged', 'Blood and Mucous at Catheter Tip', 'Painful', 'Retained Embryo'],
};
const OIK = { RECORD: 'outcome_record' };
const PREGNANCY_TYPE_OPTIONS = ['Biochemical', 'Clinical'];
const CLINICAL_SUBTYPE_OPTIONS = ['Singleton', 'Multiple Gestation', 'Miscarriage', 'Live Birth'];

const AIK = {
  COUNT: 'andrology_count',
  MOTILITY: 'andrology_motility',
  PROGRESSIVE_MOTILITY: 'andrology_progressive_motility',
  MORPHOLOGY: 'andrology_morphology',
  DFI: 'andrology_dna_fragmentation',
  DFI_BEFORE: 'andrology_dna_before_treatment',
  DFI_AFTER: 'andrology_dna_after_treatment',
  REMARKS: 'andrology_remarks',
};

const getDFIClass = (val) => {
  const s = String(val === null || val === undefined ? '' : val).trim();
  if (!s) return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  if (n <= 15) return { label: 'Normal', bg: '#e8f5e9', color: '#2e7d32', border: '#2e7d32' };
  if (n <= 21) return { label: 'Normal', bg: '#e3f2fd', color: '#1565c0', border: '#1565c0' };
  return { label: 'High', bg: '#fce4ec', color: '#c62828', border: '#c62828' };
};

const _sfv = (v) => String(v === null || v === undefined ? '' : v).trim() || null;
const _mkr = (day, key, value, remarks) => ({ day_number: day, indicator_key: key, indicator_value: _sfv(value), remarks: _sfv(remarks) });

/* ── Helpers ─────────────────────────────────────────────────────────── */
function ActionBox({ label, onClick }) {
  const theme = useTheme();
  const pc = theme.palette.logo?.main || '#00798E';
  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1.5px dashed #d0d7de', borderRadius: 2, minHeight: 100,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 1, cursor: 'pointer', backgroundColor: '#fff',
        transition: 'border-color 0.2s', '&:hover': { borderColor: pc },
      }}
    >
      <Box sx={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px solid #c8d0d8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AddIcon sx={{ fontSize: 16, color: '#adb5bd' }} />
      </Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: pc }}>{label}</Typography>
    </Box>
  );
}
ActionBox.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired };

const ANDROLOGY_REPORT_MAX_SIZE = 2 * 1024 * 1024; // eslint-disable-line no-magic-numbers

// PDF-only upload box (circle + plus) with a date-wise list of previously
// uploaded files, each with a "Preview" action that opens the PDF in a new tab.
function AndrologyReportUpload({ label, files, uploading, onUpload }) {
  const theme = useTheme();
  const pc = theme.palette.logo?.main || '#00798E';
  const inputId = `andrology-upload-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      Swal.fire({ icon: 'warning', title: 'PDF only', text: 'Only PDF files can be uploaded.', confirmButtonColor: pc });
      return;
    }
    if (file.size > ANDROLOGY_REPORT_MAX_SIZE) {
      Swal.fire({ icon: 'warning', title: 'File too large', text: 'Maximum file size is 2 MB.', confirmButtonColor: pc });
      return;
    }
    onUpload(file);
  };

  return (
    <Box sx={{ py: 1.25 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 1 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <input id={inputId} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={handleFileChange} disabled={uploading} />
        <Box component="label" htmlFor={inputId} sx={{
          width: 74, height: 74, flexShrink: 0,
          border: '1.5px dashed #d0d7de', borderRadius: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
          cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1,
          backgroundColor: '#fff', transition: 'border-color 0.2s', '&:hover': { borderColor: uploading ? '#d0d7de' : pc },
        }}>
          {uploading ? (
            <CircularProgress size={20} sx={{ color: pc }} />
          ) : (
            <>
              <Box sx={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid #c8d0d8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AddIcon sx={{ fontSize: 15, color: '#adb5bd' }} />
              </Box>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 500, color: pc, textAlign: 'center', lineHeight: 1.1 }}>Upload PDF</Typography>
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 200 }}>
          {files.length === 0 ? (
            <Typography sx={{ fontSize: '0.72rem', color: '#adb5bd', fontStyle: 'italic', pt: 2 }}>No reports uploaded yet.</Typography>
          ) : (
            files.map((f) => (
              <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.6, borderBottom: '1px solid #f5f5f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <PdfIcon sx={{ fontSize: 17, color: '#d32f2f', flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767' }}>
                      {new Date(f.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#adb5bd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }} title={f.file_name}>
                      {f.file_name}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Preview">
                  <IconButton size="small" onClick={() => window.open(`${f.base_url}${f.file_path}`, '_blank', 'noopener,noreferrer')} sx={{ color: pc, flexShrink: 0 }}>
                    <ViewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
AndrologyReportUpload.propTypes = {
  label: PropTypes.string.isRequired,
  files: PropTypes.array,
  uploading: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
};
AndrologyReportUpload.defaultProps = { files: [], uploading: false };

function SectionCard({ icon: Icon, title, actionLabel, onAction }) {
  const theme = useTheme();
  const pc = theme.palette.logo?.main || '#00798E';
  const tc = theme.palette.text?.main || '#344767';
  return (
    <Card sx={{ border: '1px solid #e9ecef', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: `${pc}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 15, color: pc }} />
        </Box>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: tc }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <ActionBox label={actionLabel} onClick={onAction} />
      </Box>
    </Card>
  );
}
SectionCard.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, actionLabel: PropTypes.string.isRequired, onAction: PropTypes.func.isRequired };

/* ── Section badge (RI / PI / KPI label within a day) ─────────────────── */
function SectionBadge({ label, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}>
      <Box sx={{ width: 3, height: 16, borderRadius: 1, backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}
SectionBadge.propTypes = { label: PropTypes.string.isRequired, color: PropTypes.string.isRequired };

/* ── Semen Preservation demographic card (Patient / Spouse / Donor) ─────
   ABORh (blood group) gets its own prominent slot instead of being folded
   into the code/age/DOB line — a missing ABORh is a real gap someone needs
   to notice and go fill in, not just a quietly absent detail. Labeled
   "ABORh" (not "Blood Group") to match the source investigation param's
   own name. */
function PersonDemoCard({ label, person, compact }) {
  const labelSize = compact ? '0.58rem' : '0.65rem';
  const nameSize = compact ? '0.78rem' : '0.82rem';
  const subSize = compact ? '0.63rem' : '0.68rem';
  return (
    <Box sx={{ flex: '1 1 180px', border: '1px solid #eef1f4', borderRadius: 1.5, p: compact ? 1.25 : 1.5, backgroundColor: '#fcfcfd' }}>
      <Typography sx={{ fontSize: labelSize, fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.3px', mb: 0.3 }}>{label}</Typography>
      {person ? (
        <>
          <Typography sx={{ fontSize: nameSize, fontWeight: 700, color: '#344767' }}>{person.name || 'N/A'}</Typography>
          <Typography sx={{ fontSize: subSize, color: '#7b809a', mt: 0.2, mb: 0.6 }}>
            {[person.code, person.age ? `${person.age} yrs` : null, person.dob].filter(Boolean).join(' · ') || 'No further details available'}
          </Typography>
          {person.bloodGroup ? (
            <Chip label={`ABORh: ${person.bloodGroup}`} size="small" sx={{ fontSize: compact ? '0.62rem' : '0.68rem', height: compact ? 20 : 22, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 800 }} />
          ) : (
            <Typography sx={{
              display: 'inline-block', fontSize: compact ? '0.66rem' : '0.72rem', fontWeight: 800, letterSpacing: '0.3px',
              color: '#fff', backgroundColor: '#d32f2f', px: 0.9, py: 0.35, borderRadius: 1,
            }}>
              ⚠ ABORH NOT AVAILABLE
            </Typography>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: subSize, color: '#9e9e9e' }}>No {label.toLowerCase()} attached to this procedure.</Typography>
      )}
    </Box>
  );
}
PersonDemoCard.propTypes = {
  label: PropTypes.string.isRequired,
  person: PropTypes.shape({ name: PropTypes.string, code: PropTypes.string, dob: PropTypes.string, age: PropTypes.number, bloodGroup: PropTypes.string }),
  compact: PropTypes.bool,
};
PersonDemoCard.defaultProps = { person: null, compact: false };

/* ── Percent field pair ──────────────────────────────────────────────── */
function PctField({ label, valueKey, remarksKey, form, onValueChange, onRemarksChange, unit }) {
  return (
    <>
      <Grid item xs={12} sm={unit ? 12 : 6} md={unit ? 6 : 4}>
        <TextField
          fullWidth size="small"
          type={unit ? 'number' : 'number'}
          label={unit ? label : `${label} (%)`}
          value={form[valueKey]}
          onChange={onValueChange}
          inputProps={{ step: '0.01', min: 0, max: unit ? undefined : 100 }}
          InputProps={unit ? { endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a', whiteSpace: 'nowrap' }}>{unit}</Typography></InputAdornment> } : undefined}
          helperText={unit ? `Value in ${unit}` : 'Value in %, max 2 decimal'}
        />
      </Grid>
      <Grid item xs={12} sm={unit ? 12 : 6} md={unit ? 6 : 8}>
        <TextField
          fullWidth size="small" label="Remarks"
          value={form[remarksKey]}
          onChange={onRemarksChange}
          multiline maxRows={2}
          inputProps={{ style: { textTransform: 'uppercase' } }}
          sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
        />
      </Grid>
    </>
  );
}
PctField.propTypes = {
  label: PropTypes.string.isRequired, valueKey: PropTypes.string.isRequired,
  remarksKey: PropTypes.string.isRequired, form: PropTypes.object.isRequired,
  onValueChange: PropTypes.func.isRequired, onRemarksChange: PropTypes.func.isRequired,
  unit: PropTypes.string,
};
PctField.defaultProps = { unit: null };

/* ── Save button row ─────────────────────────────────────────────────── */
function SaveRow({ onSave, saving, onCancel, label }) {
  const theme = useTheme();
  const pc = theme.palette.logo?.main || '#00798E';
  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#7b809a' }}>
            Cancel
          </Button>
        )}
        <Button
          onClick={onSave} variant="contained" disabled={saving}
          startIcon={saving ? null : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, '& .MuiButton-startIcon': { color: 'white' }, minWidth: 130, fontWeight: 600 }}
        >
          {saving ? 'Saving...' : (label || 'Save')}
        </Button>
      </Box>
    </Grid>
  );
}
SaveRow.propTypes = {
  onSave: PropTypes.func.isRequired, saving: PropTypes.bool.isRequired,
  onCancel: PropTypes.func, label: PropTypes.string,
};
SaveRow.defaultProps = { onCancel: null, label: null };

/* ── Dashboard indicator card ────────────────────────────────────────── */
function IndicatorCard({ rec, pc, tc, fmtLabel, hasVal, showPct, unitOf, progVal, fullWidth, smWidth }) {
  const rv = rec?.indicator_value;
  const hv = hasVal(rv);
  const sp = showPct(rec?.indicator_key);
  // Only append a unit to a numeric value — qualitative entries like "FEW"
  // (Post Wash Motile Sperm Count) stand alone.
  const isNumeric = hv && String(rv).trim() !== '' && !Number.isNaN(Number(rv));
  const dv = hv ? `${rv}${sp ? '%' : (isNumeric ? unitOf(rec?.indicator_key) : '')}` : '—';
  const remarks = String(rec?.remarks || '').trim();
  const progress = hv ? progVal(rec) : 0;
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : smWidth}>
      <Box sx={{ border: '1px solid #eef1f4', borderRadius: 1.5, p: 1.5, backgroundColor: '#fcfcfd', height: '100%' }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#7b809a', fontWeight: 600, lineHeight: 1.3, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {fmtLabel(rec?.indicator_key)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.75 }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: hv ? tc : '#bdbdbd', lineHeight: 1 }}>{dv}</Typography>
          {hv && sp && <Typography sx={{ fontSize: '0.62rem', color: '#7b809a' }}>of 100%</Typography>}
        </Box>
        {sp && (
          <Box sx={{ height: 5, width: '100%', borderRadius: 3, backgroundColor: '#e8e8e8', overflow: 'hidden', mt: 0.25 }}>
            <Box sx={{ height: '100%', width: `${progress}%`, backgroundColor: progress > 60 ? '#4caf50' : progress > 30 ? pc : '#ff9800', borderRadius: 3, transition: 'width 0.4s ease' }} />
          </Box>
        )}
        {remarks && (
          <Tooltip title={remarks} arrow placement="top">
            <Typography sx={{ fontSize: '0.62rem', color: '#7b809a', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help', textDecoration: 'underline dotted' }}>
              📝 {remarks}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Grid>
  );
}
IndicatorCard.propTypes = {
  rec: PropTypes.object, pc: PropTypes.string.isRequired, tc: PropTypes.string.isRequired,
  fmtLabel: PropTypes.func.isRequired, hasVal: PropTypes.func.isRequired,
  showPct: PropTypes.func.isRequired, unitOf: PropTypes.func.isRequired, progVal: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool, smWidth: PropTypes.number,
};
IndicatorCard.defaultProps = { rec: null, fullWidth: false, smWidth: 6 };

/* ── DAY 1 PN-level card (count left, rate right, progress bar, remarks) ── */
// 2PN is normal fertilization (green); 0PN/1PN/3PN are abnormal
// (grey/amber/red) — color coding carries embryology meaning, not just
// decoration. 0PN (no pronuclei visible — fertilization failed) gets its
// own neutral-grey slot, distinct from the amber/red "wrong PN count" cases.
const PN_LEVEL_STYLE = {
  0: { border: '#b0bec5', bg: '#f5f7f8', icon: '#546e7a', bar: '#90a4ae' },
  1: { border: '#ffcc80', bg: '#fff8f0', icon: '#e65100', bar: '#ff9800' },
  2: { border: '#a5d6a7', bg: '#f1f9f1', icon: '#2e7d32', bar: '#4caf50' },
  3: { border: '#ef9a9a', bg: '#fdf2f2', icon: '#c62828', bar: '#f44336' },
};

function PnLevelRow({ level, countRec, rateRec, tc, hasVal, progVal }) {
  const col = PN_LEVEL_STYLE[level];
  const hasCount = hasVal(countRec?.indicator_value);
  const hasRate = hasVal(rateRec?.indicator_value);
  const progress = hasRate ? progVal(rateRec) : 0;
  const remarks = String(rateRec?.remarks || countRec?.remarks || '').trim();
  return (
    <Grid item xs={12}>
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1,
        border: `1px solid ${col.border}`, borderLeft: `5px solid ${col.bar}`,
        borderRadius: 1.5, p: 1.75, backgroundColor: col.bg,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 120 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: col.bar, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.78rem', color: col.icon, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {level}PN Oocyte
          </Typography>
        </Box>

        <Box sx={{ minWidth: 70 }}>
          <Typography sx={{ fontSize: '0.6rem', color: '#7b809a' }}>Count</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: hasCount ? tc : '#bdbdbd', lineHeight: 1.15 }}>
            {hasCount ? countRec.indicator_value : '—'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#7b809a' }}>Rate</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: hasRate ? col.icon : '#bdbdbd' }}>
              {hasRate ? `${rateRec.indicator_value}%` : '—'}
            </Typography>
          </Box>
          {hasRate && (
            <Box sx={{ height: 6, width: '100%', borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.75)', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${progress}%`, backgroundColor: col.bar, borderRadius: 3, transition: 'width 0.4s ease' }} />
            </Box>
          )}
        </Box>

        {remarks && (
          <Box sx={{ flexBasis: '100%', borderTop: `1px dashed ${col.border}`, pt: 0.75, mt: 0.25 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#666', display: 'flex', gap: 0.6 }}>
              <span>📝</span>
              <span>{remarks}</span>
            </Typography>
          </Box>
        )}
      </Box>
    </Grid>
  );
}
PnLevelRow.propTypes = {
  level: PropTypes.number.isRequired, countRec: PropTypes.object, rateRec: PropTypes.object,
  tc: PropTypes.string.isRequired, hasVal: PropTypes.func.isRequired, progVal: PropTypes.func.isRequired,
};
PnLevelRow.defaultProps = { countRec: null, rateRec: null };

/* ── Day accordion header ────────────────────────────────────────────── */
function DayHeader({ day, primaryColor, hasData }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: `${primaryColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CalendarIcon sx={{ fontSize: 16, color: primaryColor }} />
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#344767', lineHeight: 1.1 }}>
          DAY {day}
        </Typography>
        {hasData && (
          <Typography sx={{ fontSize: '0.65rem', color: '#4caf50', fontWeight: 600 }}>● Data saved</Typography>
        )}
      </Box>
    </Box>
  );
}
DayHeader.propTypes = { day: PropTypes.number.isRequired, primaryColor: PropTypes.string.isRequired, hasData: PropTypes.bool.isRequired };

/* ═══════════════════════════════════════════════════════════════════════ */

// eslint-disable-next-line complexity
function IVFLabPerformance() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const pc = theme.palette.logo?.main || '#00798E';       // primary color
  const tc = theme.palette.text?.main || '#344767';       // text color

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('v1-ivf-create-lab-performance');

  const procedure = location.state?.procedure || null;
  const patient = location.state?.patient || null;

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [procedureInfo, setProcedureInfo] = useState(null);
  const [labData, setLabData] = useState(null);
  const [labModalOpen, setLabModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // ── Day-wise forms ─────────────────────────────────────────────────
  const initDay0 = {
    folliclesAbove14mm: '', oocyteRetrieved: '', oocyteRecoveryRemarks: '',
    miiOocyteCount: '', miiOocyteRemarks: '',
    postWashSpermCount: '', postWashSpermRemarks: '', postWashSpermUom: 'million/ml',
    postWashMotilityRate: '', postWashMotilityRemarks: '',
    inseminationIcsi: false, inseminationConventional: false,
    semenUserType: '', semenType: '',
    semenConcentration: '', semenConcentrationRemarks: '',
    semenMotilityRate: '', semenMotilityRemarks: '',
    semenMorphologyRate: '', semenMorphologyRemarks: '',
    semenPrepMethod: '', semenPrepRemarks: '',
    oocyteInjected: '', oocyteDamaged: '', oocyteDamageRateIcsiRemarks: '',
    oocyteInseminatedConventional: '',
  };
  const initDay1 = {
    // Conventional sub-form — base count ("No of MII Oocyte Inseminated") is
    // read-only here, pulled live from DAY 0's Conventional value.
    pn0CountConventional: '', pn1CountConventional: '', pn2CountConventional: '', pn3CountConventional: '',
    pn0RemarksConventional: '', pn1RemarksConventional: '', pn2RemarksConventional: '', pn3RemarksConventional: '',
    // ICSI sub-form — base count ("No of MII Oocyte Injected") is read-only
    // here, pulled live from DAY 0's ICSI value.
    pn0CountIcsi: '', pn1CountIcsi: '', pn2CountIcsi: '', pn3CountIcsi: '',
    pn0RemarksIcsi: '', pn1RemarksIcsi: '', pn2RemarksIcsi: '', pn3RemarksIcsi: '',
  };
  // DAY 3/4/5 — same Conventional/ICSI split as DAY 1, driven by the same
  // DAY 0 checkboxes. DAY 4/5's "No of DAY 3 Good Quality Embryos" base per
  // method isn't re-entered — it's read-only, pulled live from DAY 3's own
  // per-method Good Quality Embryo count fields (not the raw embryo count).
  const initDay3 = {
    embryoCountConventional: '', embryoFormationRemarksConventional: '',
    goodQualityEmbryoCountConventional: '', goodQualityEmbryoRateRemarksConventional: '',
    embryoCountIcsi: '', embryoFormationRemarksIcsi: '',
    goodQualityEmbryoCountIcsi: '', goodQualityEmbryoRateRemarksIcsi: '',
  };
  const initDay4 = {
    compactedEmbryoCountConventional: '', compactionRemarksConventional: '',
    compactedEmbryoCountIcsi: '', compactionRemarksIcsi: '',
  };
  const initDay5 = {
    blastocystCountConventional: '', blastocystFormationRemarksConventional: '',
    usableBlastocystCountConventional: '', usableBlastocystFormationRemarksConventional: '',
    blastocystCountIcsi: '', blastocystFormationRemarksIcsi: '',
    usableBlastocystCountIcsi: '', usableBlastocystFormationRemarksIcsi: '',
  };

  const [day0Form, setDay0Form] = useState(initDay0);
  const [day1Form, setDay1Form] = useState(initDay1);
  const [day3Form, setDay3Form] = useState(initDay3);
  const [day4Form, setDay4Form] = useState(initDay4);
  const [day5Form, setDay5Form] = useState(initDay5);
  const [savingDay, setSavingDay] = useState({ 0: false, 1: false, 3: false, 4: false, 5: false });
  const [day0KpiTab, setDay0KpiTab] = useState(null);
  const [day1DashboardTab, setDay1DashboardTab] = useState(null);
  const [day3DashboardTab, setDay3DashboardTab] = useState(null);
  const [day4DashboardTab, setDay4DashboardTab] = useState(null);
  const [day5DashboardTab, setDay5DashboardTab] = useState(null);

  // ── Lab Condition ──────────────────────────────────────────────────
  const [labConditionModalOpen, setLabConditionModalOpen] = useState(false);
  const [savingLabCondition, setSavingLabCondition] = useState(false);
  const [lcForm, setLcForm] = useState({
    deviationType: '',
    co2: '', temperature: '', o2: '', recordTime: null,
    voc: '', humidity: '',
    remarks: '',
  });
  const [lcFieldErrors, setLcFieldErrors] = useState({});
  const [lcEditId, setLcEditId] = useState(null);

  // ── Cryopreservation ──────────────────────────────────────────────────
  const [cryoModalOpen, setCryoModalOpen] = useState(false);
  // Splits the Cryopreservation modal into two tabs — Embryo/Oocyte
  // (Dates/Storage/Vitrification/CAN-CANE-Straw) and Semen Preservation —
  // instead of one long flat accordion stack.
  const [cryoModalTab, setCryoModalTab] = useState('embryo');
  const [cryoDashboardTab, setCryoDashboardTab] = useState('embryo');
  const [savingCryo, setSavingCryo] = useState({ dates: false, storage: false, vitriD0: false, vitriD3: false, vitriD4: false, vitriD5: false, warming: false, survival: false });
  // Repeatable Vitrification entries (inline "Add More" rows). Each row:
  // { id: <record id | null>, _k, type, dayNo, noPerStraw, reserved, rescued, remarks }
  const [vitriEntries, setVitriEntries] = useState([]);
  const [savingVitri, setSavingVitri] = useState(false);
  const [cryoForm, setCryoForm] = useState({
    cryoDate: null, cryoDateRemarks: '',
    thawDate: null, thawDateRemarks: '',
    csMedia: '', csMediaOthers: '', csMediaRemarks: '',
    csLnLevel: '', csLnLevelRemarks: '',
    vD0Oocyte: '', vD0RescuedThawing: '', vD0OocyteRemarks: '',
    vD3EmbryoCryo: '', vD3Rescued: '', vD3Refroze: '', vD3RescuedRemarks: '',
    vD4EmbryoCryo: '', vD4Rescued: '', vD4Refroze: '', vD4RescuedRemarks: '',
    vD5EmbryoCryo: '', vD5Rescued: '', vD5Refroze: '', vD5RescuedRemarks: '',
    wOocyte: '', wOocyteRemarks: '',
    wEmbryo: '', wEmbryoRemarks: '',
    wBlasto: '', wBlastoRemarks: '',
    srOocyte: '', srOocyteRemarks: '',
    srEmbryo: '', srEmbryoRemarks: '',
    srBlasto: '', srBlastoRemarks: '',
  });

  // ── DAY-5 CAN → Canister → CANE → Straw hierarchy ──────────────────────
  const [expandedCanId, setExpandedCanId] = useState(null);
  const [expandedCaneId, setExpandedCaneId] = useState(null);

  const [canModalOpen, setCanModalOpen] = useState(false);
  const [savingCan, setSavingCan] = useState(false);
  const [canEditId, setCanEditId] = useState(null);
  const [canForm, setCanForm] = useState({ canType: '', canisterNo: '', remarks: '' });

  const [caneModalOpen, setCaneModalOpen] = useState(false);
  const [savingCane, setSavingCane] = useState(false);
  const [caneEditId, setCaneEditId] = useState(null);
  const [caneParentCanId, setCaneParentCanId] = useState(null);
  const [caneForm, setCaneForm] = useState({ caneNo: '', tag: '', goblet: '', remarks: '' });

  // "Add Straws" — bulk creation: user gives a straw count, that many rows
  // appear (Straw No/Color/No of Embryos each), one Preservation Date shared
  // by the whole batch (they're normally frozen in the same event).
  const [strawParentCaneId, setStrawParentCaneId] = useState(null);
  const [strawModalOpen, setStrawModalOpen] = useState(false);
  const [savingStrawBatch, setSavingStrawBatch] = useState(false);
  const [strawBatchCount, setStrawBatchCount] = useState('');
  const [strawBatchPreservedAt, setStrawBatchPreservedAt] = useState(null);
  const [strawBatchPreserveError, setStrawBatchPreserveError] = useState(false);
  const [strawBatchRows, setStrawBatchRows] = useState([]);

  // Edit / Mark-used — always targets one already-existing straw.
  const [strawEditModalOpen, setStrawEditModalOpen] = useState(false);
  const [savingStraw, setSavingStraw] = useState(false);
  const [strawEditId, setStrawEditId] = useState(null);
  const [strawFieldError, setStrawFieldError] = useState(false);
  const [strawPreserveError, setStrawPreserveError] = useState(false);
  // Once a straw was already saved as used, the "used" checkbox is locked
  // (checked, disabled) — marking a straw used is a one-way action.
  const [strawLockedUsed, setStrawLockedUsed] = useState(false);
  const [strawForm, setStrawForm] = useState({ strawNo: '', preservedAt: null, color: '', embryoCount: '', used: false, usedAt: null, rescuedDay: '', rescuedCount: '', remarks: '' });

  // ── Semen Preservation — Entry → exactly two Vials ─────────────────────
  const [expandedSemenEntryId, setExpandedSemenEntryId] = useState(null);
  const [donorInfo, setDonorInfo] = useState(null);
  // Real Blood Group source — Biochem investigation's "ABORh" result
  // (irm_contact_histories), not the irm_procedure_contacts.blood_group
  // vitals field (that's a separate, often-unfilled legacy field kept only
  // as a fallback). Latest value across ALL visits, not scoped to one.
  const [aborhBloodGroups, setAborhBloodGroups] = useState({ patient: null, spouse: null, donor: null });

  const [semenEntryModalOpen, setSemenEntryModalOpen] = useState(false);
  const [savingSemenEntry, setSavingSemenEntry] = useState(false);
  const [semenEntryEditId, setSemenEntryEditId] = useState(null);
  const [semenEntryPreserveError, setSemenEntryPreserveError] = useState(false);
  const [semenEntryVialCountError, setSemenEntryVialCountError] = useState(false);
  // mediaUsed is one of CRYO_MEDIA_OPTIONS ('New'/'Regular'/'Others') — same
  // media options as normal (embryo/oocyte) Cryopreservation's Cryo Storage
  // section, not a separate list. 'Others' free text goes in mediaUsedOthers,
  // same encode-as-'Others:text' trick as cryoForm.csMedia.
  // vialCount ('1'|'2') picks how many vials to auto-create with this entry —
  // only asked/used on create; an existing entry's vials are never added to
  // or removed via this flow, so it's irrelevant once editing.
  const [semenEntryForm, setSemenEntryForm] = useState({ mediaUsed: '', mediaUsedOthers: '', canNo: '', canisterNo: '', preservedAt: null, vialCount: '', beforeCount: '', beforeMotility: '', remarks: '' });

  // Edit / Mark-used — always targets one already-existing vial (the pair is
  // auto-created with its parent entry, never added/removed individually).
  const [semenVialModalOpen, setSemenVialModalOpen] = useState(false);
  const [savingSemenVial, setSavingSemenVial] = useState(false);
  const [semenVialEditId, setSemenVialEditId] = useState(null);
  const [semenVialFieldError, setSemenVialFieldError] = useState(false);
  // Once a vial was already saved as used, the "used" checkbox is locked
  // (checked, disabled) — marking a vial used is a one-way action.
  const [semenVialLockedUsed, setSemenVialLockedUsed] = useState(false);
  const [semenVialForm, setSemenVialForm] = useState({ vialNo: '', entryId: null, used: false, usedAt: null, prepMethod: '', postWashCount: '', afterMotility: '', remarks: '' });

  // ── Embryo Biopsy ─────────────────────────────────────────────────────
  const [biopsyModalOpen, setBiopsyModalOpen] = useState(false);
  const [savingBiopsy, setSavingBiopsy] = useState(false);
  const [biopsyForm, setBiopsyForm] = useState({
    tubingRate: '', tubingRateRemarks: '',
    pgtType: '',
    euploidyRate: '', euploidyRateRemarks: '',
  });

  // ── Embryo Transfer ───────────────────────────────────────────────────
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [savingTransfer, setSavingTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({
    embryoCount: '', embryoCountRemarks: '',
    embryoDays: '', embryoDaysRemarks: '',
    embryoType: '', embryoTypeRemarks: '',
    embryoGrade: '', embryoGradeRemarks: '',
    transferProcess: '', transferTechnique: '',
  });

  // ── Implementation ────────────────────────────────────────────────────
  const [implModalOpen, setImplModalOpen] = useState(false);
  const [savingImpl, setSavingImpl] = useState(false);
  const [implForm, setImplForm] = useState({
    embryosTransferred: '', embryosTransferredRemarks: '',
    embryosImplanted: '', embryosImplantedRemarks: '',
    implementation: '',
    remarks: '',
  });

  // ── Andrology ─────────────────────────────────────────────────────────
  const [andrologyModalOpen, setAndrologyModalOpen] = useState(false);
  const [savingAndrology, setSavingAndrology] = useState(false);
  const [andrologyForm, setAndrologyForm] = useState({
    count: '', motility: '', progressiveMotility: '', morphology: '',
    dfi: '', dfiBeforeTreatment: '', dfiAfterTreatment: '', remarks: '',
  });
  const [andrologyFiles, setAndrologyFiles] = useState({ semen_analysis: [], dna_fragmentation: [] });
  const [uploadingAndrology, setUploadingAndrology] = useState({ semen_analysis: false, dna_fragmentation: false });

  // ── Outcome ───────────────────────────────────────────────────────────
  const [outcomeModalOpen, setOutcomeModalOpen] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [outcomeEditId, setOutcomeEditId] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({
    pregnancyType: '',
    subType: '',
    miscarriageWeeks: '',
    remarks: '',
  });

  /* ── Print ───────────────────────────────────────────────────────────── */
  const printRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `IVF_Lab_Report_${procedureId}`,
    pageStyle: `
      @page { size: A4; margin: 6mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        * { color: #000 !important; }
      }
    `,
    removeAfterPrint: true,
  });

  // Picks the latest "ABORh" (Biochem section) result out of a contact's
  // full irm_contact_histories list — latest by history_date (falling back
  // to updated_at), irrespective of which visit it was recorded under.
  const latestAborhValue = (histories) => {
    const matches = (Array.isArray(histories) ? histories : []).filter((h) => {
      const memberSlug = h?.param_group_member?.slug || '';
      const label = String(h?.param_group_member_option?.value || h?.name || '').toLowerCase();
      return memberSlug === 'blood-investigation-params-bio-chemistry' && label.includes('aborh');
    });
    if (matches.length === 0) return null;
    matches.sort((a, b) => new Date(b.history_date || b.updated_at || 0) - new Date(a.history_date || a.updated_at || 0));
    // `param_group_member_option.value` is just the test's own label ("ABORh"),
    // not the entered result — the actual blood group typed in lives in `.value`
    // (matches infertilitySummary's biochemistry table: Name column = the label,
    // Value column = h.value).
    return matches[0].value || null;
  };

  /* ── Fetch ─────────────────────────────────────────────────────────── */
  const fetchData = async () => {
    if (!procedureId) return;
    setLoading(true);
    try {
      const [initRes, labRes] = await Promise.all([
        api.ivf.getInitiationData(procedureId),
        api.ivf.getLabPerformance(procedureId),
      ]);
      if (initRes?.flag === 1) {
        setProcedureInfo(initRes.data);
        // Same contact-derivation shape as the render-time cr/sc below, done
        // here since fetchData can't wait for a render to know these ids.
        const d = initRes.data;
        const dcInit = d?.patientData?.detailedContact ?? d?.patientData?.data ?? d?.patientData ?? null;
        const crInit = dcInit?.contact ?? dcInit;
        const sdInit = d?.spouseData?.detailedContact ?? d?.spouseData?.data ?? d?.spouseData ?? null;
        const scInit = sdInit?.contact ?? sdInit;
        const patientContactId = crInit?.id || d?.procedure?.irm_contacts_id;
        const spouseContactId = scInit?.id || d?.procedure?.irm_spouse_contacts_id;
        Promise.all([
          patientContactId ? api.opd.getContactHistory(patientContactId).catch(() => null) : null,
          spouseContactId ? api.opd.getContactHistory(spouseContactId).catch(() => null) : null,
        ]).then(([patientRes, spouseRes]) => {
          setAborhBloodGroups((p) => ({
            ...p,
            patient: latestAborhValue(patientRes?.data?.contact_histories),
            spouse: latestAborhValue(spouseRes?.data?.contact_histories),
          }));
        });
      }
      // Fired separately (not in the Promise.all above) — a procedure with
      // no attached donor 404s, and that's a normal, expected state here,
      // not a page-load failure.
      api.ivf.getProcedureDonor(procedureId, 'M')
        .then((r) => {
          const donorRec = r?.data || null;
          setDonorInfo(donorRec);
          const donorContactId = donorRec?.contact?.irm_contacts_id || donorRec?.irm_contacts_id;
          if (donorContactId) {
            api.opd.getContactHistory(donorContactId)
              .then((hRes) => setAborhBloodGroups((p) => ({ ...p, donor: latestAborhValue(hRes?.data?.contact_histories) })))
              .catch(() => {});
          }
        })
        .catch(() => setDonorInfo(null));
      if (labRes?.flag === 1) {
        setLabData(labRes.data);
        // Loaded up front (not only when the Andrology modal opens) so the
        // dashboard card can show uploaded reports without an extra click.
        loadAndrologyFiles(labRes.data?.id);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [procedureId]);

  const stub = (title) => Swal.fire({ icon: 'info', title, text: 'Coming soon', confirmButtonColor: pc });

  /* ── Records helpers ────────────────────────────────────────────────── */
  const allRecords = Array.isArray(labData?.records) ? labData.records : [];
  const getR = (key, day) => allRecords.find((r) => Number(r?.day_number) === day && String(r?.indicator_key || '').toLowerCase() === String(key).toLowerCase());
  // Media values (Cryo Storage's csMedia, Semen Preservation's mediaUsed) are
  // saved as 'Others:<free text>' when the "Others" option is picked — this
  // decodes that for display, same as openCryoModal's isOthers check.
  const fmtMedia = (v) => (v && String(v).startsWith('Others:') ? `Others: ${String(v).slice(7).trim()}` : v);
  const hasDay = (day) => allRecords.some((r) => Number(r?.day_number) === day && String(r?.indicator_key || '').toLowerCase() !== IK.LAB_CONDITION_DEVIATION);
  const labConditionRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase() === IK.LAB_CONDITION_DEVIATION);
  const cryoRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('cryo_'));
  const getCryo = (key) => cryoRecords.find((r) => r.indicator_key === key);
  const parseTarget = (rec) => { try { return JSON.parse(rec?.target || '{}'); } catch { return {}; } };
  const vitriEntryRecords = cryoRecords.filter((r) => r.indicator_key === CIK.VITRI_ENTRY);
  const canRecords = cryoRecords.filter((r) => r.indicator_key === CIK.VITRI_D5_CAN_ENTRY);
  const caneRecords = cryoRecords.filter((r) => r.indicator_key === CIK.VITRI_D5_CANE_ENTRY);
  const strawRecords = cryoRecords.filter((r) => r.indicator_key === CIK.VITRI_D5_STRAW_ENTRY);
  const canesForCan = (canId) => caneRecords.filter((r) => parseTarget(r).canId === canId);
  const strawsForCane = (caneId) => strawRecords.filter((r) => parseTarget(r).caneId === caneId);
  const usedStrawCount = strawRecords.filter((r) => parseTarget(r).used).length;
  // Rescue rate per DAY (0/3/4/5) — pooled procedure-wide across every used
  // straw tagged with that day, not per-CANE: sum of rescued embryos over
  // sum of stored embryos, same sum-then-rate approach used elsewhere in
  // this file (not an average of individual straws' rates).
  const strawRescueStatsByDay = (day) => {
    const straws = strawRecords.filter((r) => {
      const t = parseTarget(r);
      return t.used && Number(t.rescuedDay) === day;
    });
    const totalEmbryos = straws.reduce((sum, r) => sum + (Number(parseTarget(r).embryoCount) || 0), 0);
    const totalRescued = straws.reduce((sum, r) => sum + (Number(parseTarget(r).rescuedCount) || 0), 0);
    return { strawCount: straws.length, totalEmbryos, totalRescued, rate: computeRate(totalRescued, totalEmbryos) };
  };
  const semenPresRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('semen_pres_'));
  const hasSemenPresData = semenPresRecords.length > 0;
  const semenEntryRecords = semenPresRecords.filter((r) => r.indicator_key === SPIK.ENTRY);
  const semenVialRecords = semenPresRecords.filter((r) => r.indicator_key === SPIK.VIAL);
  const vialsForEntry = (entryId) => semenVialRecords.filter((r) => parseTarget(r).entryId === entryId)
    .sort((a, b) => Number(a.indicator_value) - Number(b.indicator_value));
  const usedSemenVialCount = semenVialRecords.filter((r) => parseTarget(r).used).length;
  // Covers both cryo_-prefixed (embryo/oocyte) and semen_pres_-prefixed
  // (semen) records — the Cryopreservation card and modal cover both.
  const hasCryoData = cryoRecords.length > 0 || hasSemenPresData;
  const biopsyRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('biopsy_'));
  const getBiopsy = (key) => biopsyRecords.find((r) => r.indicator_key === key);
  const hasBiopsyData = biopsyRecords.length > 0;
  const transferRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('transfer_'));
  const getTransfer = (key) => transferRecords.find((r) => r.indicator_key === key);
  const hasTransferData = transferRecords.length > 0;
  const implRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('impl_'));
  const getImpl = (key) => implRecords.find((r) => r.indicator_key === key);
  const hasImplData = implRecords.length > 0;
  const outcomeRecords = allRecords.filter((r) => r?.indicator_key === OIK.RECORD);
  const hasOutcomeData = outcomeRecords.length > 0;
  const parseOutcome = (rec) => { try { return JSON.parse(rec?.indicator_value || '{}'); } catch { return {}; } };
  const andrologyRecords = allRecords.filter((r) => String(r?.indicator_key || '').toLowerCase().startsWith('andrology_'));
  const getAndrology = (key) => andrologyRecords.find((r) => r.indicator_key === key);
  const hasAndrologyFiles = andrologyFiles.semen_analysis.length > 0 || andrologyFiles.dna_fragmentation.length > 0;
  const hasAndrologyData = andrologyRecords.length > 0 || hasAndrologyFiles;
  const displayRecords = allRecords.filter((r) => {
    const k = String(r?.indicator_key || '').toLowerCase();
    return k !== IK.LAB_CONDITION_DEVIATION && !k.startsWith('cryo_') && !k.startsWith('biopsy_') && !k.startsWith('transfer_') && !k.startsWith('impl_') && !k.startsWith('outcome_') && !k.startsWith('andrology_');
  });

  const dayWiseRecords = Object.entries(
    displayRecords.reduce((acc, r) => {
      const d = Number(r?.day_number ?? 0);
      if (!acc[d]) acc[d] = [];
      acc[d].push(r);
      return acc;
    }, {}),
  ).map(([d, recs]) => ({ day: Number(d), records: recs })).sort((a, b) => a.day - b.day);

  const activeDay = selectedDay ?? dayWiseRecords[0]?.day ?? null;
  const shownDayRecords = activeDay !== null ? dayWiseRecords.filter((dg) => dg.day === activeDay) : [];

  // Inside the Conventional/ICSI tabs the method is already implied by
  // which tab you're looking at, so drop the redundant suffix from labels
  // that would otherwise read "OOCYTE INSEMINATED CONVENTIONAL".
  const DASHBOARD_LABEL_OVERRIDES = {
    [IK.OOCYTE_INSEMINATED_CONVENTIONAL]: 'MII OOCYTE INSEMINATED',
    [IK.OOCYTE_INJECTED]: 'MII OOCYTE INJECTED',
    [IK.GOOD_QUALITY_EMBRYO_COUNT_CONVENTIONAL]: 'GOOD QUALITY EMBRYO COUNT',
    [IK.GOOD_QUALITY_EMBRYO_RATE_CONVENTIONAL]: 'GOOD QUALITY EMBRYO RATE',
    [IK.GOOD_QUALITY_EMBRYO_COUNT_ICSI]: 'GOOD QUALITY EMBRYO COUNT',
    [IK.GOOD_QUALITY_EMBRYO_RATE_ICSI]: 'GOOD QUALITY EMBRYO RATE',
    // Legacy — no longer entered on DAY 0 (see IK comment), kept only so
    // any already-saved records with this key still display with a clean label.
    [IK.OOCYTE_DAMAGED_CONVENTIONAL]: 'OOCYTE DAMAGED',
    [IK.OOCYTE_DAMAGE_RATE_CONVENTIONAL]: 'OOCYTE DAMAGE RATE',
    [IK.OOCYTE_DAMAGE_RATE_ICSI]: 'OOCYTE DAMAGE RATE',
    [IK.D5_DAY3_FORMATION_COUNT]: 'DAY3 FORMATION COUNT',
  };
  const fmtLabel = (k) => DASHBOARD_LABEL_OVERRIDES[k] || String(k || '').split('_').map((p) => p.toUpperCase()).join(' ');
  const hasVal = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  const pctVal = (v) => { const n = parseFloat(String(v).replace(',', '.')); return isNaN(n) ? null : n; };
  const showPct = (k) => /rate|proportion|percent/.test(String(k || '').toLowerCase());
  const unitOf = (k) => {
    const key = String(k).toLowerCase();
    if (key === IK.POST_WASH_NO_TAIL_SPERM_COUNT) {
      return ` ${getR(IK.POST_WASH_SPERM_COUNT_UOM, 0)?.indicator_value || 'million/ml'}`;
    }
    if (key === IK.SEMEN_CONCENTRATION) return ' million/ml';
    return '';
  };
  const progVal = (r) => { const n = pctVal(r?.indicator_value); if (n === null) return 0; const p = n > 100 && n <= 10000 ? n / 100 : n; return Math.max(0, Math.min(100, p)); };

  /* ── Patient info ────────────────────────────────────────────────────── */
  const dc = procedureInfo?.patientData?.detailedContact ?? procedureInfo?.patientData?.data ?? procedureInfo?.patientData ?? null;
  const cr = dc?.contact ?? dc;
  const phones = dc?.phones ?? procedureInfo?.patientData?.phones ?? [];
  const sd = procedureInfo?.spouseData?.detailedContact ?? procedureInfo?.spouseData?.data ?? procedureInfo?.spouseData ?? null;
  const sc = sd?.contact ?? sd;
  const ap = procedureInfo?.procedure || procedure || null;
  const coupleInfo = dc?.patientCouple ?? dc?.spouseCouple ?? dc?.couple_info ?? null;

  const patientName = cr?.full_name || `${cr?.first_name || ''} ${cr?.last_name || ''}`.trim() || patient?.full_name || ap?.patient_name || 'N/A';
  const spouseName = sc?.full_name || `${sc?.first_name || ''} ${sc?.last_name || ''}`.trim() || cr?.spouse_name || coupleInfo?.spouse_name || 'N/A';
  const procedureDate = ap?.created_at ? new Date(ap.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const procedureCode = ap?.external_code || 'N/A';
  // cr/patient are Contact records — the code field is `external_code`, not
  // `patient_code`/`code` (neither exists on a Contact; this previously
  // always fell through to 'N/A').
  const patientCode = cr?.external_code || patient?.external_code || 'N/A';
  // coupleInfo (above) is already the resolved Couple record; sc is the
  // spouse's own Contact record — both carry `external_code` directly.
  const coupleCode = coupleInfo?.external_code || 'N/A';
  const spouseCode = sc?.external_code || 'N/A';
  const mobile = phones?.[0]?.phone_number || phones?.[0]?.phone || cr?.mobile_number || patient?.mobile_number || 'N/A';
  const procedureCategory = ap?.procedure_type || 'IVF';
  const procedureSubCategory = ap?.procedure_sub_type || 'SELF';
  const procedureStatus = ap?.procedure_status || 'CREATED';
  const isReadOnly = ['CANCELLED', 'COMPLETED'].includes(String(procedureStatus).toUpperCase());

  /* ── Semen Preservation demographics — Patient & Spouse always, Donor
     only when DAY 0's Type of Semen User is 'Donor' ────────────────────── */
  const fmtAge = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  };
  const fmtDOB = (dob) => (dob ? new Date(dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null);
  // blood_group lives on the per-procedure ProcedureContact row (irm_procedure_contacts),
  // not on the base Contact record — same `procedureContacts` array getInitiationData
  // already returns for procedureInitiation's patient-info card.
  const procedureContacts = Array.isArray(procedureInfo?.procedureContacts) ? procedureInfo.procedureContacts : [];
  const bloodGroupFor = (contactId) => procedureContacts.find((pcRec) => pcRec.irm_contacts_id === contactId)?.blood_group || null;

  const patientPerson = {
    name: patientName !== 'N/A' ? patientName : null,
    code: patientCode !== 'N/A' ? patientCode : null,
    dob: fmtDOB(cr?.date_of_birth),
    age: cr?.age ?? fmtAge(cr?.date_of_birth),
    // Real source is the Biochem "ABORh" investigation result (latest across
    // all visits) — irm_procedure_contacts.blood_group is only a fallback
    // for when that hasn't been recorded.
    bloodGroup: aborhBloodGroups.patient || bloodGroupFor(cr?.id || ap?.irm_contacts_id),
  };
  const spousePerson = {
    name: spouseName !== 'N/A' ? spouseName : null,
    code: spouseCode !== 'N/A' ? spouseCode : null,
    dob: fmtDOB(sc?.date_of_birth),
    age: sc?.age ?? fmtAge(sc?.date_of_birth),
    bloodGroup: aborhBloodGroups.spouse || bloodGroupFor(sc?.id || ap?.irm_spouse_contacts_id),
  };
  // getProcedureDonor's response row is flat (first_name/last_name/external_code/
  // date_of_birth aliased directly off a raw SQL join) but is read defensively in
  // case a nested `.contact` shape is ever returned instead.
  const donorContact = donorInfo?.contact || donorInfo || null;
  const donorPerson = donorContact ? {
    name: donorContact.full_name || `${donorContact.first_name || ''} ${donorContact.last_name || ''}`.trim() || null,
    code: donorContact.external_code || null,
    dob: fmtDOB(donorContact.date_of_birth),
    age: donorContact.age ?? fmtAge(donorContact.date_of_birth),
    bloodGroup: aborhBloodGroups.donor || bloodGroupFor(donorInfo?.irm_contacts_id),
  } : null;
  const semenUserType = getR(IK.SEMEN_USER_TYPE, 0)?.indicator_value || '';

  /* ── Field change handlers ──────────────────────────────────────────── */
  const makePctChange = (setter, field) => (e) => {
    const v = e.target.value;
    if (v === '') { setter((p) => ({ ...p, [field]: '' })); return; }
    if (/[eE+-]/.test(v)) return;
    if (!/^\d{0,3}(\.\d{0,2})?$/.test(v)) return;
    if (Number(v) > 100) return;
    setter((p) => ({ ...p, [field]: v }));
  };

  const makeNumChange = (setter, field) => (e) => {
    const v = e.target.value;
    if (v === '') { setter((p) => ({ ...p, [field]: '' })); return; }
    if (/[eE+-]/.test(v)) return;
    if (!/^\d*(\.\d{0,2})?$/.test(v)) return;
    setter((p) => ({ ...p, [field]: v }));
  };

  const makeIntChange = (setter, field) => (e) => {
    const v = e.target.value;
    if (v === '') { setter((p) => ({ ...p, [field]: '' })); return; }
    if (!/^\d+$/.test(v)) return;
    setter((p) => ({ ...p, [field]: v }));
  };

  const makeRemarksChange = (setter, field) => (e) =>
    setter((p) => ({ ...p, [field]: e.target.value.toUpperCase() }));

  const computeRate = (numerator, denominator) => {
    const n = parseInt(numerator, 10);
    const d = parseInt(denominator, 10);
    if (isNaN(n) || isNaN(d) || d === 0) return 0;
    return Math.round((n / d) * 100);
  };

  // Formats an "YYYY-MM-DD HH:mm" straw preservation/usage timestamp.
  const fmtDT = (v) => v ? new Date(String(v).replace(' ', 'T')).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  /*
   * ── Ensure lab performance exists ──────────────────────────────────────
   * Caches the in-flight creation promise so concurrent callers (e.g.
   * picking the Semen Analysis and DNA Fragmentation files back-to-back,
   * both racing in from a still-null labData) await the SAME creation
   * instead of each independently POSTing a new envelope — otherwise the
   * two files end up split across two different lab-performance rows and
   * only one survives to be shown (loadAndrologyFiles fetches by whichever
   * id resolved last).
   */
  const ensureLabIdPromiseRef = useRef(null);
  const ensureLabId = async () => {
    if (labData?.id) return labData.id;
    if (!ensureLabIdPromiseRef.current) {
      ensureLabIdPromiseRef.current = api.ivf
        .createLabPerformance(procedureId, { initiated_at: new Date().toISOString() })
        .then((r) => {
          if (r?.flag !== 1 || !r?.data?.id) throw new Error(r?.message || 'Failed to create lab performance');
          setLabData((p) => p?.id ? p : r.data);
          return r.data.id;
        })
        .finally(() => { ensureLabIdPromiseRef.current = null; });
    }
    return ensureLabIdPromiseRef.current;
  };

  /* ── Open modal and pre-fill ─────────────────────────────────────────── */
  const openModal = () => {
    const rv = (k, d) => getR(k, d)?.indicator_value || '';
    const rr = (k, d) => getR(k, d)?.remarks || '';

    setDay0Form({
      folliclesAbove14mm: rv(IK.FOLLICLES_ABOVE_14MM, 0),
      oocyteRetrieved: rv(IK.OOCYTE_RETRIEVED, 0),
      oocyteRecoveryRemarks: rr(IK.OOCYTE_RECOVERY_RATE, 0),
      miiOocyteCount: rv(IK.MII_OOCYTE_COUNT, 0),
      miiOocyteRemarks: rr(IK.MII_OOCYTE_PROPORTION, 0),
      postWashSpermCount: rv(IK.POST_WASH_NO_TAIL_SPERM_COUNT, 0),
      postWashSpermRemarks: rr(IK.POST_WASH_NO_TAIL_SPERM_COUNT, 0),
      postWashSpermUom: rv(IK.POST_WASH_SPERM_COUNT_UOM, 0) || 'million/ml',
      postWashMotilityRate: rv(IK.POST_WASH_MOTILITY_RATE, 0),
      postWashMotilityRemarks: rr(IK.POST_WASH_MOTILITY_RATE, 0),
      inseminationIcsi: rv(IK.INSEMINATION_METHOD_ICSI, 0) === 'Y',
      inseminationConventional: rv(IK.INSEMINATION_METHOD_CONVENTIONAL, 0) === 'Y',
      semenUserType: rv(IK.SEMEN_USER_TYPE, 0),
      semenType: rv(IK.SEMEN_TYPE, 0),
      semenConcentration: rv(IK.SEMEN_CONCENTRATION, 0),
      semenConcentrationRemarks: rr(IK.SEMEN_CONCENTRATION, 0),
      semenMotilityRate: rv(IK.SEMEN_MOTILITY_RATE, 0),
      semenMotilityRemarks: rr(IK.SEMEN_MOTILITY_RATE, 0),
      semenMorphologyRate: rv(IK.SEMEN_MORPHOLOGY_RATE, 0),
      semenMorphologyRemarks: rr(IK.SEMEN_MORPHOLOGY_RATE, 0),
      semenPrepMethod: rv(IK.SEMEN_PREPARATION_METHOD, 0),
      semenPrepRemarks: rr(IK.SEMEN_PREPARATION_METHOD, 0),
      oocyteInjected: rv(IK.OOCYTE_INJECTED, 0),
      oocyteDamaged: rv(IK.OOCYTE_DAMAGED, 0),
      oocyteDamageRateIcsiRemarks: rr(IK.OOCYTE_DAMAGE_RATE_ICSI, 0),
      oocyteInseminatedConventional: rv(IK.OOCYTE_INSEMINATED_CONVENTIONAL, 0),
    });
    setDay1Form({
      pn0CountConventional: rv(IK.PN0_OOCYTE_COUNT_CONVENTIONAL, 1),
      pn1CountConventional: rv(IK.PN1_OOCYTE_COUNT_CONVENTIONAL, 1),
      pn2CountConventional: rv(IK.PN2_OOCYTE_COUNT_CONVENTIONAL, 1),
      pn3CountConventional: rv(IK.PN3_OOCYTE_COUNT_CONVENTIONAL, 1),
      pn0RemarksConventional: rr(IK.PN0_RATE_CONVENTIONAL, 1),
      pn1RemarksConventional: rr(IK.PN1_RATE_CONVENTIONAL, 1),
      pn2RemarksConventional: rr(IK.PN2_RATE_CONVENTIONAL, 1),
      pn3RemarksConventional: rr(IK.PN3_RATE_CONVENTIONAL, 1),
      pn0CountIcsi: rv(IK.PN0_OOCYTE_COUNT_ICSI, 1),
      pn1CountIcsi: rv(IK.PN1_OOCYTE_COUNT_ICSI, 1),
      pn2CountIcsi: rv(IK.PN2_OOCYTE_COUNT_ICSI, 1),
      pn3CountIcsi: rv(IK.PN3_OOCYTE_COUNT_ICSI, 1),
      pn0RemarksIcsi: rr(IK.PN0_RATE_ICSI, 1),
      pn1RemarksIcsi: rr(IK.PN1_RATE_ICSI, 1),
      pn2RemarksIcsi: rr(IK.PN2_RATE_ICSI, 1),
      pn3RemarksIcsi: rr(IK.PN3_RATE_ICSI, 1),
    });
    setDay3Form({
      embryoCountConventional: rv(IK.EMBRYO_COUNT_CONVENTIONAL, 3),
      embryoFormationRemarksConventional: rr(IK.EMBRYO_FORMATION_RATE_CONVENTIONAL, 3),
      goodQualityEmbryoCountConventional: rv(IK.GOOD_QUALITY_EMBRYO_COUNT_CONVENTIONAL, 3),
      goodQualityEmbryoRateRemarksConventional: rr(IK.GOOD_QUALITY_EMBRYO_RATE_CONVENTIONAL, 3),
      embryoCountIcsi: rv(IK.EMBRYO_COUNT_ICSI, 3),
      embryoFormationRemarksIcsi: rr(IK.EMBRYO_FORMATION_RATE_ICSI, 3),
      goodQualityEmbryoCountIcsi: rv(IK.GOOD_QUALITY_EMBRYO_COUNT_ICSI, 3),
      goodQualityEmbryoRateRemarksIcsi: rr(IK.GOOD_QUALITY_EMBRYO_RATE_ICSI, 3),
    });
    setDay4Form({
      compactedEmbryoCountConventional: rv(IK.COMPACTED_EMBRYO_COUNT_CONVENTIONAL, 4),
      compactionRemarksConventional: rr(IK.COMPACTION_RATE_CONVENTIONAL, 4),
      compactedEmbryoCountIcsi: rv(IK.COMPACTED_EMBRYO_COUNT_ICSI, 4),
      compactionRemarksIcsi: rr(IK.COMPACTION_RATE_ICSI, 4),
    });
    setDay5Form({
      blastocystCountConventional: rv(IK.BLASTOCYST_COUNT_CONVENTIONAL, 5),
      blastocystFormationRemarksConventional: rr(IK.BLASTOCYST_FORMATION_RATE_CONVENTIONAL, 5),
      usableBlastocystCountConventional: rv(IK.USABLE_BLASTOCYST_COUNT_CONVENTIONAL, 5),
      usableBlastocystFormationRemarksConventional: rr(IK.USABLE_BLASTOCYST_FORMATION_RATE_CONVENTIONAL, 5),
      blastocystCountIcsi: rv(IK.BLASTOCYST_COUNT_ICSI, 5),
      blastocystFormationRemarksIcsi: rr(IK.BLASTOCYST_FORMATION_RATE_ICSI, 5),
      usableBlastocystCountIcsi: rv(IK.USABLE_BLASTOCYST_COUNT_ICSI, 5),
      usableBlastocystFormationRemarksIcsi: rr(IK.USABLE_BLASTOCYST_FORMATION_RATE_ICSI, 5),
    });
    setLabModalOpen(true);
  };

  /* ── Save per day ────────────────────────────────────────────────────── */
  const saveDay = async (day) => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }

    if (day === 0) {
      const rr = computeRate(day0Form.oocyteRetrieved, day0Form.folliclesAbove14mm);
      const mr = computeRate(day0Form.miiOocyteCount, day0Form.oocyteRetrieved);
      const dr = computeRate(day0Form.oocyteDamaged, day0Form.oocyteInjected);
      if ((day0Form.oocyteRetrieved || day0Form.folliclesAbove14mm) && rr > 100) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Oocyte Recovery Rate cannot exceed 100%', confirmButtonColor: pc }); return;
      }
      if ((day0Form.miiOocyteCount || day0Form.oocyteRetrieved) && mr > 100) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'MII Oocyte Proportion cannot exceed 100%', confirmButtonColor: pc }); return;
      }
      if (!day0Form.inseminationIcsi && !day0Form.inseminationConventional) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Select at least one Insemination Method — Conventional or ICSI', confirmButtonColor: pc }); return;
      }
      if (!day0Form.semenUserType) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Select a Type of Semen User', confirmButtonColor: pc }); return;
      }
      if (!day0Form.semenType) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Select a Type of Semen', confirmButtonColor: pc }); return;
      }
      if (!day0Form.semenPrepMethod) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Select a Method of Semen Preparation', confirmButtonColor: pc }); return;
      }
      if (day0Form.inseminationIcsi && (day0Form.oocyteDamaged || day0Form.oocyteInjected) && dr > 100) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Oocyte Damage Rate following ICSI cannot exceed 100%', confirmButtonColor: pc }); return;
      }
    }

    if (day === 1) {
      if (!day0Form.inseminationIcsi && !day0Form.inseminationConventional) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Save DAY 0 with an Insemination Method selected before DAY 1', confirmButtonColor: pc }); return;
      }
      const checks = [
        [day0Form.inseminationConventional, day1Pn0RateConventional, day1Form.pn0CountConventional || day1ConventionalBase, '0PN Rate (Conventional)'],
        [day0Form.inseminationConventional, day1Pn1RateConventional, day1Form.pn1CountConventional || day1ConventionalBase, '1PN Rate (Conventional)'],
        [day0Form.inseminationConventional, day1Pn2RateConventional, day1Form.pn2CountConventional || day1ConventionalBase, '2PN Rate (Conventional)'],
        [day0Form.inseminationConventional, day1Pn3RateConventional, day1Form.pn3CountConventional || day1ConventionalBase, '3PN Rate (Conventional)'],
        [day0Form.inseminationIcsi, day1Pn0RateIcsi, day1Form.pn0CountIcsi || day1IcsiBase, '0PN Rate (ICSI)'],
        [day0Form.inseminationIcsi, day1Pn1RateIcsi, day1Form.pn1CountIcsi || day1IcsiBase, '1PN Rate (ICSI)'],
        [day0Form.inseminationIcsi, day1Pn2RateIcsi, day1Form.pn2CountIcsi || day1IcsiBase, '2PN Rate (ICSI)'],
        [day0Form.inseminationIcsi, day1Pn3RateIcsi, day1Form.pn3CountIcsi || day1IcsiBase, '3PN Rate (ICSI)'],
      ];
      for (const [applicable, rate, hasInputs, label] of checks) {
        if (applicable && hasInputs && rate > 100) {
          Swal.fire({ icon: 'error', title: 'Validation Error', text: `${label} cannot exceed 100%`, confirmButtonColor: pc }); return;
        }
      }
    }

    if (day === 3 || day === 4 || day === 5) {
      if (!day0Form.inseminationIcsi && !day0Form.inseminationConventional) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: `Save DAY 0 with an Insemination Method selected before DAY ${day}`, confirmButtonColor: pc }); return;
      }
    }
    if (day === 3) {
      const checks = [
        [day0Form.inseminationConventional, day3EmbryoFormationRateConventional, day3Form.embryoCountConventional || day3ConventionalBase, 'Embryo Formation Rate (Conventional)'],
        [day0Form.inseminationIcsi, day3EmbryoFormationRateIcsi, day3Form.embryoCountIcsi || day3IcsiBase, 'Embryo Formation Rate (ICSI)'],
        [day0Form.inseminationConventional, day3GoodQualityRateConventional, day3Form.goodQualityEmbryoCountConventional || day3Form.embryoCountConventional, 'Good Quality Embryo Rate (Conventional)'],
        [day0Form.inseminationIcsi, day3GoodQualityRateIcsi, day3Form.goodQualityEmbryoCountIcsi || day3Form.embryoCountIcsi, 'Good Quality Embryo Rate (ICSI)'],
      ];
      for (const [applicable, rate, hasInputs, label] of checks) {
        if (applicable && hasInputs && rate > 100) {
          Swal.fire({ icon: 'error', title: 'Validation Error', text: `${label} cannot exceed 100%`, confirmButtonColor: pc }); return;
        }
      }
    }
    if (day === 4) {
      const checks = [
        [day0Form.inseminationConventional, day4CompactionRateConventional, day4Form.compactedEmbryoCountConventional || day4ConventionalBase, 'Compaction Rate (Conventional)'],
        [day0Form.inseminationIcsi, day4CompactionRateIcsi, day4Form.compactedEmbryoCountIcsi || day4IcsiBase, 'Compaction Rate (ICSI)'],
      ];
      for (const [applicable, rate, hasInputs, label] of checks) {
        if (applicable && hasInputs && rate > 100) {
          Swal.fire({ icon: 'error', title: 'Validation Error', text: `${label} cannot exceed 100%`, confirmButtonColor: pc }); return;
        }
      }
    }
    if (day === 5) {
      const checks = [
        [day0Form.inseminationConventional, day5BlastocystRateConventional, day5Form.blastocystCountConventional || day5ConventionalBase, 'Blastocyst Formation Rate (Conventional)'],
        [day0Form.inseminationIcsi, day5BlastocystRateIcsi, day5Form.blastocystCountIcsi || day5IcsiBase, 'Blastocyst Formation Rate (ICSI)'],
        [day0Form.inseminationConventional, day5UsableBlastocystRateConventional, day5Form.usableBlastocystCountConventional || day5ConventionalBase, 'Usable Blastocyst Formation Rate (Conventional)'],
        [day0Form.inseminationIcsi, day5UsableBlastocystRateIcsi, day5Form.usableBlastocystCountIcsi || day5IcsiBase, 'Usable Blastocyst Formation Rate (ICSI)'],
      ];
      for (const [applicable, rate, hasInputs, label] of checks) {
        if (applicable && hasInputs && rate > 100) {
          Swal.fire({ icon: 'error', title: 'Validation Error', text: `${label} cannot exceed 100%`, confirmButtonColor: pc }); return;
        }
      }
    }

    setSavingDay((p) => ({ ...p, [day]: true }));
    try {
      const labId = await ensureLabId();

      const day0Records = (() => {
        const recRate = computeRate(day0Form.oocyteRetrieved, day0Form.folliclesAbove14mm);
        const miiRate = computeRate(day0Form.miiOocyteCount, day0Form.oocyteRetrieved);
        const dmgRate = computeRate(day0Form.oocyteDamaged, day0Form.oocyteInjected);
        const hasRec = day0Form.oocyteRetrieved || day0Form.folliclesAbove14mm;
        const hasMii = day0Form.miiOocyteCount || day0Form.oocyteRetrieved;
        const hasDmg = day0Form.inseminationIcsi && (day0Form.oocyteDamaged || day0Form.oocyteInjected);
        return [
          { day_number: 0, indicator_key: IK.FOLLICLES_ABOVE_14MM, indicator_value: day0Form.folliclesAbove14mm || null, remarks: null },
          { day_number: 0, indicator_key: IK.OOCYTE_RETRIEVED, indicator_value: day0Form.oocyteRetrieved || null, remarks: null },
          { day_number: 0, indicator_key: IK.OOCYTE_RECOVERY_RATE, indicator_value: hasRec ? String(recRate) : null, remarks: day0Form.oocyteRecoveryRemarks || null },
          { day_number: 0, indicator_key: IK.MII_OOCYTE_COUNT, indicator_value: day0Form.miiOocyteCount || null, remarks: null },
          { day_number: 0, indicator_key: IK.MII_OOCYTE_PROPORTION, indicator_value: hasMii ? String(miiRate) : null, remarks: day0Form.miiOocyteRemarks || null },
          { day_number: 0, indicator_key: IK.POST_WASH_NO_TAIL_SPERM_COUNT, indicator_value: day0Form.postWashSpermCount || null, remarks: day0Form.postWashSpermRemarks || null },
          { day_number: 0, indicator_key: IK.POST_WASH_SPERM_COUNT_UOM, indicator_value: (day0Form.postWashSpermCount && day0Form.postWashSpermCount !== POST_WASH_SPERM_FEW) ? (day0Form.postWashSpermUom || 'million/ml') : null, remarks: null },
          { day_number: 0, indicator_key: IK.POST_WASH_MOTILITY_RATE, indicator_value: day0Form.postWashMotilityRate || null, remarks: day0Form.postWashMotilityRemarks || null },
          { day_number: 0, indicator_key: IK.INSEMINATION_METHOD_ICSI, indicator_value: day0Form.inseminationIcsi ? 'Y' : 'N', remarks: null },
          { day_number: 0, indicator_key: IK.INSEMINATION_METHOD_CONVENTIONAL, indicator_value: day0Form.inseminationConventional ? 'Y' : 'N', remarks: null },
          { day_number: 0, indicator_key: IK.SEMEN_USER_TYPE, indicator_value: day0Form.semenUserType || null, remarks: null },
          { day_number: 0, indicator_key: IK.SEMEN_TYPE, indicator_value: day0Form.semenType || null, remarks: null },
          { day_number: 0, indicator_key: IK.SEMEN_CONCENTRATION, indicator_value: day0Form.semenConcentration || null, remarks: day0Form.semenConcentrationRemarks || null },
          { day_number: 0, indicator_key: IK.SEMEN_MOTILITY_RATE, indicator_value: day0Form.semenMotilityRate || null, remarks: day0Form.semenMotilityRemarks || null },
          { day_number: 0, indicator_key: IK.SEMEN_MORPHOLOGY_RATE, indicator_value: day0Form.semenMorphologyRate || null, remarks: day0Form.semenMorphologyRemarks || null },
          { day_number: 0, indicator_key: IK.SEMEN_PREPARATION_METHOD, indicator_value: day0Form.semenPrepMethod || null, remarks: day0Form.semenPrepRemarks || null },
          { day_number: 0, indicator_key: IK.OOCYTE_INJECTED, indicator_value: day0Form.inseminationIcsi ? (day0Form.oocyteInjected || null) : null, remarks: null },
          { day_number: 0, indicator_key: IK.OOCYTE_DAMAGED, indicator_value: day0Form.inseminationIcsi ? (day0Form.oocyteDamaged || null) : null, remarks: null },
          { day_number: 0, indicator_key: IK.OOCYTE_DAMAGE_RATE_ICSI, indicator_value: hasDmg ? String(dmgRate) : null, remarks: day0Form.inseminationIcsi ? (day0Form.oocyteDamageRateIcsiRemarks || null) : null },
          { day_number: 0, indicator_key: IK.OOCYTE_INSEMINATED_CONVENTIONAL, indicator_value: day0Form.inseminationConventional ? (day0Form.oocyteInseminatedConventional || null) : null, remarks: null },
        ];
      })();

      const recordsByDay = {
        0: day0Records,
        1: (() => {
          const conv = day0Form.inseminationConventional;
          const icsi = day0Form.inseminationIcsi;
          const insC = day1ConventionalBase;
          const insI = day1IcsiBase;
          const records = [];
          if (conv) {
            const r0c = computeRate(day1Form.pn0CountConventional, insC);
            const r1c = computeRate(day1Form.pn1CountConventional, insC);
            const r2c = computeRate(day1Form.pn2CountConventional, insC);
            const r3c = computeRate(day1Form.pn3CountConventional, insC);
            records.push(
              { day_number: 1, indicator_key: IK.OOCYTE_INSEMINATED, indicator_value: insC || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN0_OOCYTE_COUNT_CONVENTIONAL, indicator_value: day1Form.pn0CountConventional || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN1_OOCYTE_COUNT_CONVENTIONAL, indicator_value: day1Form.pn1CountConventional || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN2_OOCYTE_COUNT_CONVENTIONAL, indicator_value: day1Form.pn2CountConventional || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN3_OOCYTE_COUNT_CONVENTIONAL, indicator_value: day1Form.pn3CountConventional || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN0_RATE_CONVENTIONAL, indicator_value: (day1Form.pn0CountConventional || insC) ? String(r0c) : null, remarks: day1Form.pn0RemarksConventional || null },
              { day_number: 1, indicator_key: IK.PN1_RATE_CONVENTIONAL, indicator_value: (day1Form.pn1CountConventional || insC) ? String(r1c) : null, remarks: day1Form.pn1RemarksConventional || null },
              { day_number: 1, indicator_key: IK.PN2_RATE_CONVENTIONAL, indicator_value: (day1Form.pn2CountConventional || insC) ? String(r2c) : null, remarks: day1Form.pn2RemarksConventional || null },
              { day_number: 1, indicator_key: IK.PN3_RATE_CONVENTIONAL, indicator_value: (day1Form.pn3CountConventional || insC) ? String(r3c) : null, remarks: day1Form.pn3RemarksConventional || null },
            );
          }
          if (icsi) {
            const r0i = computeRate(day1Form.pn0CountIcsi, insI);
            const r1i = computeRate(day1Form.pn1CountIcsi, insI);
            const r2i = computeRate(day1Form.pn2CountIcsi, insI);
            const r3i = computeRate(day1Form.pn3CountIcsi, insI);
            records.push(
              { day_number: 1, indicator_key: IK.OOCYTE_INJECTED_DAY1, indicator_value: insI || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN0_OOCYTE_COUNT_ICSI, indicator_value: day1Form.pn0CountIcsi || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN1_OOCYTE_COUNT_ICSI, indicator_value: day1Form.pn1CountIcsi || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN2_OOCYTE_COUNT_ICSI, indicator_value: day1Form.pn2CountIcsi || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN3_OOCYTE_COUNT_ICSI, indicator_value: day1Form.pn3CountIcsi || null, remarks: null },
              { day_number: 1, indicator_key: IK.PN0_RATE_ICSI, indicator_value: (day1Form.pn0CountIcsi || insI) ? String(r0i) : null, remarks: day1Form.pn0RemarksIcsi || null },
              { day_number: 1, indicator_key: IK.PN1_RATE_ICSI, indicator_value: (day1Form.pn1CountIcsi || insI) ? String(r1i) : null, remarks: day1Form.pn1RemarksIcsi || null },
              { day_number: 1, indicator_key: IK.PN2_RATE_ICSI, indicator_value: (day1Form.pn2CountIcsi || insI) ? String(r2i) : null, remarks: day1Form.pn2RemarksIcsi || null },
              { day_number: 1, indicator_key: IK.PN3_RATE_ICSI, indicator_value: (day1Form.pn3CountIcsi || insI) ? String(r3i) : null, remarks: day1Form.pn3RemarksIcsi || null },
            );
          }
          if (conv && icsi) {
            records.push(
              { day_number: 1, indicator_key: IK.TOTAL_OOCYTE_BASE_DAY1, indicator_value: day1TotalBase ? String(day1TotalBase) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN0_OOCYTE_COUNT, indicator_value: day1TotalPn0 ? String(day1TotalPn0) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN1_OOCYTE_COUNT, indicator_value: day1TotalPn1 ? String(day1TotalPn1) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN2_OOCYTE_COUNT, indicator_value: day1TotalPn2 ? String(day1TotalPn2) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN3_OOCYTE_COUNT, indicator_value: day1TotalPn3 ? String(day1TotalPn3) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN0_RATE, indicator_value: day1TotalBase ? String(day1TotalPn0Rate) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN1_RATE, indicator_value: day1TotalBase ? String(day1TotalPn1Rate) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN2_RATE, indicator_value: day1TotalBase ? String(day1TotalPn2Rate) : null, remarks: null },
              { day_number: 1, indicator_key: IK.TOTAL_PN3_RATE, indicator_value: day1TotalBase ? String(day1TotalPn3Rate) : null, remarks: null },
            );
          }
          return records;
        })(),
        3: (() => {
          const conv = day0Form.inseminationConventional;
          const icsi = day0Form.inseminationIcsi;
          const baseC = day3ConventionalBase;
          const baseI = day3IcsiBase;
          const records = [];
          if (conv) {
            const rC = computeRate(day3Form.embryoCountConventional, baseC);
            const gqC = computeRate(day3Form.goodQualityEmbryoCountConventional, day3Form.embryoCountConventional);
            records.push(
              { day_number: 3, indicator_key: IK.EMBRYO_COUNT_CONVENTIONAL, indicator_value: day3Form.embryoCountConventional || null, remarks: null },
              { day_number: 3, indicator_key: IK.EMBRYO_FORMATION_RATE_CONVENTIONAL, indicator_value: (day3Form.embryoCountConventional || baseC) ? String(rC) : null, remarks: day3Form.embryoFormationRemarksConventional || null },
              { day_number: 3, indicator_key: IK.GOOD_QUALITY_EMBRYO_COUNT_CONVENTIONAL, indicator_value: day3Form.goodQualityEmbryoCountConventional || null, remarks: null },
              { day_number: 3, indicator_key: IK.GOOD_QUALITY_EMBRYO_RATE_CONVENTIONAL, indicator_value: (day3Form.goodQualityEmbryoCountConventional || day3Form.embryoCountConventional) ? String(gqC) : null, remarks: day3Form.goodQualityEmbryoRateRemarksConventional || null },
            );
          }
          if (icsi) {
            const rI = computeRate(day3Form.embryoCountIcsi, baseI);
            const gqI = computeRate(day3Form.goodQualityEmbryoCountIcsi, day3Form.embryoCountIcsi);
            records.push(
              { day_number: 3, indicator_key: IK.EMBRYO_COUNT_ICSI, indicator_value: day3Form.embryoCountIcsi || null, remarks: null },
              { day_number: 3, indicator_key: IK.EMBRYO_FORMATION_RATE_ICSI, indicator_value: (day3Form.embryoCountIcsi || baseI) ? String(rI) : null, remarks: day3Form.embryoFormationRemarksIcsi || null },
              { day_number: 3, indicator_key: IK.GOOD_QUALITY_EMBRYO_COUNT_ICSI, indicator_value: day3Form.goodQualityEmbryoCountIcsi || null, remarks: null },
              { day_number: 3, indicator_key: IK.GOOD_QUALITY_EMBRYO_RATE_ICSI, indicator_value: (day3Form.goodQualityEmbryoCountIcsi || day3Form.embryoCountIcsi) ? String(gqI) : null, remarks: day3Form.goodQualityEmbryoRateRemarksIcsi || null },
            );
          }
          if (conv && icsi) {
            records.push(
              { day_number: 3, indicator_key: IK.TOTAL_EMBRYO_COUNT, indicator_value: day3TotalEmbryoCount ? String(day3TotalEmbryoCount) : null, remarks: null },
              { day_number: 3, indicator_key: IK.TOTAL_EMBRYO_FORMATION_RATE, indicator_value: day1TotalPn2 ? String(day3TotalEmbryoFormationRate) : null, remarks: null },
              { day_number: 3, indicator_key: IK.TOTAL_GOOD_QUALITY_EMBRYO_COUNT, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : null, remarks: null },
              { day_number: 3, indicator_key: IK.TOTAL_GOOD_QUALITY_EMBRYO_RATE, indicator_value: day3TotalEmbryoCount ? String(day3TotalGoodQualityEmbryoRate) : null, remarks: null },
            );
          }
          return records;
        })(),
        4: (() => {
          const conv = day0Form.inseminationConventional;
          const icsi = day0Form.inseminationIcsi;
          const baseC = day4ConventionalBase;
          const baseI = day4IcsiBase;
          const records = [];
          if (conv) {
            const rC = computeRate(day4Form.compactedEmbryoCountConventional, baseC);
            records.push(
              { day_number: 4, indicator_key: IK.D4_DAY3_EMBRYO_COUNT_CONVENTIONAL, indicator_value: baseC || null, remarks: null },
              { day_number: 4, indicator_key: IK.COMPACTED_EMBRYO_COUNT_CONVENTIONAL, indicator_value: day4Form.compactedEmbryoCountConventional || null, remarks: null },
              { day_number: 4, indicator_key: IK.COMPACTION_RATE_CONVENTIONAL, indicator_value: (day4Form.compactedEmbryoCountConventional || baseC) ? String(rC) : null, remarks: day4Form.compactionRemarksConventional || null },
            );
          }
          if (icsi) {
            const rI = computeRate(day4Form.compactedEmbryoCountIcsi, baseI);
            records.push(
              { day_number: 4, indicator_key: IK.D4_DAY3_EMBRYO_COUNT_ICSI, indicator_value: baseI || null, remarks: null },
              { day_number: 4, indicator_key: IK.COMPACTED_EMBRYO_COUNT_ICSI, indicator_value: day4Form.compactedEmbryoCountIcsi || null, remarks: null },
              { day_number: 4, indicator_key: IK.COMPACTION_RATE_ICSI, indicator_value: (day4Form.compactedEmbryoCountIcsi || baseI) ? String(rI) : null, remarks: day4Form.compactionRemarksIcsi || null },
            );
          }
          if (conv && icsi) {
            records.push(
              { day_number: 4, indicator_key: IK.TOTAL_D4_DAY3_EMBRYO_COUNT, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : null, remarks: null },
              { day_number: 4, indicator_key: IK.TOTAL_COMPACTED_EMBRYO_COUNT, indicator_value: day4TotalCompactedEmbryoCount ? String(day4TotalCompactedEmbryoCount) : null, remarks: null },
              { day_number: 4, indicator_key: IK.TOTAL_COMPACTION_RATE, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day4TotalCompactionRate) : null, remarks: null },
            );
          }
          return records;
        })(),
        5: (() => {
          const conv = day0Form.inseminationConventional;
          const icsi = day0Form.inseminationIcsi;
          const baseC = day5ConventionalBase;
          const baseI = day5IcsiBase;
          const records = [];
          if (conv) {
            const bC = computeRate(day5Form.blastocystCountConventional, baseC);
            const uC = computeRate(day5Form.usableBlastocystCountConventional, baseC);
            records.push(
              { day_number: 5, indicator_key: IK.D5_DAY3_FORMATION_COUNT_CONVENTIONAL, indicator_value: baseC || null, remarks: null },
              { day_number: 5, indicator_key: IK.BLASTOCYST_COUNT_CONVENTIONAL, indicator_value: day5Form.blastocystCountConventional || null, remarks: null },
              { day_number: 5, indicator_key: IK.BLASTOCYST_FORMATION_RATE_CONVENTIONAL, indicator_value: (day5Form.blastocystCountConventional || baseC) ? String(bC) : null, remarks: day5Form.blastocystFormationRemarksConventional || null },
              { day_number: 5, indicator_key: IK.USABLE_BLASTOCYST_COUNT_CONVENTIONAL, indicator_value: day5Form.usableBlastocystCountConventional || null, remarks: null },
              { day_number: 5, indicator_key: IK.USABLE_BLASTOCYST_FORMATION_RATE_CONVENTIONAL, indicator_value: (day5Form.usableBlastocystCountConventional || baseC) ? String(uC) : null, remarks: day5Form.usableBlastocystFormationRemarksConventional || null },
            );
          }
          if (icsi) {
            const bI = computeRate(day5Form.blastocystCountIcsi, baseI);
            const uI = computeRate(day5Form.usableBlastocystCountIcsi, baseI);
            records.push(
              { day_number: 5, indicator_key: IK.D5_DAY3_FORMATION_COUNT_ICSI, indicator_value: baseI || null, remarks: null },
              { day_number: 5, indicator_key: IK.BLASTOCYST_COUNT_ICSI, indicator_value: day5Form.blastocystCountIcsi || null, remarks: null },
              { day_number: 5, indicator_key: IK.BLASTOCYST_FORMATION_RATE_ICSI, indicator_value: (day5Form.blastocystCountIcsi || baseI) ? String(bI) : null, remarks: day5Form.blastocystFormationRemarksIcsi || null },
              { day_number: 5, indicator_key: IK.USABLE_BLASTOCYST_COUNT_ICSI, indicator_value: day5Form.usableBlastocystCountIcsi || null, remarks: null },
              { day_number: 5, indicator_key: IK.USABLE_BLASTOCYST_FORMATION_RATE_ICSI, indicator_value: (day5Form.usableBlastocystCountIcsi || baseI) ? String(uI) : null, remarks: day5Form.usableBlastocystFormationRemarksIcsi || null },
            );
          }
          if (conv && icsi) {
            records.push(
              { day_number: 5, indicator_key: IK.TOTAL_D5_DAY3_FORMATION_COUNT, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : null, remarks: null },
              { day_number: 5, indicator_key: IK.TOTAL_BLASTOCYST_COUNT, indicator_value: day5TotalBlastocystCount ? String(day5TotalBlastocystCount) : null, remarks: null },
              { day_number: 5, indicator_key: IK.TOTAL_BLASTOCYST_FORMATION_RATE, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day5TotalBlastocystRate) : null, remarks: null },
              { day_number: 5, indicator_key: IK.TOTAL_USABLE_BLASTOCYST_COUNT, indicator_value: day5TotalUsableBlastocystCount ? String(day5TotalUsableBlastocystCount) : null, remarks: null },
              { day_number: 5, indicator_key: IK.TOTAL_USABLE_BLASTOCYST_FORMATION_RATE, indicator_value: day3TotalGoodQualityEmbryoCount ? String(day5TotalUsableBlastocystRate) : null, remarks: null },
            );
          }
          return records;
        })(),
      };

      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records: recordsByDay[day] });
      if (res?.flag !== 1) throw new Error(res?.message || 'Save failed');

      Swal.fire({ icon: 'success', title: 'Saved', text: `DAY ${day} data saved successfully`, confirmButtonColor: pc, timer: 1800, showConfirmButton: false });
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingDay((p) => ({ ...p, [day]: false }));
    }
  };

  const EMPTY_LC_FORM = { deviationType: '', co2: '', temperature: '', o2: '', recordTime: null, voc: '', humidity: '', remarks: '' };

  // Each Lab Condition type asks for a different set of fields — this is
  // the single source of truth for what's mandatory per type, used both to
  // block save and to drive which * labels/errors show in the modal.
  // Record Time is mandatory for every type, not just Incubator.
  const validateLabCondition = () => {
    const errs = {};
    if (!lcForm.deviationType) { errs.deviationType = true; return errs; }
    if (!lcForm.recordTime) errs.recordTime = true;
    if (lcForm.deviationType === 'INCUBATOR') {
      if (!String(lcForm.co2).trim()) errs.co2 = true;
      if (!String(lcForm.temperature).trim()) errs.temperature = true;
    } else if (lcForm.deviationType === 'LAB ENVIRONMENT') {
      if (!String(lcForm.voc).trim()) errs.voc = true;
      if (!String(lcForm.humidity).trim()) errs.humidity = true;
      if (!String(lcForm.temperature).trim()) errs.temperature = true;
    } else if (lcForm.deviationType === 'OTHERS') {
      if (!lcForm.remarks.trim()) errs.remarks = true;
    }
    return errs;
  };

  /* ── Save lab condition ──────────────────────────────────────────────── */
  const saveLabCondition = async () => {
    const errs = validateLabCondition();
    if (Object.keys(errs).length > 0) { setLcFieldErrors(errs); return; }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingLabCondition(true);
    try {
      let res;
      let target;
      if (lcForm.deviationType === 'INCUBATOR') {
        target = JSON.stringify({ co2: lcForm.co2.trim(), temperature: lcForm.temperature.trim(), o2: lcForm.o2.trim(), recordTime: lcForm.recordTime });
      } else if (lcForm.deviationType === 'LAB ENVIRONMENT') {
        target = JSON.stringify({ voc: lcForm.voc.trim(), humidity: lcForm.humidity.trim(), temperature: lcForm.temperature.trim(), recordTime: lcForm.recordTime });
      } else {
        target = JSON.stringify({ recordTime: lcForm.recordTime });
      }
      const payload = { target, indicator_value: lcForm.deviationType || null, remarks: lcForm.remarks.trim() || null };
      if (lcEditId) {
        res = await api.ivf.updateLabPerformanceRecord(lcEditId, payload);
      } else {
        const labId = await ensureLabId();
        res = await api.ivf.createLabPerformanceRecord(labId, { day_number: 0, indicator_key: IK.LAB_CONDITION_DEVIATION, ...payload });
      }
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', text: lcEditId ? 'Lab condition record updated' : 'Lab condition recorded', confirmButtonColor: pc, timer: 1800, showConfirmButton: false });
      setLabConditionModalOpen(false);
      setLcForm(EMPTY_LC_FORM);
      setLcFieldErrors({});
      setLcEditId(null);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingLabCondition(false);
    }
  };

  const openLabConditionModal = () => { setLcEditId(null); setLcForm(EMPTY_LC_FORM); setLcFieldErrors({}); setLabConditionModalOpen(true); };
  const openEditLabConditionModal = (rec) => {
    let t = {};
    try { t = JSON.parse(rec.target || '{}'); } catch { t = {}; }
    setLcEditId(rec.id);
    setLcForm({
      deviationType: rec.indicator_value || '',
      co2: t.co2 || '', temperature: t.temperature || '', o2: t.o2 || '', recordTime: t.recordTime || null,
      voc: t.voc || '', humidity: t.humidity || '',
      remarks: rec.remarks || '',
    });
    setLcFieldErrors({});
    setLabConditionModalOpen(true);
  };
  const setLcF = (field) => (e) => { setLcForm((p) => ({ ...p, [field]: e.target.value })); setLcFieldErrors((p) => ({ ...p, [field]: false })); };
  const setLcFV = (field, val) => { setLcForm((p) => ({ ...p, [field]: val })); setLcFieldErrors((p) => ({ ...p, [field]: false })); };
  const handleLcTypeChange = (val) => { setLcForm((p) => ({ ...p, deviationType: val })); setLcFieldErrors({}); };

  const deleteLabCondition = async (rec) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this lab condition record?',
      text: `${rec.indicator_value || 'Lab Condition'}${rec.created_at ? ` — ${new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}`,
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#7b809a',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteLabPerformanceRecord(rec.id);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  /* ── DAY-5 CAN → Canister → CANE → Straw hierarchy ────────────────────── */
  const openCanModal = () => { setCanEditId(null); setCanForm({ canType: '', canisterNo: '', remarks: '' }); setCanModalOpen(true); };
  const openEditCanModal = (rec) => { setCanEditId(rec.id); setCanForm({ canType: rec.target || '', canisterNo: rec.indicator_value || '', remarks: rec.remarks || '' }); setCanModalOpen(true); };

  const saveCan = async () => {
    if (!canForm.canType) { Swal.fire({ icon: 'warning', title: 'Select a CAN type', confirmButtonColor: pc }); return; }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingCan(true);
    try {
      let res;
      const payload = { target: canForm.canType, indicator_value: canForm.canisterNo.trim() || null, remarks: canForm.remarks.trim() || null };
      if (canEditId) {
        res = await api.ivf.updateLabPerformanceRecord(canEditId, payload);
      } else {
        const labId = await ensureLabId();
        res = await api.ivf.createLabPerformanceRecord(labId, { day_number: 5, indicator_key: CIK.VITRI_D5_CAN_ENTRY, ...payload });
      }
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setCanModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingCan(false);
    }
  };

  const deleteCan = async (rec) => {
    if (canesForCan(rec.id).length > 0) {
      Swal.fire({ icon: 'warning', title: 'Cannot delete', text: 'Remove all CANEs under this CAN first.', confirmButtonColor: pc });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete this CAN?', text: `${rec.target || 'CAN'} — Canister ${rec.indicator_value || '—'}`,
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a', confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteLabPerformanceRecord(rec.id);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  const openCaneModal = (canId) => { setCaneEditId(null); setCaneParentCanId(canId); setCaneForm({ caneNo: '', tag: '', goblet: '', remarks: '' }); setCaneModalOpen(true); };
  const openEditCaneModal = (rec) => {
    const t = parseTarget(rec);
    setCaneEditId(rec.id);
    setCaneParentCanId(t.canId);
    setCaneForm({ caneNo: rec.indicator_value || '', tag: t.tag || '', goblet: t.goblet || '', remarks: rec.remarks || '' });
    setCaneModalOpen(true);
  };

  const saveCane = async () => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingCane(true);
    try {
      let res;
      const payload = {
        target: JSON.stringify({ canId: caneParentCanId, tag: caneForm.tag, goblet: caneForm.goblet }),
        indicator_value: caneForm.caneNo.trim() || null,
        remarks: caneForm.remarks.trim() || null,
      };
      if (caneEditId) {
        res = await api.ivf.updateLabPerformanceRecord(caneEditId, payload);
      } else {
        const labId = await ensureLabId();
        res = await api.ivf.createLabPerformanceRecord(labId, { day_number: 5, indicator_key: CIK.VITRI_D5_CANE_ENTRY, ...payload });
      }
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setCaneModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingCane(false);
    }
  };

  const deleteCane = async (rec) => {
    if (strawsForCane(rec.id).length > 0) {
      Swal.fire({ icon: 'warning', title: 'Cannot delete', text: 'Remove all straws under this CANE first.', confirmButtonColor: pc });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete this CANE?', text: `CANE ${rec.indicator_value || '—'}`,
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a', confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteLabPerformanceRecord(rec.id);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  // "Add Straws" — opens the bulk-creation modal, empty until the user
  // types a count.
  const openAddStrawsModal = (caneId) => {
    setStrawParentCaneId(caneId);
    setStrawBatchPreserveError(false);
    // Default the preservation date to the gadget's overall Cryopreservation
    // Date, since a straw's own freeze date is normally the same event.
    setStrawBatchPreservedAt(cryoForm.cryoDate || null);
    setStrawBatchCount('');
    setStrawBatchRows([]);
    setStrawModalOpen(true);
  };
  // Regenerates the row list to match the typed count — rows already filled
  // in (by index) are preserved, new rows default their Straw No to the next
  // number after whatever straws already exist under this CANE.
  const handleStrawBatchCountChange = (e) => {
    const raw = e.target.value;
    if (raw !== '' && !/^\d*$/.test(raw)) return;
    setStrawBatchCount(raw);
    const n = Math.min(parseInt(raw, 10) || 0, MAX_STRAW_BATCH);
    const existingCount = strawsForCane(strawParentCaneId).length;
    setStrawBatchRows((prev) => Array.from({ length: n }, (_, i) =>
      prev[i] || { strawNo: String(existingCount + i + 1), color: '', embryoCount: '' }));
  };
  const setStrawBatchRowField = (idx, field) => (e) => {
    const val = e.target.value;
    if (field === 'embryoCount' && val !== '' && !/^\d*$/.test(val)) return;
    setStrawBatchRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: val } : row)));
  };

  const saveStrawBatch = async () => {
    if (!strawBatchPreservedAt) { setStrawBatchPreserveError(true); return; }
    if (strawBatchRows.length === 0) { Swal.fire({ icon: 'info', title: 'Enter the number of straws first', confirmButtonColor: pc }); return; }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingStrawBatch(true);
    try {
      const labId = await ensureLabId();
      const results = await Promise.all(strawBatchRows.map((row) => api.ivf.createLabPerformanceRecord(labId, {
        day_number: 5,
        indicator_key: CIK.VITRI_D5_STRAW_ENTRY,
        target: JSON.stringify({
          caneId: strawParentCaneId,
          preservedAt: strawBatchPreservedAt,
          color: row.color || null,
          embryoCount: row.embryoCount || null,
          used: false,
          usedAt: null,
        }),
        indicator_value: row.strawNo.trim() || null,
        remarks: null,
      })));
      const failedCount = results.filter((res) => res?.flag !== 1).length;
      if (failedCount > 0) throw new Error(`${failedCount} of ${results.length} straws failed to save`);
      Swal.fire({ icon: 'success', title: `${results.length} straw${results.length === 1 ? '' : 's'} saved`, confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setStrawModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingStrawBatch(false);
    }
  };

  const openEditStrawModal = (rec) => {
    const t = parseTarget(rec);
    setStrawEditId(rec.id);
    setStrawParentCaneId(t.caneId);
    setStrawFieldError(false);
    setStrawPreserveError(false);
    setStrawLockedUsed(!!t.used);
    setStrawForm({ strawNo: rec.indicator_value || '', preservedAt: t.preservedAt || null, color: t.color || '', embryoCount: t.embryoCount || '', used: !!t.used, usedAt: t.usedAt || null, rescuedDay: t.rescuedDay ?? '', rescuedCount: t.rescuedCount || '', remarks: rec.remarks || '' });
    setStrawEditModalOpen(true);
  };
  // Checkbox on the straw list row — opens the same edit modal with "used"
  // pre-checked so the user must still supply the required usage date/time;
  // the actual irreversible confirmation happens once, in saveStraw, right
  // before it's persisted (covers this path and manually checking "used"
  // inside the modal on a fresh/edited straw alike). By default this
  // prefills to right now — but it's an editable date/time picker, so the
  // user can move it earlier/later if the straw was actually thawed at a
  // different moment.
  const openMarkUsedModal = (rec) => {
    const t = parseTarget(rec);
    if (t.used) return; // already used — checkbox is disabled, this is just a safety no-op
    setStrawEditId(rec.id);
    setStrawParentCaneId(t.caneId);
    setStrawFieldError(false);
    setStrawPreserveError(false);
    setStrawLockedUsed(false);
    setStrawForm({ strawNo: rec.indicator_value || '', preservedAt: t.preservedAt || null, color: t.color || '', embryoCount: t.embryoCount || '', used: true, usedAt: dayjs().format('YYYY-MM-DD HH:mm'), rescuedDay: '', rescuedCount: '', remarks: rec.remarks || '' });
    setStrawEditModalOpen(true);
  };

  const saveStraw = async () => {
    if (!strawForm.preservedAt) { setStrawPreserveError(true); return; }
    if (strawForm.used && !strawForm.usedAt) { setStrawFieldError(true); return; }
    // Marking a straw used is permanent (can't be reverted or deleted
    // afterward) — confirm once, right before it's committed, regardless of
    // whether "used" was checked via the list checkbox or inside this modal.
    if (strawForm.used && !strawLockedUsed) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Mark this straw as used?',
        text: 'Once saved, this cannot be reverted or deleted.',
        showCancelButton: true,
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#7b809a',
        confirmButtonText: 'Yes, mark as used',
        cancelButtonText: 'Cancel',
      });
      if (!confirm.isConfirmed) return;
    }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingStraw(true);
    try {
      const payload = {
        target: JSON.stringify({
          caneId: strawParentCaneId,
          preservedAt: strawForm.preservedAt,
          color: strawForm.color || null,
          embryoCount: strawForm.embryoCount || null,
          used: strawForm.used,
          usedAt: strawForm.used ? strawForm.usedAt : null,
          rescuedDay: strawForm.used && strawForm.rescuedDay !== '' ? strawForm.rescuedDay : null,
          rescuedCount: strawForm.used ? (strawForm.rescuedCount || null) : null,
        }),
        indicator_value: strawForm.strawNo.trim() || null,
        remarks: strawForm.remarks.trim() || null,
      };
      const res = await api.ivf.updateLabPerformanceRecord(strawEditId, payload);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setStrawEditModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingStraw(false);
    }
  };

  const deleteStraw = async (rec) => {
    if (parseTarget(rec).used) {
      Swal.fire({ icon: 'warning', title: 'Cannot delete', text: 'A straw that has been marked used cannot be deleted.', confirmButtonColor: pc });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete this straw?', text: `Straw ${rec.indicator_value || '—'}`,
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a', confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteLabPerformanceRecord(rec.id);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  /* ── Semen Preservation — Entry (Media/Can/Canister/Date) + 1-2 Vials ─── */
  const openSemenEntryModal = () => {
    setSemenEntryEditId(null);
    setSemenEntryPreserveError(false);
    setSemenEntryVialCountError(false);
    setSemenEntryForm({ mediaUsed: '', mediaUsedOthers: '', canNo: '', canisterNo: '', preservedAt: null, vialCount: '', beforeCount: '', beforeMotility: '', remarks: '' });
    setSemenEntryModalOpen(true);
  };
  const openEditSemenEntryModal = (rec) => {
    const t = parseTarget(rec);
    const mv = rec.indicator_value || '';
    const isOthers = mv.startsWith('Others:');
    setSemenEntryEditId(rec.id);
    setSemenEntryPreserveError(false);
    setSemenEntryVialCountError(false);
    setSemenEntryForm({ mediaUsed: isOthers ? 'Others' : mv, mediaUsedOthers: isOthers ? mv.slice(7) : '', canNo: t.canNo || '', canisterNo: t.canisterNo || '', preservedAt: t.preservedAt || null, vialCount: '', beforeCount: t.beforeCount || '', beforeMotility: t.beforeMotility || '', remarks: rec.remarks || '' });
    setSemenEntryModalOpen(true);
  };

  const saveSemenEntry = async () => {
    if (!semenEntryForm.preservedAt) { setSemenEntryPreserveError(true); return; }
    if (!semenEntryEditId && !semenEntryForm.vialCount) { setSemenEntryVialCountError(true); return; }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingSemenEntry(true);
    try {
      const mv = semenEntryForm.mediaUsed === 'Others' ? `Others:${semenEntryForm.mediaUsedOthers}` : semenEntryForm.mediaUsed;
      const payload = {
        target: JSON.stringify({ canNo: semenEntryForm.canNo.trim() || null, canisterNo: semenEntryForm.canisterNo.trim() || null, preservedAt: semenEntryForm.preservedAt, beforeCount: (semenEntryForm.beforeCount || '').trim() || null, beforeMotility: (semenEntryForm.beforeMotility || '').trim() || null }),
        indicator_value: mv || null,
        remarks: semenEntryForm.remarks.trim() || null,
      };
      if (semenEntryEditId) {
        const res = await api.ivf.updateLabPerformanceRecord(semenEntryEditId, payload);
        if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      } else {
        const labId = await ensureLabId();
        const entryRes = await api.ivf.createLabPerformanceRecord(labId, { day_number: 0, indicator_key: SPIK.ENTRY, ...payload });
        if (entryRes?.flag !== 1 || !entryRes?.data?.id) throw new Error(entryRes?.message || 'Failed');
        const entryId = entryRes.data.id;
        // Creates exactly as many vials as chosen (Vial 1, or Vial 1 & 2).
        const vialNumbers = Array.from({ length: Number(semenEntryForm.vialCount) }, (_, i) => i + 1);
        const vialResults = await Promise.all(vialNumbers.map((vialNo) => api.ivf.createLabPerformanceRecord(labId, {
          day_number: 0,
          indicator_key: SPIK.VIAL,
          target: JSON.stringify({ entryId, used: false, usedAt: null, prepMethod: null, postWashCount: null, afterMotility: null }),
          indicator_value: String(vialNo),
          remarks: null,
        })));
        if (vialResults.some((r) => r?.flag !== 1)) throw new Error('Entry saved but creating its vials failed — please delete and retry.');
      }
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setSemenEntryModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingSemenEntry(false);
    }
  };

  const deleteSemenEntry = async (rec) => {
    const vials = vialsForEntry(rec.id);
    if (vials.some((v) => parseTarget(v).used)) {
      Swal.fire({ icon: 'warning', title: 'Cannot delete', text: 'A vial under this entry has already been marked used.', confirmButtonColor: pc });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete this entry?', text: `${rec.indicator_value || 'Semen Preservation'} entry and its vials`,
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a', confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const results = await Promise.all([rec.id, ...vials.map((v) => v.id)].map((id) => api.ivf.deleteLabPerformanceRecord(id)));
      if (results.some((r) => r?.flag !== 1)) throw new Error('Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  // Checkbox on the vial row — opens the edit modal with "used" pre-checked,
  // same one-way-confirm-in-saveSemenVial pattern as straws.
  const openMarkVialUsedModal = (rec) => {
    const t = parseTarget(rec);
    if (t.used) return; // already used — checkbox is disabled, this is just a safety no-op
    setSemenVialEditId(rec.id);
    setSemenVialFieldError(false);
    setSemenVialLockedUsed(false);
    setSemenVialForm({ vialNo: rec.indicator_value || '', entryId: t.entryId, used: true, usedAt: dayjs().format('YYYY-MM-DD HH:mm'), prepMethod: '', postWashCount: '', afterMotility: '', remarks: rec.remarks || '' });
    setSemenVialModalOpen(true);
  };
  const openViewVialModal = (rec) => {
    const t = parseTarget(rec);
    setSemenVialEditId(rec.id);
    setSemenVialFieldError(false);
    setSemenVialLockedUsed(!!t.used);
    setSemenVialForm({ vialNo: rec.indicator_value || '', entryId: t.entryId, used: !!t.used, usedAt: t.usedAt || null, prepMethod: t.prepMethod || '', postWashCount: t.postWashCount || '', afterMotility: t.afterMotility || '', remarks: rec.remarks || '' });
    setSemenVialModalOpen(true);
  };

  const saveSemenVial = async () => {
    if (semenVialForm.used && !semenVialForm.usedAt) { setSemenVialFieldError(true); return; }
    // Marking a vial used is permanent — confirm once, right before it's
    // committed, regardless of whether "used" was checked via the list
    // checkbox or inside this modal.
    if (semenVialForm.used && !semenVialLockedUsed) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Mark this vial as used?',
        text: 'Once saved, this cannot be reverted.',
        showCancelButton: true,
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#7b809a',
        confirmButtonText: 'Yes, mark as used',
        cancelButtonText: 'Cancel',
      });
      if (!confirm.isConfirmed) return;
    }
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingSemenVial(true);
    try {
      const payload = {
        target: JSON.stringify({
          entryId: semenVialForm.entryId,
          used: semenVialForm.used,
          usedAt: semenVialForm.used ? semenVialForm.usedAt : null,
          prepMethod: semenVialForm.used ? (semenVialForm.prepMethod || null) : null,
          postWashCount: semenVialForm.used ? (semenVialForm.postWashCount || null) : null,
          afterMotility: semenVialForm.used ? (semenVialForm.afterMotility || null) : null,
        }),
        indicator_value: semenVialForm.vialNo || null,
        remarks: semenVialForm.remarks.trim() || null,
      };
      const res = await api.ivf.updateLabPerformanceRecord(semenVialEditId, payload);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      setSemenVialModalOpen(false);
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingSemenVial(false);
    }
  };

  /* ── Cryo helpers ────────────────────────────────────────────────────── */
  const setCF = (field) => (e) => setCryoForm((p) => ({ ...p, [field]: e.target.value }));
  const setCFV = (field, val) => setCryoForm((p) => ({ ...p, [field]: val }));

  const openCryoModal = (tab = 'embryo') => {
    setCryoModalTab(tab);
    const gv = (key) => getCryo(key)?.indicator_value || '';
    const gr = (key) => getCryo(key)?.remarks || '';
    const mv = gv(CIK.STORAGE_MEDIA);
    const isOthers = mv.startsWith('Others:');
    setCryoForm({
      cryoDate: gv(CIK.DATE_CRYOPRESERVATION) || null, cryoDateRemarks: gr(CIK.DATE_CRYOPRESERVATION),
      thawDate: gv(CIK.DATE_THAWING) || null, thawDateRemarks: gr(CIK.DATE_THAWING),
      csMedia: isOthers ? 'Others' : mv, csMediaOthers: isOthers ? mv.slice(7) : '', csMediaRemarks: gr(CIK.STORAGE_MEDIA),
      csLnLevel: gv(CIK.STORAGE_LN_LEVEL), csLnLevelRemarks: gr(CIK.STORAGE_LN_LEVEL),
      vD0Oocyte: gv(CIK.VITRI_D0_OOCYTE), vD0RescuedThawing: gv(CIK.VITRI_D0_RESCUED_THAWING), vD0OocyteRemarks: gr(CIK.VITRI_D0_OOCYTE),
      vD3EmbryoCryo: gv(CIK.VITRI_D3_EMBRYO_CRYO), vD3Rescued: gv(CIK.VITRI_D3_RESCUED), vD3Refroze: gv(CIK.VITRI_D3_REFROZE), vD3RescuedRemarks: gr(CIK.VITRI_D3_RESCUED_RATE),
      vD4EmbryoCryo: gv(CIK.VITRI_D4_EMBRYO_CRYO), vD4Rescued: gv(CIK.VITRI_D4_RESCUED), vD4Refroze: gv(CIK.VITRI_D4_REFROZE), vD4RescuedRemarks: gr(CIK.VITRI_D4_RESCUED_RATE),
      vD5EmbryoCryo: gv(CIK.VITRI_D5_EMBRYO_CRYO), vD5Rescued: gv(CIK.VITRI_D5_RESCUED), vD5Refroze: gv(CIK.VITRI_D5_REFROZE), vD5RescuedRemarks: gr(CIK.VITRI_D5_RESCUED_RATE),
      wOocyte: gv(CIK.WARMING_OOCYTE), wOocyteRemarks: gr(CIK.WARMING_OOCYTE),
      wEmbryo: gv(CIK.WARMING_EMBRYO), wEmbryoRemarks: gr(CIK.WARMING_EMBRYO),
      wBlasto: gv(CIK.WARMING_BLASTO), wBlastoRemarks: gr(CIK.WARMING_BLASTO),
      srOocyte: gv(CIK.SURVIVAL_OOCYTE), srOocyteRemarks: gr(CIK.SURVIVAL_OOCYTE),
      srEmbryo: gv(CIK.SURVIVAL_EMBRYO), srEmbryoRemarks: gr(CIK.SURVIVAL_EMBRYO),
      srBlasto: gv(CIK.SURVIVAL_BLASTO), srBlastoRemarks: gr(CIK.SURVIVAL_BLASTO),
    });
    setVitriEntries(vitriEntryRecords.map((rec) => {
      const t = parseTarget(rec);
      return {
        id: rec.id, _k: `r${rec.id}`,
        type: rec.indicator_value || '',
        dayNo: t.dayNo || '', noPerStraw: t.noPerStraw || '',
        reserved: t.reserved || '', rescued: t.rescued || '',
        remarks: rec.remarks || '',
      };
    }));
    setCryoModalOpen(true);
  };

  const saveCryoSection = async (key, records) => {
    const filtered = records.filter((r) => r.indicator_value !== null || r.remarks !== null);
    if (!filtered.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
    setSavingCryo((p) => ({ ...p, [key]: true }));
    try {
      const labId = await ensureLabId();
      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records: filtered });
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingCryo((p) => ({ ...p, [key]: false }));
    }
  };

  const saveCryoDates = () => saveCryoSection('dates', [
    _mkr(0, CIK.DATE_CRYOPRESERVATION, cryoForm.cryoDate, cryoForm.cryoDateRemarks),
    _mkr(0, CIK.DATE_THAWING, cryoForm.thawDate, cryoForm.thawDateRemarks),
  ]);
  const saveCryoStorage = () => {
    const mv = cryoForm.csMedia === 'Others' ? `Others:${cryoForm.csMediaOthers}` : cryoForm.csMedia;
    saveCryoSection('storage', [_mkr(0, CIK.STORAGE_MEDIA, mv, cryoForm.csMediaRemarks), _mkr(0, CIK.STORAGE_LN_LEVEL, cryoForm.csLnLevel, cryoForm.csLnLevelRemarks)]);
  };
  /* ── Vitrification entries (repeatable "Add More" rows) ───────────────── */
  const vitriRowIsEmpty = (r) =>
    !r.type && !r.dayNo && !r.noPerStraw && !r.reserved && !r.rescued && !(r.remarks || '').trim();

  const addVitriEntry = () => setVitriEntries((p) => [
    ...p,
    { id: null, _k: `n${Date.now()}_${p.length}`, type: '', dayNo: '', noPerStraw: '', reserved: '', rescued: '', remarks: '' },
  ]);

  const setVitriField = (idx, field, val) =>
    setVitriEntries((p) => p.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));

  const setVitriIntField = (idx, field) => (e) => {
    const val = e.target.value;
    if (val !== '' && !/^\d*$/.test(val)) return;
    setVitriEntries((p) => p.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const removeVitriEntry = async (idx) => {
    const row = vitriEntries[idx];
    if (row?.id) {
      const result = await Swal.fire({
        icon: 'warning', title: 'Delete this entry?',
        text: `${row.type || 'Vitrification'} — Day ${row.dayNo || '—'}`,
        showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a',
        confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
        const res = await api.ivf.deleteLabPerformanceRecord(row.id);
        if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
        await fetchData();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
        return;
      }
    }
    setVitriEntries((p) => p.filter((_, i) => i !== idx));
  };

  const saveVitriEntries = async () => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    const rows = vitriEntries.filter((r) => !vitriRowIsEmpty(r));
    if (!rows.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
    setSavingVitri(true);
    try {
      const labId = await ensureLabId();
      const saved = await Promise.all(rows.map(async (row) => {
        const payload = {
          target: JSON.stringify({
            dayNo: row.dayNo || null,
            noPerStraw: row.noPerStraw || null,
            reserved: row.reserved || null,
            rescued: row.rescued || null,
          }),
          indicator_value: row.type || null,
          remarks: (row.remarks || '').trim() || null,
        };
        if (row.id) {
          const res = await api.ivf.updateLabPerformanceRecord(row.id, payload);
          return { row, res, id: row.id };
        }
        const res = await api.ivf.createLabPerformanceRecord(labId, { day_number: 0, indicator_key: CIK.VITRI_ENTRY, ...payload });
        return { row, res, id: res?.data?.id ?? null };
      }));
      const failed = saved.filter((s) => s.res?.flag !== 1).length;
      if (failed > 0) throw new Error(`${failed} of ${saved.length} entries failed to save`);
      // Re-sync local rows with their (new) record ids so a second save
      // doesn't re-create the rows that were just inserted.
      setVitriEntries(saved.map(({ row, id }) => ({ ...row, id, _k: id ? `r${id}` : row._k })));
      Swal.fire({ icon: 'success', title: 'Saved', confirmButtonColor: pc, timer: 1500, showConfirmButton: false });
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.message || 'Unable to save', confirmButtonColor: pc });
    } finally {
      setSavingVitri(false);
    }
  };
  const saveCryoVitriD3 = () => {
    const rate = computeRate(cryoForm.vD3Rescued, cryoForm.vD3EmbryoCryo);
    const hasInputs = cryoForm.vD3EmbryoCryo || cryoForm.vD3Rescued;
    saveCryoSection('vitriD3', [
      _mkr(3, CIK.VITRI_D3_EMBRYO_CRYO, cryoForm.vD3EmbryoCryo, null),
      _mkr(3, CIK.VITRI_D3_RESCUED, cryoForm.vD3Rescued, null),
      _mkr(3, CIK.VITRI_D3_REFROZE, cryoForm.vD3Refroze, null),
      _mkr(3, CIK.VITRI_D3_RESCUED_RATE, hasInputs ? String(rate) : null, cryoForm.vD3RescuedRemarks),
    ]);
  };
  const saveCryoVitriD4 = () => {
    const rate = computeRate(cryoForm.vD4Rescued, cryoForm.vD4EmbryoCryo);
    const hasInputs = cryoForm.vD4EmbryoCryo || cryoForm.vD4Rescued;
    saveCryoSection('vitriD4', [
      _mkr(4, CIK.VITRI_D4_EMBRYO_CRYO, cryoForm.vD4EmbryoCryo, null),
      _mkr(4, CIK.VITRI_D4_RESCUED, cryoForm.vD4Rescued, null),
      _mkr(4, CIK.VITRI_D4_REFROZE, cryoForm.vD4Refroze, null),
      _mkr(4, CIK.VITRI_D4_RESCUED_RATE, hasInputs ? String(rate) : null, cryoForm.vD4RescuedRemarks),
    ]);
  };
  const saveCryoVitriD5 = () => {
    const rate = computeRate(cryoForm.vD5Rescued, cryoForm.vD5EmbryoCryo);
    const hasInputs = cryoForm.vD5EmbryoCryo || cryoForm.vD5Rescued;
    saveCryoSection('vitriD5', [
      _mkr(5, CIK.VITRI_D5_EMBRYO_CRYO, cryoForm.vD5EmbryoCryo, null),
      _mkr(5, CIK.VITRI_D5_RESCUED, cryoForm.vD5Rescued, null),
      _mkr(5, CIK.VITRI_D5_REFROZE, cryoForm.vD5Refroze, null),
      _mkr(5, CIK.VITRI_D5_RESCUED_RATE, hasInputs ? String(rate) : null, cryoForm.vD5RescuedRemarks),
    ]);
  };
  const saveCryoWarming = () => saveCryoSection('warming', [
    _mkr(0, CIK.WARMING_OOCYTE, cryoForm.wOocyte, cryoForm.wOocyteRemarks),
    _mkr(0, CIK.WARMING_EMBRYO, cryoForm.wEmbryo, cryoForm.wEmbryoRemarks),
    _mkr(0, CIK.WARMING_BLASTO, cryoForm.wBlasto, cryoForm.wBlastoRemarks),
  ]);
  const saveCryoSurvival = () => saveCryoSection('survival', [
    _mkr(0, CIK.SURVIVAL_OOCYTE, cryoForm.srOocyte, cryoForm.srOocyteRemarks),
    _mkr(0, CIK.SURVIVAL_EMBRYO, cryoForm.srEmbryo, cryoForm.srEmbryoRemarks),
    _mkr(0, CIK.SURVIVAL_BLASTO, cryoForm.srBlasto, cryoForm.srBlastoRemarks),
  ]);

  /* ── Biopsy helpers ──────────────────────────────────────────────────── */
  const setBFV = (field, val) => setBiopsyForm((p) => ({ ...p, [field]: val }));
  const setCFBiopsy = (field) => (e) => setBiopsyForm((p) => ({ ...p, [field]: e.target.value }));

  const openBiopsyModal = () => {
    const gv = (key) => getBiopsy(key)?.indicator_value || '';
    const gr = (key) => getBiopsy(key)?.remarks || '';
    setBiopsyForm({
      tubingRate: gv(BIK.TUBING_RATE), tubingRateRemarks: gr(BIK.TUBING_RATE),
      pgtType: gv(BIK.PGT_TYPE),
      euploidyRate: gv(BIK.EUPLOIDY_RATE), euploidyRateRemarks: gr(BIK.EUPLOIDY_RATE),
    });
    setBiopsyModalOpen(true);
  };

  const saveBiopsy = async () => {
    setSavingBiopsy(true);
    try {
      const labId = await ensureLabId();
      const records = [
        _mkr(0, BIK.TUBING_RATE, biopsyForm.tubingRate, biopsyForm.tubingRateRemarks),
        _mkr(0, BIK.PGT_TYPE, biopsyForm.pgtType, null),
        _mkr(0, BIK.EUPLOIDY_RATE, biopsyForm.euploidyRate, biopsyForm.euploidyRateRemarks),
      ].filter((r) => r.indicator_value !== null);
      if (!records.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records });
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
      Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
      setBiopsyModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setSavingBiopsy(false);
    }
  };

  /* ── Transfer helpers ────────────────────────────────────────────────── */
  const setTFV = (field, val) => setTransferForm((p) => ({ ...p, [field]: val }));
  const setCFTransfer = (field) => (e) => setTransferForm((p) => ({ ...p, [field]: e.target.value }));

  const openTransferModal = () => {
    const gv = (key) => getTransfer(key)?.indicator_value || '';
    const gr = (key) => getTransfer(key)?.remarks || '';
    setTransferForm({
      embryoCount: gv(TIK.EMBRYO_COUNT), embryoCountRemarks: gr(TIK.EMBRYO_COUNT),
      embryoDays: gv(TIK.EMBRYO_DAYS), embryoDaysRemarks: gr(TIK.EMBRYO_DAYS),
      embryoType: gv(TIK.EMBRYO_TYPE), embryoTypeRemarks: gr(TIK.EMBRYO_TYPE),
      embryoGrade: gv(TIK.EMBRYO_GRADE), embryoGradeRemarks: gr(TIK.EMBRYO_GRADE),
      transferProcess: gv(TIK.TRANSFER_PROCESS), transferTechnique: gv(TIK.TRANSFER_TECHNIQUE),
    });
    setTransferModalOpen(true);
  };

  const saveTransfer = async () => {
    setSavingTransfer(true);
    try {
      const labId = await ensureLabId();
      if (transferForm.transferProcess && !transferForm.transferTechnique) {
        Swal.fire({ icon: 'warning', title: 'Transfer Technique required', text: 'Please select a Transfer Technique for the chosen process.', confirmButtonColor: pc });
        return;
      }
      const records = [
        _mkr(0, TIK.EMBRYO_COUNT, transferForm.embryoCount, transferForm.embryoCountRemarks),
        _mkr(0, TIK.EMBRYO_DAYS, transferForm.embryoDays, transferForm.embryoDaysRemarks),
        _mkr(0, TIK.EMBRYO_TYPE, transferForm.embryoType, transferForm.embryoTypeRemarks),
        _mkr(0, TIK.EMBRYO_GRADE, transferForm.embryoGrade, transferForm.embryoGradeRemarks),
        _mkr(0, TIK.TRANSFER_PROCESS, transferForm.transferProcess, null),
        _mkr(0, TIK.TRANSFER_TECHNIQUE, transferForm.transferTechnique, null),
      ].filter((r) => r.indicator_value !== null);
      if (!records.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records });
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
      Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
      setTransferModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setSavingTransfer(false);
    }
  };

  /* ── Implementation helpers ──────────────────────────────────────────── */
  const setIFV = (field, val) => setImplForm((p) => ({ ...p, [field]: val }));
  const setCFImpl = (field) => (e) => setImplForm((p) => ({ ...p, [field]: e.target.value }));

  const openImplModal = () => {
    const gv = (key) => getImpl(key)?.indicator_value || '';
    const gr = (key) => getImpl(key)?.remarks || '';
    setImplForm({
      embryosTransferred: gv(IIK.EMBRYOS_TRANSFERRED), embryosTransferredRemarks: gr(IIK.EMBRYOS_TRANSFERRED),
      embryosImplanted: gv(IIK.EMBRYOS_IMPLANTED), embryosImplantedRemarks: gr(IIK.EMBRYOS_IMPLANTED),
      implementation: gv(IIK.IMPLEMENTATION),
      remarks: gv(IIK.REMARKS),
    });
    setImplModalOpen(true);
  };

  const saveImpl = async () => {
    setSavingImpl(true);
    try {
      const labId = await ensureLabId();
      const records = [
        _mkr(0, IIK.EMBRYOS_TRANSFERRED, implForm.embryosTransferred, implForm.embryosTransferredRemarks),
        _mkr(0, IIK.EMBRYOS_IMPLANTED, implForm.embryosImplanted, implForm.embryosImplantedRemarks),
        _mkr(0, IIK.IMPLEMENTATION, implForm.implementation, null),
        _mkr(0, IIK.REMARKS, implForm.remarks, null),
      ].filter((r) => r.indicator_value !== null);
      if (!records.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records });
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
      Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
      setImplModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setSavingImpl(false);
    }
  };

  /* ── Outcome helpers ──────────────────────────────────────────────────── */
  const setOFV = (field, val) => setOutcomeForm((p) => ({ ...p, [field]: val }));
  const setCFOutcome = (field) => (e) => setOutcomeForm((p) => ({ ...p, [field]: e.target.value }));
  const EMPTY_OUTCOME_FORM = { pregnancyType: '', subType: '', miscarriageWeeks: '', remarks: '' };

  const openOutcomeModal = () => {
    setOutcomeEditId(null);
    setOutcomeForm(EMPTY_OUTCOME_FORM);
    setOutcomeModalOpen(true);
  };

  const openEditOutcomeModal = (rec) => {
    const parsed = parseOutcome(rec);
    setOutcomeEditId(rec.id);
    setOutcomeForm({
      pregnancyType: parsed.pregnancyType || '',
      subType: parsed.subType || '',
      miscarriageWeeks: parsed.miscarriageWeeks || '',
      remarks: rec.remarks || '',
    });
    setOutcomeModalOpen(true);
  };

  const saveOutcome = async () => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    if (!outcomeForm.pregnancyType) { Swal.fire({ icon: 'warning', title: 'Pregnancy Type is required', confirmButtonColor: pc }); return; }
    if (outcomeForm.pregnancyType === 'Clinical' && !outcomeForm.subType) { Swal.fire({ icon: 'warning', title: 'Sub Type is required', confirmButtonColor: pc }); return; }
    if (outcomeForm.subType === 'Miscarriage' && !String(outcomeForm.miscarriageWeeks).trim()) { Swal.fire({ icon: 'warning', title: 'Weeks is required for Miscarriage', confirmButtonColor: pc }); return; }
    setSavingOutcome(true);
    try {
      const effectiveSubType = outcomeForm.pregnancyType === 'Biochemical' ? 'Biochemical' : outcomeForm.subType;
      const payload = JSON.stringify({
        pregnancyType: outcomeForm.pregnancyType,
        subType: effectiveSubType,
        ...(effectiveSubType === 'Miscarriage' && { miscarriageWeeks: outcomeForm.miscarriageWeeks }),
      });
      const remarksVal = outcomeForm.remarks.trim() || null;
      let res;
      if (outcomeEditId) {
        res = await api.ivf.updateLabPerformanceRecord(outcomeEditId, { indicator_value: payload, remarks: remarksVal });
      } else {
        const labId = await ensureLabId();
        res = await api.ivf.createLabPerformanceRecord(labId, { day_number: 0, indicator_key: OIK.RECORD, indicator_value: payload, remarks: remarksVal });
      }
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
      Swal.fire({ icon: 'success', title: outcomeEditId ? 'Updated' : 'Saved', timer: 1200, showConfirmButton: false });
      setOutcomeModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setSavingOutcome(false);
    }
  };

  const deleteOutcome = async (rec) => {
    const parsed = parseOutcome(rec);
    const label = [parsed.pregnancyType, parsed.subType].filter(Boolean).join(' — ');
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete this outcome?',
      text: label || 'This outcome record will be permanently removed.',
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#7b809a',
      confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteLabPerformanceRecord(rec.id);
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.message || 'Unable to delete', confirmButtonColor: pc });
    }
  };

  /* ── Andrology helpers ───────────────────────────────────────────────── */
  const setAFV = (field, val) => setAndrologyForm((p) => ({ ...p, [field]: val }));
  const setCFAndrology = (field) => (e) => setAndrologyForm((p) => ({ ...p, [field]: e.target.value }));

  const makePctAndrology = (field) => (e) => {
    const v = e.target.value;
    if (v === '' || (/[eE+-]/.test(v) === false && /^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 100)) setAFV(field, v);
  };
  const makeNumAndrology = (field) => (e) => {
    const v = e.target.value;
    if (v === '' || (/[eE+-]/.test(v) === false && /^\d*(\.\d{0,2})?$/.test(v))) setAFV(field, v);
  };

  const loadAndrologyFiles = async (labId) => {
    if (!labId) { setAndrologyFiles({ semen_analysis: [], dna_fragmentation: [] }); return; }
    try {
      const res = await api.ivf.getAndrologyReportFiles(labId);
      const files = res?.flag === 1 ? (res.data || []) : [];
      setAndrologyFiles({
        semen_analysis: files.filter((f) => f.report_type === 'semen_analysis'),
        dna_fragmentation: files.filter((f) => f.report_type === 'dna_fragmentation'),
      });
    } catch {
      setAndrologyFiles({ semen_analysis: [], dna_fragmentation: [] });
    }
  };

  const handleUploadAndrologyFile = async (reportType, file) => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setUploadingAndrology((p) => ({ ...p, [reportType]: true }));
    try {
      const labId = await ensureLabId();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('report_type', reportType);
      const res = await api.ivf.uploadAndrologyReportFile(labId, formData);
      if (res?.flag !== 1) throw new Error(res?.message || 'Upload failed');
      await loadAndrologyFiles(labId);
      Swal.fire({ icon: 'success', title: 'Uploaded', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setUploadingAndrology((p) => ({ ...p, [reportType]: false }));
    }
  };

  const openAndrologyModal = () => {
    const gv = (key) => getAndrology(key)?.indicator_value || '';
    setAndrologyForm({
      count: gv(AIK.COUNT),
      motility: gv(AIK.MOTILITY),
      progressiveMotility: gv(AIK.PROGRESSIVE_MOTILITY),
      morphology: gv(AIK.MORPHOLOGY),
      dfi: gv(AIK.DFI),
      dfiBeforeTreatment: gv(AIK.DFI_BEFORE),
      dfiAfterTreatment: gv(AIK.DFI_AFTER),
      remarks: gv(AIK.REMARKS),
    });
    setAndrologyModalOpen(true);
    loadAndrologyFiles(labData?.id);
  };

  const saveAndrology = async () => {
    if (!canCreate) { Swal.fire({ icon: 'warning', title: 'Permission denied', confirmButtonColor: pc }); return; }
    setSavingAndrology(true);
    try {
      const labId = await ensureLabId();
      const dfiNum = parseFloat(andrologyForm.dfi);
      const isHighDFI = !isNaN(dfiNum) && dfiNum >= 22;
      const records = [
        _mkr(0, AIK.COUNT, andrologyForm.count, null),
        _mkr(0, AIK.MOTILITY, andrologyForm.motility, null),
        _mkr(0, AIK.PROGRESSIVE_MOTILITY, andrologyForm.progressiveMotility, null),
        _mkr(0, AIK.MORPHOLOGY, andrologyForm.morphology, null),
        _mkr(0, AIK.DFI, andrologyForm.dfi, null),
        ...(isHighDFI ? [
          _mkr(0, AIK.DFI_BEFORE, andrologyForm.dfiBeforeTreatment, null),
          _mkr(0, AIK.DFI_AFTER, andrologyForm.dfiAfterTreatment, null),
          andrologyForm.remarks.trim() ? _mkr(0, AIK.REMARKS, andrologyForm.remarks.trim(), null) : null,
        ] : []),
      ].filter(Boolean).filter((r) => r.indicator_value !== null);
      if (!records.length) { Swal.fire({ icon: 'info', title: 'Nothing to save', confirmButtonColor: pc }); return; }
      const res = await api.ivf.upsertLabPerformanceRecords(labId, { records });
      if (res?.flag !== 1) throw new Error(res?.message || 'Failed');
      await fetchData();
      Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
      setAndrologyModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: pc });
    } finally {
      setSavingAndrology(false);
    }
  };

  const anyDaySaving = Object.values(savingDay).some(Boolean);

  const day0OocyteRecoveryRate = computeRate(day0Form.oocyteRetrieved, day0Form.folliclesAbove14mm);
  const day0MiiProportion = computeRate(day0Form.miiOocyteCount, day0Form.oocyteRetrieved);
  const day0DamageRate = computeRate(day0Form.oocyteDamaged, day0Form.oocyteInjected);

  // DAY 1's base oocyte counts aren't re-entered — they're the same cohort
  // tracked from DAY 0, so they're pulled live from DAY 0's own fields
  // (Conventional's "No of MII Oocyte Inseminated" / ICSI's "No of MII
  // Oocyte Injected") rather than being a separate editable DAY 1 input.
  const day1ConventionalBase = day0Form.inseminationConventional ? day0Form.oocyteInseminatedConventional : '';
  const day1IcsiBase = day0Form.inseminationIcsi ? day0Form.oocyteInjected : '';
  const day1Pn0RateConventional = computeRate(day1Form.pn0CountConventional, day1ConventionalBase);
  const day1Pn1RateConventional = computeRate(day1Form.pn1CountConventional, day1ConventionalBase);
  const day1Pn2RateConventional = computeRate(day1Form.pn2CountConventional, day1ConventionalBase);
  const day1Pn3RateConventional = computeRate(day1Form.pn3CountConventional, day1ConventionalBase);
  const day1Pn0RateIcsi = computeRate(day1Form.pn0CountIcsi, day1IcsiBase);
  const day1Pn1RateIcsi = computeRate(day1Form.pn1CountIcsi, day1IcsiBase);
  const day1Pn2RateIcsi = computeRate(day1Form.pn2CountIcsi, day1IcsiBase);
  const day1Pn3RateIcsi = computeRate(day1Form.pn3CountIcsi, day1IcsiBase);
  // Combined totals — naturally collapse to "just that one method's numbers"
  // when only Conventional or only ICSI was selected (the other side is 0),
  // so DAY 3's embryo formation rate below stays correct in every case.
  const day1TotalBase = (Number(day1ConventionalBase) || 0) + (Number(day1IcsiBase) || 0);
  const day1TotalPn0 = (Number(day1Form.pn0CountConventional) || 0) + (Number(day1Form.pn0CountIcsi) || 0);
  const day1TotalPn1 = (Number(day1Form.pn1CountConventional) || 0) + (Number(day1Form.pn1CountIcsi) || 0);
  const day1TotalPn2 = (Number(day1Form.pn2CountConventional) || 0) + (Number(day1Form.pn2CountIcsi) || 0);
  const day1TotalPn3 = (Number(day1Form.pn3CountConventional) || 0) + (Number(day1Form.pn3CountIcsi) || 0);
  const day1TotalPn0Rate = computeRate(day1TotalPn0, day1TotalBase);
  const day1TotalPn1Rate = computeRate(day1TotalPn1, day1TotalBase);
  const day1TotalPn2Rate = computeRate(day1TotalPn2, day1TotalBase);
  const day1TotalPn3Rate = computeRate(day1TotalPn3, day1TotalBase);
  const day1ShowTotal = Boolean(day0Form.inseminationConventional && day0Form.inseminationIcsi);
  // Same "both methods selected" gate reused by DAY 3/4/5's Combined
  // sections below — named generically since it isn't DAY-1-specific.
  const bothMethodsSelected = day1ShowTotal;

  // DAY 3 — base per method is DAY 1's own per-method 2PN count.
  const day3ConventionalBase = day0Form.inseminationConventional ? day1Form.pn2CountConventional : '';
  const day3IcsiBase = day0Form.inseminationIcsi ? day1Form.pn2CountIcsi : '';
  const day3EmbryoFormationRateConventional = computeRate(day3Form.embryoCountConventional, day3ConventionalBase);
  const day3EmbryoFormationRateIcsi = computeRate(day3Form.embryoCountIcsi, day3IcsiBase);
  // Combined totals — sum-then-rate, same approach as DAY 1's Total (not an
  // average of the two method rates), so it collapses correctly to a single
  // method's numbers when only one was selected.
  const day3TotalEmbryoCount = (Number(day3Form.embryoCountConventional) || 0) + (Number(day3Form.embryoCountIcsi) || 0);
  const day3TotalEmbryoFormationRate = computeRate(day3TotalEmbryoCount, day1TotalPn2);
  // Good Quality Embryo — rate is against that method's own "No of Embryos"
  // count (not the 2PN base), same relationship Embryo Formation Rate has to
  // its own base.
  const day3GoodQualityRateConventional = computeRate(day3Form.goodQualityEmbryoCountConventional, day3Form.embryoCountConventional);
  const day3GoodQualityRateIcsi = computeRate(day3Form.goodQualityEmbryoCountIcsi, day3Form.embryoCountIcsi);
  const day3TotalGoodQualityEmbryoCount = (Number(day3Form.goodQualityEmbryoCountConventional) || 0) + (Number(day3Form.goodQualityEmbryoCountIcsi) || 0);
  const day3TotalGoodQualityEmbryoRate = computeRate(day3TotalGoodQualityEmbryoCount, day3TotalEmbryoCount);

  // DAY 4 — base per method is DAY 3's own per-method Good Quality Embryo
  // count (not the raw embryo count — compaction only makes sense to track
  // against the embryos that were actually graded good quality).
  const day4ConventionalBase = day0Form.inseminationConventional ? day3Form.goodQualityEmbryoCountConventional : '';
  const day4IcsiBase = day0Form.inseminationIcsi ? day3Form.goodQualityEmbryoCountIcsi : '';
  const day4CompactionRateConventional = computeRate(day4Form.compactedEmbryoCountConventional, day4ConventionalBase);
  const day4CompactionRateIcsi = computeRate(day4Form.compactedEmbryoCountIcsi, day4IcsiBase);
  const day4TotalCompactedEmbryoCount = (Number(day4Form.compactedEmbryoCountConventional) || 0) + (Number(day4Form.compactedEmbryoCountIcsi) || 0);
  const day4TotalCompactionRate = computeRate(day4TotalCompactedEmbryoCount, day3TotalGoodQualityEmbryoCount);

  // DAY 5 — base per method is DAY 3's own per-method Good Quality Embryo
  // count, same as DAY 4 above.
  const day5ConventionalBase = day0Form.inseminationConventional ? day3Form.goodQualityEmbryoCountConventional : '';
  const day5IcsiBase = day0Form.inseminationIcsi ? day3Form.goodQualityEmbryoCountIcsi : '';
  const day5BlastocystRateConventional = computeRate(day5Form.blastocystCountConventional, day5ConventionalBase);
  const day5BlastocystRateIcsi = computeRate(day5Form.blastocystCountIcsi, day5IcsiBase);
  const day5UsableBlastocystRateConventional = computeRate(day5Form.usableBlastocystCountConventional, day5ConventionalBase);
  const day5UsableBlastocystRateIcsi = computeRate(day5Form.usableBlastocystCountIcsi, day5IcsiBase);
  const day5TotalBlastocystCount = (Number(day5Form.blastocystCountConventional) || 0) + (Number(day5Form.blastocystCountIcsi) || 0);
  const day5TotalUsableBlastocystCount = (Number(day5Form.usableBlastocystCountConventional) || 0) + (Number(day5Form.usableBlastocystCountIcsi) || 0);
  const day5TotalBlastocystRate = computeRate(day5TotalBlastocystCount, day3TotalGoodQualityEmbryoCount);
  const day5TotalUsableBlastocystRate = computeRate(day5TotalUsableBlastocystCount, day3TotalGoodQualityEmbryoCount);

  /* ═══════════════════════════════════════ RENDER ════════════════════════ */
  return (
    <DashboardLayout>
      <DashboardNavbar title={procedureCode !== 'N/A' ? procedureCode : undefined} />
      <MDBox py={3}>

        {/* ── Breadcrumb ────────────────────────────────────────────────── */}
        <Breadcrumbs separator="/" sx={{ mb: 2, fontSize: '0.875rem' }}>
          <Link underline="hover" onClick={() => navigate('/dashboard')} sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', '&:hover': { color: pc } }}>
            <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
          </Link>
          <Link underline="hover" onClick={() => navigate('/ivf')} sx={{ color: '#7b809a', cursor: 'pointer', '&:hover': { color: pc } }}>IVF</Link>
          <Typography sx={{ color: pc, fontWeight: 600, fontSize: '0.875rem' }}>Procedure Details</Typography>
        </Breadcrumbs>

        {/* ── Procedure Header Card ─────────────────────────────────────── */}
        <Card sx={{ mb: 2.5, border: '1px solid #e9ecef', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'visible' }}>
          {/* Colored top bar */}
          <Box sx={{ mx: 2, mt: -1.5, py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>IVF Lab Performance</Typography>
            </Box>
          </Box>

          <Box sx={{ p: 2.5, pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3 }}>PROCEDURE ID</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: pc }}>{procedureCode}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mt: 1, mb: 0.3 }}>INITIATED DATE</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc }}>{procedureDate}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mt: 1, mb: 0.3 }}>CATEGORY</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc }}>{procedureCategory} / {procedureSubCategory}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3 }}>PATIENT NAME</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc, mb: 1 }}>{patientName}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3 }}>MOBILE</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc }}>{mobile}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3 }}>PATIENT CODE</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc, mb: 1 }}>{patientCode}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3 }}>SPOUSE NAME</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tc }}>{spouseName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontWeight: 700, letterSpacing: '0.6px', mb: 0.3, textAlign: 'right' }}>STATUS</Typography>
                  <Chip label={procedureStatus.replace(/_/g, ' ')} size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                </Box>
                <Tooltip title="Print Lab Report">
                  <IconButton onClick={handlePrint} size="small" sx={{ border: '1px solid #e9ecef', borderRadius: 1, p: 0.75, color: '#7b809a', '&:hover': { borderColor: pc, color: pc } }}>
                    <PrintIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* ── Main Grid ──────────────────────────────────────────────────── */}
        <Grid container spacing={2.5}>

          {/* Lab Performance Card */}
          <Grid item xs={12} md={7}>
            <Card sx={{ border: '1px solid #e9ecef', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChartIcon sx={{ color: pc, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: tc }}>Lab Performance</Typography>
                </Box>
                {!isReadOnly && (!labData ? (
                  <Link component="button" type="button" onClick={openModal} underline="always" sx={{ fontSize: '0.82rem', fontWeight: 600, color: pc }}>+ Add Data</Link>
                ) : (
                  <>
                    <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                      <MoreVertIcon sx={{ fontSize: 20, color: '#7b809a' }} />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                      <MenuItem onClick={() => { setAnchorEl(null); openModal(); }} sx={{ fontSize: '0.875rem' }}>Edit</MenuItem>
                    </Menu>
                  </>
                ))}
              </Box>
              <Box sx={{ p: 2 }}>
                {!labData ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    {!isReadOnly && <ActionBox label="Add Lab Performance Data" onClick={openModal} />}
                    <Typography sx={{ mt: isReadOnly ? 0 : 1, fontSize: '0.75rem', color: '#9e9e9e' }}>No data recorded yet</Typography>
                  </Box>
                ) : (
                  <Box>
                    {/* ── Day selector tabs (only days with data) ── */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      {[0, 1, 3, 4, 5].filter((d) => hasDay(d)).map((d) => {
                        const isActive = activeDay === d;
                        return (
                          <Box
                            key={d}
                            onClick={() => setSelectedDay(d)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              px: 1.5, py: 0.5, borderRadius: '999px', cursor: 'pointer',
                              backgroundColor: isActive ? pc : `${pc}15`,
                              border: `1.5px solid ${isActive ? pc : `${pc}40`}`,
                              transition: 'all 0.18s',
                              '&:hover': { backgroundColor: isActive ? pc : `${pc}28` },
                            }}
                          >
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: isActive ? 'white' : pc }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isActive ? 'white' : pc }}>DAY {d}</Typography>
                          </Box>
                        );
                      })}
                      <Typography sx={{ ml: 'auto', fontSize: '0.68rem', color: '#7b809a', fontWeight: 500 }}>
                        {[0, 1, 3, 4, 5].filter((d) => hasDay(d)).length} / 5 days recorded
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {/* ── Records for selected day ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {shownDayRecords.length === 0 ? (
                        <Box sx={{ border: '1px dashed #e9ecef', borderRadius: 2, p: 3, textAlign: 'center' }}>
                          <Typography sx={{ color: '#7b809a', fontSize: '0.82rem' }}>No indicator data saved yet. Click Edit to add.</Typography>
                        </Box>
                      ) : (
                        shownDayRecords.map((dg) => {
                          const header = (
                            <Box sx={{ px: 2, py: 0.9, backgroundColor: `${pc}0A`, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 22, height: 22, borderRadius: 0.75, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CalendarIcon sx={{ fontSize: 12, color: pc }} />
                              </Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: pc }}>DAY {dg.day}</Typography>
                              <Chip label={`${dg.records.length} indicator${dg.records.length > 1 ? 's' : ''}`} size="small" sx={{ ml: 'auto', height: 18, fontSize: '0.62rem', backgroundColor: `${pc}12`, color: pc, fontWeight: 600 }} />
                            </Box>
                          );

                          // DAY 0 / DAY 1 / DAY 3 / DAY 4 / DAY 5 — common
                          // RI/PI cards (DAY 0 only) stay flat; every day's
                          // KPI/PI splits into a Conventional/ICSI tab
                          // switcher (plus a combined Total tab when both
                          // methods were selected on DAY 0), showing only
                          // the methods that actually have saved records
                          // for that day.
                          let commonRecords = [];
                          let kpiTabs = [];
                          const dashboardTabStateByDay = {
                            0: [day0KpiTab, setDay0KpiTab],
                            1: [day1DashboardTab, setDay1DashboardTab],
                            3: [day3DashboardTab, setDay3DashboardTab],
                            4: [day4DashboardTab, setDay4DashboardTab],
                            5: [day5DashboardTab, setDay5DashboardTab],
                          };
                          const [activeTabState, setActiveTabState] = dashboardTabStateByDay[dg.day];
                          // Builds a flat (DAY-0-style) tab: tries each
                          // key-list in order per row so newer per-method
                          // keys fall back to the pre-split legacy key
                          // (folded into Conventional), same pattern as DAY
                          // 1's buildPnTab below.
                          const buildFlatTab = (tabKey, label, keySpecs) => {
                            const records = keySpecs
                              .map((keys) => keys.map((k) => dg.records.find((r) => r.indicator_key === k)).find(Boolean))
                              .filter(Boolean);
                            if (records.length === 0) return null;
                            return { key: tabKey, label, records };
                          };

                          if (dg.day === 3) {
                            kpiTabs = [
                              buildFlatTab('conventional', 'Conventional', [
                                [IK.EMBRYO_COUNT_CONVENTIONAL, IK.EMBRYO_COUNT],
                                [IK.EMBRYO_FORMATION_RATE_CONVENTIONAL, IK.EMBRYO_FORMATION_RATE],
                                [IK.GOOD_QUALITY_EMBRYO_COUNT_CONVENTIONAL],
                                [IK.GOOD_QUALITY_EMBRYO_RATE_CONVENTIONAL],
                              ]),
                              buildFlatTab('icsi', 'ICSI', [
                                [IK.EMBRYO_COUNT_ICSI],
                                [IK.EMBRYO_FORMATION_RATE_ICSI],
                                [IK.GOOD_QUALITY_EMBRYO_COUNT_ICSI],
                                [IK.GOOD_QUALITY_EMBRYO_RATE_ICSI],
                              ]),
                              buildFlatTab('total', 'Total (Combined)', [
                                [IK.TOTAL_EMBRYO_COUNT],
                                [IK.TOTAL_EMBRYO_FORMATION_RATE],
                                [IK.TOTAL_GOOD_QUALITY_EMBRYO_COUNT],
                                [IK.TOTAL_GOOD_QUALITY_EMBRYO_RATE],
                              ]),
                            ].filter(Boolean);
                          } else if (dg.day === 4) {
                            kpiTabs = [
                              buildFlatTab('conventional', 'Conventional', [
                                [IK.D4_DAY3_EMBRYO_COUNT_CONVENTIONAL, IK.D4_DAY3_EMBRYO_COUNT],
                                [IK.COMPACTED_EMBRYO_COUNT_CONVENTIONAL, IK.COMPACTED_EMBRYO_COUNT],
                                [IK.COMPACTION_RATE_CONVENTIONAL, IK.COMPACTION_RATE],
                              ]),
                              buildFlatTab('icsi', 'ICSI', [
                                [IK.D4_DAY3_EMBRYO_COUNT_ICSI],
                                [IK.COMPACTED_EMBRYO_COUNT_ICSI],
                                [IK.COMPACTION_RATE_ICSI],
                              ]),
                              buildFlatTab('total', 'Total (Combined)', [
                                [IK.TOTAL_D4_DAY3_EMBRYO_COUNT],
                                [IK.TOTAL_COMPACTED_EMBRYO_COUNT],
                                [IK.TOTAL_COMPACTION_RATE],
                              ]),
                            ].filter(Boolean);
                          } else if (dg.day === 5) {
                            kpiTabs = [
                              buildFlatTab('conventional', 'Conventional', [
                                [IK.D5_DAY3_FORMATION_COUNT_CONVENTIONAL, IK.D5_DAY3_FORMATION_COUNT],
                                [IK.BLASTOCYST_COUNT_CONVENTIONAL, IK.BLASTOCYST_COUNT],
                                [IK.BLASTOCYST_FORMATION_RATE_CONVENTIONAL, IK.BLASTOCYST_FORMATION_RATE],
                                [IK.USABLE_BLASTOCYST_COUNT_CONVENTIONAL, IK.USABLE_BLASTOCYST_COUNT],
                                [IK.USABLE_BLASTOCYST_FORMATION_RATE_CONVENTIONAL, IK.USABLE_BLASTOCYST_FORMATION_RATE],
                              ]),
                              buildFlatTab('icsi', 'ICSI', [
                                [IK.D5_DAY3_FORMATION_COUNT_ICSI],
                                [IK.BLASTOCYST_COUNT_ICSI],
                                [IK.BLASTOCYST_FORMATION_RATE_ICSI],
                                [IK.USABLE_BLASTOCYST_COUNT_ICSI],
                                [IK.USABLE_BLASTOCYST_FORMATION_RATE_ICSI],
                              ]),
                              buildFlatTab('total', 'Total (Combined)', [
                                [IK.TOTAL_D5_DAY3_FORMATION_COUNT],
                                [IK.TOTAL_BLASTOCYST_COUNT],
                                [IK.TOTAL_BLASTOCYST_FORMATION_RATE],
                                [IK.TOTAL_USABLE_BLASTOCYST_COUNT],
                                [IK.TOTAL_USABLE_BLASTOCYST_FORMATION_RATE],
                              ]),
                            ].filter(Boolean);
                          } else if (dg.day === 0) {
                            const icsiKeys = [IK.OOCYTE_INJECTED, IK.OOCYTE_DAMAGED, IK.OOCYTE_DAMAGE_RATE_ICSI];
                            const conventionalKeys = [IK.OOCYTE_INSEMINATED_CONVENTIONAL, IK.OOCYTE_DAMAGED_CONVENTIONAL, IK.OOCYTE_DAMAGE_RATE_CONVENTIONAL];
                            const methodFlagKeys = [IK.INSEMINATION_METHOD_ICSI, IK.INSEMINATION_METHOD_CONVENTIONAL];
                            const icsiSelected = dg.records.find((r) => r.indicator_key === IK.INSEMINATION_METHOD_ICSI)?.indicator_value === 'Y';
                            const conventionalSelected = dg.records.find((r) => r.indicator_key === IK.INSEMINATION_METHOD_CONVENTIONAL)?.indicator_value === 'Y';
                            // Order by the key list (Inseminated/Injected →
                            // Damaged → Damage Rate), not DB return order.
                            const inOrder = (keys) => keys.map((k) => dg.records.find((r) => r.indicator_key === k)).filter(Boolean);
                            // Explicit display order — row 1: Follicles/
                            // Retrieved/Recovery Rate (3-across); row 2: MII
                            // count/proportion; row 3: post-wash sperm count/
                            // motility rate — not DB return order.
                            commonRecords = inOrder([
                              IK.FOLLICLES_ABOVE_14MM, IK.OOCYTE_RETRIEVED, IK.OOCYTE_RECOVERY_RATE,
                              IK.MII_OOCYTE_COUNT, IK.MII_OOCYTE_PROPORTION,
                              IK.SEMEN_USER_TYPE, IK.SEMEN_TYPE,
                              IK.SEMEN_CONCENTRATION, IK.SEMEN_MOTILITY_RATE, IK.SEMEN_MORPHOLOGY_RATE,
                              IK.SEMEN_PREPARATION_METHOD,
                              IK.POST_WASH_NO_TAIL_SPERM_COUNT, IK.POST_WASH_MOTILITY_RATE,
                            ]);
                            kpiTabs = [
                              conventionalSelected && { key: 'conventional', label: 'Conventional', records: inOrder(conventionalKeys) },
                              icsiSelected && { key: 'icsi', label: 'ICSI', records: inOrder(icsiKeys) },
                            ].filter(Boolean);
                          } else {
                            // DAY 1 — each tab shows count+rate paired per PN
                            // level, ordered 2PN, 1PN, 3PN, 0PN (not insertion
                            // order). Legacy (pre insemination-method split)
                            // keys fold into Conventional so older saved
                            // records stay visible.
                            const findRec = (key) => dg.records.find((r) => r.indicator_key === key);
                            const findRecAny = (keys) => keys.map(findRec).find(Boolean);
                            const buildPnTab = (tabKey, label, baseKeys, pnKeyMap) => {
                              const baseRec = findRecAny(baseKeys);
                              const pnRows = [2, 1, 3, 0]
                                .map((level) => ({ level, countRec: findRecAny(pnKeyMap[level].count), rateRec: findRecAny(pnKeyMap[level].rate) }))
                                .filter((row) => hasVal(row.countRec?.indicator_value) || hasVal(row.rateRec?.indicator_value));
                              if (!baseRec && pnRows.length === 0) return null;
                              return { key: tabKey, label, baseRec, pnRows };
                            };
                            const conventionalTab = buildPnTab('conventional', 'Conventional', [IK.OOCYTE_INSEMINATED], {
                              0: { count: [IK.PN0_OOCYTE_COUNT_CONVENTIONAL], rate: [IK.PN0_RATE_CONVENTIONAL] },
                              1: { count: [IK.PN1_OOCYTE_COUNT_CONVENTIONAL, IK.PN1_OOCYTE_COUNT], rate: [IK.PN1_RATE_CONVENTIONAL, IK.PN1_RATE] },
                              2: { count: [IK.PN2_OOCYTE_COUNT_CONVENTIONAL, IK.PN2_OOCYTE_COUNT], rate: [IK.PN2_RATE_CONVENTIONAL, IK.PN2_RATE] },
                              3: { count: [IK.PN3_OOCYTE_COUNT_CONVENTIONAL, IK.PN3_OOCYTE_COUNT], rate: [IK.PN3_RATE_CONVENTIONAL, IK.PN3_RATE] },
                            });
                            const icsiTab = buildPnTab('icsi', 'ICSI', [IK.OOCYTE_INJECTED_DAY1], {
                              0: { count: [IK.PN0_OOCYTE_COUNT_ICSI], rate: [IK.PN0_RATE_ICSI] },
                              1: { count: [IK.PN1_OOCYTE_COUNT_ICSI], rate: [IK.PN1_RATE_ICSI] },
                              2: { count: [IK.PN2_OOCYTE_COUNT_ICSI], rate: [IK.PN2_RATE_ICSI] },
                              3: { count: [IK.PN3_OOCYTE_COUNT_ICSI], rate: [IK.PN3_RATE_ICSI] },
                            });
                            const totalTab = buildPnTab('total', 'Total (Combined)', [IK.TOTAL_OOCYTE_BASE_DAY1], {
                              0: { count: [IK.TOTAL_PN0_OOCYTE_COUNT], rate: [IK.TOTAL_PN0_RATE] },
                              1: { count: [IK.TOTAL_PN1_OOCYTE_COUNT], rate: [IK.TOTAL_PN1_RATE] },
                              2: { count: [IK.TOTAL_PN2_OOCYTE_COUNT], rate: [IK.TOTAL_PN2_RATE] },
                              3: { count: [IK.TOTAL_PN3_OOCYTE_COUNT], rate: [IK.TOTAL_PN3_RATE] },
                            });
                            kpiTabs = [conventionalTab, icsiTab, totalTab].filter(Boolean);
                          }
                          const activeKpiTabKey = kpiTabs.some((t) => t.key === activeTabState) ? activeTabState : kpiTabs[0]?.key;
                          const activeKpiTab = kpiTabs.find((t) => t.key === activeKpiTabKey);

                          return (
                            <Box key={`day-${dg.day}`} sx={{ border: '1px solid #e9ecef', borderRadius: 2, overflow: 'hidden' }}>
                              {header}
                              <Box sx={{ p: 1.5 }}>
                                {commonRecords.length > 0 && (
                                  <Grid container spacing={1.5} sx={{ mb: kpiTabs.length ? 1.5 : 0 }}>
                                    {commonRecords.map((rec, idx) => {
                                      const threeAcross = dg.day === 0 && [IK.FOLLICLES_ABOVE_14MM, IK.OOCYTE_RETRIEVED, IK.OOCYTE_RECOVERY_RATE].includes(rec?.indicator_key);
                                      return (
                                        <IndicatorCard key={`${rec?.indicator_key}-${idx}`} rec={rec} pc={pc} tc={tc} fmtLabel={fmtLabel} hasVal={hasVal} showPct={showPct} unitOf={unitOf} progVal={progVal} smWidth={threeAcross ? 4 : 6} />
                                      );
                                    })}
                                  </Grid>
                                )}
                                {kpiTabs.length > 0 && (
                                  <>
                                    {commonRecords.length > 0 && <Divider sx={{ mb: 1.5 }} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#7b809a', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{dg.day === 0 ? 'KPI' : 'PI'}</Typography>
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        {kpiTabs.map((tab) => {
                                          const active = tab.key === activeKpiTabKey;
                                          return (
                                            <Box
                                              key={tab.key}
                                              onClick={() => setActiveTabState(tab.key)}
                                              sx={{
                                                px: 1.5, py: 0.4, borderRadius: '999px', cursor: kpiTabs.length > 1 ? 'pointer' : 'default', fontSize: '0.7rem', fontWeight: 700,
                                                border: `1.5px solid ${active ? pc : '#e0e0e0'}`,
                                                backgroundColor: active ? pc : 'transparent',
                                                color: active ? 'white' : '#666',
                                                transition: 'all 0.15s',
                                              }}
                                            >
                                              {tab.label}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                    {dg.day !== 1 ? (
                                      <Grid container spacing={1.5}>
                                        {(activeKpiTab?.records || []).map((rec, idx) => (
                                          <IndicatorCard key={`${rec?.indicator_key}-${idx}`} rec={rec} pc={pc} tc={tc} fmtLabel={fmtLabel} hasVal={hasVal} showPct={showPct} unitOf={unitOf} progVal={progVal} />
                                        ))}
                                      </Grid>
                                    ) : (
                                      <Grid container spacing={1.5}>
                                        {activeKpiTab?.baseRec && (
                                          <IndicatorCard rec={activeKpiTab.baseRec} pc={pc} tc={tc} fmtLabel={fmtLabel} hasVal={hasVal} showPct={showPct} unitOf={unitOf} progVal={progVal} fullWidth />
                                        )}
                                        {(activeKpiTab?.pnRows || []).map((row) => (
                                          <PnLevelRow key={row.level} level={row.level} countRec={row.countRec} rateRec={row.rateRec} tc={tc} hasVal={hasVal} progVal={progVal} />
                                        ))}
                                      </Grid>
                                    )}
                                  </>
                                )}
                                {commonRecords.length === 0 && kpiTabs.length === 0 && (
                                  <Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No indicator data saved yet.</Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2.5}>

              {/* ── Andrology Card ──────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Card sx={{ border: `1px solid ${hasAndrologyData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1, background: hasAndrologyData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <AndrologyIcon sx={{ color: pc, fontSize: 17 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc }}>Andrology</Typography>
                      <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Semen analysis parameters</Typography>
                    </Box>
                    {hasAndrologyData ? (
                      <IconButton size="small" onClick={openAndrologyModal} sx={{ color: pc }}>
                        {isReadOnly ? <ViewIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    ) : !isReadOnly ? (
                      <Link component="button" type="button" onClick={openAndrologyModal} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, flexShrink: 0 }}>+ Add</Link>
                    ) : null}
                  </Box>
                  <Box sx={{ p: 1.5, minHeight: 100 }}>
                    {!hasAndrologyData ? (
                      isReadOnly
                        ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No data recorded</Typography></Box>
                        : <ActionBox label="New Semen Analysis" onClick={openAndrologyModal} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {/* Basic Sperm Parameters */}
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {getAndrology(AIK.COUNT) && (
                            <Chip label={`Count: ${getAndrology(AIK.COUNT).indicator_value} M/ml`} size="small"
                              sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 700 }} />
                          )}
                          {getAndrology(AIK.MOTILITY) && (
                            <Chip label={`Motility: ${getAndrology(AIK.MOTILITY).indicator_value}%`} size="small"
                              sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                          )}
                          {getAndrology(AIK.PROGRESSIVE_MOTILITY) && (
                            <Chip label={`Progressive Motility: ${getAndrology(AIK.PROGRESSIVE_MOTILITY).indicator_value}%`} size="small"
                              sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e0f7fa', color: '#00838f', fontWeight: 600 }} />
                          )}
                          {getAndrology(AIK.MORPHOLOGY) && (
                            <Chip label={`Morphology: ${getAndrology(AIK.MORPHOLOGY).indicator_value}%`} size="small"
                              sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                          )}
                        </Box>
                        {/* DNA Fragmentation */}
                        {getAndrology(AIK.DFI) && (() => {
                          const cls = getDFIClass(getAndrology(AIK.DFI).indicator_value);
                          return (
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip label={`DFI: ${getAndrology(AIK.DFI).indicator_value}%`} size="small"
                                sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                              {cls && (
                                <Chip label={cls.label} size="small"
                                  sx={{ fontSize: '0.6rem', height: 18, backgroundColor: cls.bg, color: cls.color, fontWeight: 700, border: `1px solid ${cls.border}30` }} />
                              )}
                              {getAndrology(AIK.DFI_BEFORE) && (
                                <Chip label={`Pre-Tx: ${getAndrology(AIK.DFI_BEFORE).indicator_value}%`} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600 }} />
                              )}
                              {getAndrology(AIK.DFI_AFTER) && (
                                <Chip label={`Post-Tx: ${getAndrology(AIK.DFI_AFTER).indicator_value}%`} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                              )}
                            </Box>
                          );
                        })()}
                        {getAndrology(AIK.REMARKS)?.indicator_value && (
                          <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', fontStyle: 'italic' }}>
                            {getAndrology(AIK.REMARKS).indicator_value}
                          </Typography>
                        )}
                        {/* Uploaded reports — date-wise, so re-uploads of
                            the same report type are told apart. */}
                        {hasAndrologyFiles && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[
                              ['Semen Analysis Report', andrologyFiles.semen_analysis],
                              ['DNA Fragmentation Report', andrologyFiles.dna_fragmentation],
                            ].map(([label, files]) => files.length === 0 ? null : (
                              <Box key={label}>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', letterSpacing: '0.4px', textTransform: 'uppercase', mb: 0.4 }}>
                                  {label}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                  {files.map((f) => (
                                    <Box key={f.id} onClick={() => window.open(`${f.base_url}${f.file_path}`, '_blank', 'noopener,noreferrer')}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.6, cursor: 'pointer', color: pc, '&:hover': { textDecoration: 'underline' } }}>
                                      <PdfIcon sx={{ fontSize: 13, color: '#c62828', flexShrink: 0 }} />
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'inherit', flexShrink: 0 }}>
                                        {new Date(f.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.65rem', color: '#adb5bd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.file_name}>
                                        {f.file_name}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Cryopreservation */}
              <Grid item xs={12}>
                <Card sx={{ border: `1px solid ${hasCryoData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1, background: hasCryoData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CryoIcon sx={{ color: pc, fontSize: 17 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc }}>Cryopreservation</Typography>
                      <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Embryo, oocyte &amp; semen freezing records</Typography>
                    </Box>
                    {hasCryoData ? (
                      <IconButton size="small" onClick={() => openCryoModal(cryoDashboardTab)} sx={{ color: pc }}>
                        {isReadOnly ? <ViewIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    ) : !isReadOnly ? (
                      <Link component="button" type="button" onClick={() => openCryoModal('embryo')} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, flexShrink: 0 }}>+ Add</Link>
                    ) : null}
                  </Box>
                  <Box sx={{ p: 1.5, minHeight: 120 }}>
                    {!hasCryoData ? (
                      isReadOnly
                        ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No data recorded</Typography></Box>
                        : <ActionBox label="Record Cryo Data" onClick={() => openCryoModal('embryo')} />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                          {[{ key: 'embryo', label: 'Embryo / Oocyte' }, { key: 'semen', label: 'Semen Preservation' }].map((tab) => {
                            const active = cryoDashboardTab === tab.key;
                            return (
                              <Box
                                key={tab.key}
                                onClick={() => setCryoDashboardTab(tab.key)}
                                sx={{
                                  px: 1.5, py: 0.4, borderRadius: '999px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700,
                                  border: `1.5px solid ${active ? pc : '#e0e0e0'}`,
                                  backgroundColor: active ? pc : 'transparent',
                                  color: active ? 'white' : '#666',
                                  transition: 'all 0.15s',
                                }}
                              >
                                {tab.label}
                              </Box>
                            );
                          })}
                        </Box>
                        {cryoDashboardTab === 'semen' ? (
                          !hasSemenPresData ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                              <Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No semen preservation data recorded yet.</Typography>
                              {!isReadOnly && (
                                <Link component="button" type="button" onClick={() => openCryoModal('semen')} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, mt: 1, display: 'inline-block' }}>+ Add</Link>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {/* Demographics */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <PersonDemoCard label="Patient" person={patientPerson} compact />
                                <PersonDemoCard label="Spouse" person={spousePerson} compact />
                                {semenUserType === 'Donor' && <PersonDemoCard label="Donor" person={donorPerson} compact />}
                              </Box>

                              {/* Vials — same circular Available/Used stat badges as the Straws block */}
                              <Box>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Vials</Typography>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', my: 1.25 }}>
                                  <Box sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e8f5e9', border: '3px solid #2e7d32' }}>
                                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#2e7d32', lineHeight: 1 }}>{semenVialRecords.length - usedSemenVialCount}</Typography>
                                    <Typography sx={{ fontSize: '0.5rem', color: '#2e7d32', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Available</Typography>
                                  </Box>
                                  <Box sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffebee', border: '3px solid #c62828' }}>
                                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#c62828', lineHeight: 1 }}>{usedSemenVialCount}</Typography>
                                    <Typography sx={{ fontSize: '0.5rem', color: '#c62828', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Used</Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Entries */}
                              <Box>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>
                                  Preservation Entries
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {semenEntryRecords.map((entry) => {
                                    const et = parseTarget(entry);
                                    const vials = vialsForEntry(entry.id);
                                    return (
                                      <Box key={entry.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1.25, p: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                                          <Chip label={fmtMedia(entry.indicator_value) || 'Media —'} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: `${pc}15`, color: pc, fontWeight: 700 }} />
                                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#344767' }}>CAN {et.canNo || '—'} · Canister {et.canisterNo || '—'}</Typography>
                                          {et.preservedAt && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e' }}>· {fmtDT(et.preservedAt)}</Typography>}
                                        </Box>
                                        {(hasVal(et.beforeCount) || hasVal(et.beforeMotility)) && (
                                          <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', mb: 0.4, pl: 0.5 }}>
                                            Before cryo:{hasVal(et.beforeCount) ? ` ${et.beforeCount} million/ml` : ''}{hasVal(et.beforeCount) && hasVal(et.beforeMotility) ? ' ·' : ''}{hasVal(et.beforeMotility) ? ` ${et.beforeMotility}% motility` : ''}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                          {vials.map((vial) => {
                                            const vt = parseTarget(vial);
                                            return (
                                              <Box key={vial.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 0.5, flexWrap: 'wrap' }}>
                                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: vt.used ? '#c62828' : '#2e7d32', flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: '0.66rem', color: '#344767', fontWeight: 600 }}>Vial {vial.indicator_value || '—'}</Typography>
                                                {vt.used ? (
                                                  <Chip label={`Used${vt.usedAt ? ` — ${fmtDT(vt.usedAt)}` : ''}${vt.prepMethod ? ` · ${vt.prepMethod}` : ''}`} size="small" sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
                                                ) : (
                                                  <Chip label="Available" size="small" sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                                )}
                                                {vt.used && (hasVal(vt.postWashCount) || hasVal(vt.afterMotility)) && (
                                                  <Typography sx={{ fontSize: '0.58rem', color: '#7b809a' }}>
                                                    After thawing:{hasVal(vt.postWashCount) ? ` ${vt.postWashCount} million/ml` : ''}{hasVal(vt.postWashCount) && hasVal(vt.afterMotility) ? ' ·' : ''}{hasVal(vt.afterMotility) ? ` ${vt.afterMotility}%` : ''}
                                                  </Typography>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          )
                        ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                        {/* Cryopreservation / Thawing Dates */}
                        {(getCryo(CIK.DATE_CRYOPRESERVATION) || getCryo(CIK.DATE_THAWING)) && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Cryopreservation / Thawing Dates</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {getCryo(CIK.DATE_CRYOPRESERVATION) && <Chip label={`Cryopreservation: ${dayjs(getCryo(CIK.DATE_CRYOPRESERVATION).indicator_value, 'YYYY-MM-DD HH:mm').format('DD MMM YYYY, hh:mm A')}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 600 }} />}
                              {getCryo(CIK.DATE_THAWING) && <Chip label={`Thawing: ${dayjs(getCryo(CIK.DATE_THAWING).indicator_value, 'YYYY-MM-DD HH:mm').format('DD MMM YYYY, hh:mm A')}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                            </Box>
                          </Box>
                        )}

                        {/* Cryo Storage */}
                        {(getCryo(CIK.STORAGE_MEDIA) || getCryo(CIK.STORAGE_LN_LEVEL)) && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Cryo Storage</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {getCryo(CIK.STORAGE_MEDIA) && <Chip label={getCryo(CIK.STORAGE_MEDIA).indicator_value?.startsWith('Others:') ? `Media: ${getCryo(CIK.STORAGE_MEDIA).indicator_value.slice(7)}` : `Media: ${getCryo(CIK.STORAGE_MEDIA).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 600 }} />}
                              {getCryo(CIK.STORAGE_LN_LEVEL) && <Chip label={`LN Level: ${getCryo(CIK.STORAGE_LN_LEVEL).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f5f5f5', color: '#555' }} />}
                            </Box>
                          </Box>
                        )}

                        {/* Vitrification */}
                        {(vitriEntryRecords.length > 0 || getCryo(CIK.VITRI_D0_OOCYTE) || getCryo(CIK.VITRI_D0_RESCUED_THAWING) || getCryo(CIK.VITRI_D0_CRYO_PCT) || getCryo(CIK.VITRI_D3_EMBRYO_CRYO) || getCryo(CIK.VITRI_D3_RESCUED) || getCryo(CIK.VITRI_D3_RESCUED_RATE) || getCryo(CIK.VITRI_D4_EMBRYO_CRYO) || getCryo(CIK.VITRI_D4_RESCUED) || getCryo(CIK.VITRI_D4_RESCUED_RATE) || getCryo(CIK.VITRI_D5_EMBRYO_CRYO) || getCryo(CIK.VITRI_D5_RESCUED) || getCryo(CIK.VITRI_D5_RESCUED_RATE) || canRecords.length > 0) && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Vitrification</Typography>
                            {vitriEntryRecords.length > 0 && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.75 }}>
                                {vitriEntryRecords.map((rec) => {
                                  const t = parseTarget(rec);
                                  const pctv = Number(t.reserved) > 0 ? computeRate(t.rescued, t.reserved) : null;
                                  return (
                                    <Box key={rec.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1, px: 1, py: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                      <Chip label={rec.indicator_value || 'Type —'} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: `${pc}15`, color: pc, fontWeight: 700 }} />
                                      {hasVal(t.dayNo) && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e' }}>Day {t.dayNo}</Typography>}
                                      {hasVal(t.noPerStraw) && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e' }}>· {t.noPerStraw}/straw</Typography>}
                                      {hasVal(t.reserved) && <Chip label={`Reserved: ${t.reserved}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e8f5e9', color: '#388e3c' }} />}
                                      {hasVal(t.rescued) && <Chip label={`Rescued: ${t.rescued}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                      {pctv !== null && <Chip label={`${pctv}%`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                                      {rec.remarks && <Typography sx={{ fontSize: '0.6rem', color: '#7b809a', fontStyle: 'italic' }}>📝 {rec.remarks}</Typography>}
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                            {(getCryo(CIK.VITRI_D0_OOCYTE) || getCryo(CIK.VITRI_D0_RESCUED_THAWING) || getCryo(CIK.VITRI_D0_CRYO_PCT)) && (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#388e3c', mb: 0.3 }}>DAY-0</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                  {getCryo(CIK.VITRI_D0_OOCYTE) && <Chip label={`Cryo Preserved: ${getCryo(CIK.VITRI_D0_OOCYTE).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#388e3c' }} />}
                                  {getCryo(CIK.VITRI_D0_RESCUED_THAWING) && <Chip label={`Rescued: ${getCryo(CIK.VITRI_D0_RESCUED_THAWING).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                  {getCryo(CIK.VITRI_D0_CRYO_PCT) && <Chip label={`Cryo %: ${getCryo(CIK.VITRI_D0_CRYO_PCT).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                                </Box>
                              </Box>
                            )}
                            {(getCryo(CIK.VITRI_D3_EMBRYO_CRYO) || getCryo(CIK.VITRI_D3_RESCUED) || getCryo(CIK.VITRI_D3_REFROZE) || getCryo(CIK.VITRI_D3_RESCUED_RATE)) && (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#2e7d32', mb: 0.3 }}>DAY-3</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                  {getCryo(CIK.VITRI_D3_EMBRYO_CRYO) && <Chip label={`Cryo Preserved: ${getCryo(CIK.VITRI_D3_EMBRYO_CRYO).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                                  {getCryo(CIK.VITRI_D3_RESCUED) && <Chip label={`Rescued: ${getCryo(CIK.VITRI_D3_RESCUED).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                  {getCryo(CIK.VITRI_D3_REFROZE) && <Chip label={`Refroze: ${getCryo(CIK.VITRI_D3_REFROZE).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f5f5f5', color: '#555' }} />}
                                  {getCryo(CIK.VITRI_D3_RESCUED_RATE) && <Chip label={`Rescued %: ${getCryo(CIK.VITRI_D3_RESCUED_RATE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                                </Box>
                              </Box>
                            )}
                            {(getCryo(CIK.VITRI_D4_EMBRYO_CRYO) || getCryo(CIK.VITRI_D4_RESCUED) || getCryo(CIK.VITRI_D4_REFROZE) || getCryo(CIK.VITRI_D4_RESCUED_RATE)) && (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#e65100', mb: 0.3 }}>DAY-4</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                  {getCryo(CIK.VITRI_D4_EMBRYO_CRYO) && <Chip label={`Cryo Preserved: ${getCryo(CIK.VITRI_D4_EMBRYO_CRYO).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fbe9e7', color: '#e65100' }} />}
                                  {getCryo(CIK.VITRI_D4_RESCUED) && <Chip label={`Rescued: ${getCryo(CIK.VITRI_D4_RESCUED).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                  {getCryo(CIK.VITRI_D4_REFROZE) && <Chip label={`Refroze: ${getCryo(CIK.VITRI_D4_REFROZE).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f5f5f5', color: '#555' }} />}
                                  {getCryo(CIK.VITRI_D4_RESCUED_RATE) && <Chip label={`Rescued %: ${getCryo(CIK.VITRI_D4_RESCUED_RATE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                                </Box>
                              </Box>
                            )}
                            {(getCryo(CIK.VITRI_D5_EMBRYO_CRYO) || getCryo(CIK.VITRI_D5_RESCUED) || getCryo(CIK.VITRI_D5_REFROZE) || getCryo(CIK.VITRI_D5_RESCUED_RATE) || canRecords.length > 0) && (
                              <Box>
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#6a1b9a', mb: 0.3 }}>DAY-5</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {getCryo(CIK.VITRI_D5_EMBRYO_CRYO) && <Chip label={`Cryo Preserved: ${getCryo(CIK.VITRI_D5_EMBRYO_CRYO).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f3e5f5', color: '#6a1b9a' }} />}
                                  {getCryo(CIK.VITRI_D5_RESCUED) && <Chip label={`Rescued: ${getCryo(CIK.VITRI_D5_RESCUED).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                  {getCryo(CIK.VITRI_D5_REFROZE) && <Chip label={`Refroze: ${getCryo(CIK.VITRI_D5_REFROZE).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f5f5f5', color: '#555' }} />}
                                  {getCryo(CIK.VITRI_D5_RESCUED_RATE) && <Chip label={`Rescued %: ${getCryo(CIK.VITRI_D5_RESCUED_RATE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                                  {canRecords.length > 0 && (
                                    <Chip
                                      label={`${canRecords.length} CAN · ${caneRecords.length} CANE · ${usedStrawCount}/${strawRecords.length} straws used`}
                                      size="small"
                                      onClick={() => openCryoModal('embryo')}
                                      sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8eaf6', color: '#283593', cursor: 'pointer', fontWeight: 600, '&:hover': { backgroundColor: '#c5cae9' } }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Straw-wise details */}
                        {strawRecords.length > 0 && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Straws</Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', my: 1.25 }}>
                              <Box sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e8f5e9', border: '3px solid #2e7d32' }}>
                                <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#2e7d32', lineHeight: 1 }}>{strawRecords.length - usedStrawCount}</Typography>
                                <Typography sx={{ fontSize: '0.5rem', color: '#2e7d32', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Available</Typography>
                              </Box>
                              <Box sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffebee', border: '3px solid #c62828' }}>
                                <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#c62828', lineHeight: 1 }}>{usedStrawCount}</Typography>
                                <Typography sx={{ fontSize: '0.5rem', color: '#c62828', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Used</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                              {strawRecords.map((straw) => {
                                const st = parseTarget(straw);
                                const cane = caneRecords.find((c) => c.id === st.caneId);
                                const caneT = cane ? parseTarget(cane) : {};
                                const can = cane ? canRecords.find((c) => c.id === caneT.canId) : null;
                                return (
                                  <Box key={straw.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, border: '1px solid #f0f0f0', borderRadius: 1, px: 1, py: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: st.used ? '#c62828' : '#2e7d32', flexShrink: 0 }} />
                                    {st.color && <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: CRYO_COLOR_MAP[st.color] || '#ccc', flexShrink: 0 }} />}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#344767' }}>
                                        Straw {straw.indicator_value || '—'}
                                        {cane && (
                                          <Typography component="span" sx={{ fontSize: '0.6rem', color: '#9e9e9e', ml: 0.5, fontWeight: 400 }}>
                                            · CANE {cane.indicator_value || '—'}{can ? ` · CAN ${can.target || ''}${can.indicator_value ? `/${can.indicator_value}` : ''}` : ''}
                                          </Typography>
                                        )}
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.6rem', color: '#7b809a' }}>
                                        {st.preservedAt && `Preserved: ${fmtDT(st.preservedAt)}`}
                                        {hasVal(st.embryoCount) && `${st.preservedAt ? ' · ' : ''}${st.embryoCount} embryo${Number(st.embryoCount) === 1 ? '' : 's'}`}
                                      </Typography>
                                    </Box>
                                    {st.used ? (
                                      <Chip label={`${st.usedAt ? fmtDT(st.usedAt) : 'Used'}${hasVal(st.rescuedCount) ? ` · ${st.rescuedCount} rescued` : ''}`} size="small" sx={{ fontSize: '0.58rem', height: 18, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
                                    ) : (
                                      <Chip label="Available" size="small" sx={{ fontSize: '0.58rem', height: 18, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Straw Rescue Rate — pooled per DAY across every used straw in this procedure */}
                        {STRAW_RESCUE_DAY_OPTIONS.some((d) => strawRescueStatsByDay(d).strawCount > 0) && (
                          <Box sx={{ mt: 1.25 }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Straw Rescue Rate</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                              {STRAW_RESCUE_DAY_OPTIONS.map((d) => {
                                const stats = strawRescueStatsByDay(d);
                                if (stats.strawCount === 0) return null;
                                return (
                                  <Box key={d} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#344767', width: 44, flexShrink: 0 }}>DAY {d}</Typography>
                                    <Typography sx={{ fontSize: '0.65rem', color: '#7b809a', flex: 1 }}>{stats.totalRescued}/{stats.totalEmbryos} embryos · {stats.strawCount} straw{stats.strawCount === 1 ? '' : 's'}</Typography>
                                    <Chip label={`${stats.rate}%`} size="small" sx={{ fontSize: '0.62rem', height: 19, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Warming Survival Rate (disabled) */}
                        {false && (getCryo(CIK.WARMING_OOCYTE) || getCryo(CIK.WARMING_EMBRYO) || getCryo(CIK.WARMING_BLASTO)) && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Warming Survival Rate</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {getCryo(CIK.WARMING_OOCYTE) && <Chip label={`Oocyte: ${getCryo(CIK.WARMING_OOCYTE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fce4ec', color: '#c62828' }} />}
                              {getCryo(CIK.WARMING_EMBRYO) && <Chip label={`Embryo: ${getCryo(CIK.WARMING_EMBRYO).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fce4ec', color: '#c62828' }} />}
                              {getCryo(CIK.WARMING_BLASTO) && <Chip label={`Blasto: ${getCryo(CIK.WARMING_BLASTO).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fce4ec', color: '#c62828' }} />}
                            </Box>
                          </Box>
                        )}

                        {/* Cryo Survival Rate (disabled) */}
                        {false && (getCryo(CIK.SURVIVAL_OOCYTE) || getCryo(CIK.SURVIVAL_EMBRYO) || getCryo(CIK.SURVIVAL_BLASTO)) && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, borderBottom: '1px solid #f0f0f0', pb: 0.3 }}>Cryo Survival Rate</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {getCryo(CIK.SURVIVAL_OOCYTE) && <Chip label={`Oocyte: ${getCryo(CIK.SURVIVAL_OOCYTE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                              {getCryo(CIK.SURVIVAL_EMBRYO) && <Chip label={`Embryo: ${getCryo(CIK.SURVIVAL_EMBRYO).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                              {getCryo(CIK.SURVIVAL_BLASTO) && <Chip label={`Blasto: ${getCryo(CIK.SURVIVAL_BLASTO).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                            </Box>
                          </Box>
                        )}

                      </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Lab Condition */}
              <Grid item xs={12}>
                <Card sx={{ border: `1px solid ${pc}30`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, background: `linear-gradient(135deg, ${pc}10, transparent)` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <BiotechIcon sx={{ color: pc, fontSize: 17 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc, lineHeight: 1.1 }}>Lab Condition</Typography>
                        <Typography sx={{ fontSize: '0.63rem', color: '#7b809a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          Lab Culture Condition Record
                        </Typography>
                      </Box>
                    </Box>
                    {!isReadOnly && (
                      <Button
                        size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={openLabConditionModal}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: `${pc}10` } }}
                      >
                        Add
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    {labConditionRecords.length === 0 ? (
                      isReadOnly
                        ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No lab conditions recorded</Typography></Box>
                        : <ActionBox label="Record Lab Condition" onClick={openLabConditionModal} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 260, overflowY: 'auto' }}>
                        {labConditionRecords.map((rec, idx) => {
                          let t = {};
                          try { t = JSON.parse(rec.target || '{}'); } catch { t = {}; }
                          const type = rec.indicator_value || 'Lab Condition';
                          return (
                            <Box key={`lc-${idx}`} sx={{ border: '1px solid #e3f2fd', borderRadius: 1.5, p: 1.25, backgroundColor: `${pc}05` }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <WarningIcon sx={{ fontSize: 13, color: pc, flexShrink: 0, mt: 0.25 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: pc, lineHeight: 1.3 }}>
                                    {type}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.6rem', color: '#9e9e9e', mt: 0.25 }}>
                                    {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                                  </Typography>
                                  {(t.recordTime || type === 'INCUBATOR' || type === 'LAB ENVIRONMENT') && (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                      {type === 'INCUBATOR' && (
                                        <>
                                          {t.co2 && <Chip label={`CO2: ${t.co2}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                                          {t.temperature && <Chip label={`Temp: ${t.temperature}°C`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#fff3e0', color: '#e65100' }} />}
                                          {t.o2 && <Chip label={`O2: ${t.o2}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                        </>
                                      )}
                                      {type === 'LAB ENVIRONMENT' && (
                                        <>
                                          {t.voc && <Chip label={`VOC: ${t.voc}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />}
                                          {t.humidity && <Chip label={`Humidity: ${t.humidity}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                          {t.temperature && <Chip label={`Temp: ${t.temperature}°C`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#fff3e0', color: '#e65100' }} />}
                                        </>
                                      )}
                                      {t.recordTime && <Chip label={`Recorded: ${fmtDT(t.recordTime)}`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#f5f5f5', color: '#555' }} />}
                                    </Box>
                                  )}
                                  {rec.remarks && (
                                    <Typography sx={{ fontSize: '0.7rem', color: tc, lineHeight: 1.4, wordBreak: 'break-word', mt: 0.4 }}>
                                      {rec.remarks}
                                    </Typography>
                                  )}
                                </Box>
                                {!isReadOnly && (
                                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                    <IconButton size="small" onClick={() => openEditLabConditionModal(rec)} sx={{ p: 0.4, color: pc, '&:hover': { backgroundColor: `${pc}15` } }}>
                                      <EditIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => deleteLabCondition(rec)} sx={{ p: 0.4, color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}>
                                      <DeleteIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>

            </Grid>
          </Grid>

          {/* Bottom sections */}
          {/* ── Embryo Biopsy Card ──────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ border: `1px solid ${hasBiopsyData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1, background: hasBiopsyData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BiopsyIcon sx={{ color: pc, fontSize: 17 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc }}>Embryo Biopsy</Typography>
                  <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>PGT &amp; biopsy records</Typography>
                </Box>
                {hasBiopsyData ? (
                  <IconButton size="small" onClick={openBiopsyModal} sx={{ color: pc }}>
                    {isReadOnly ? <ViewIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                ) : !isReadOnly ? (
                  <Link component="button" type="button" onClick={openBiopsyModal} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, flexShrink: 0 }}>+ Add</Link>
                ) : null}
              </Box>
              <Box sx={{ p: 1.5, minHeight: 100 }}>
                {!hasBiopsyData ? (
                  isReadOnly
                    ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No data recorded</Typography></Box>
                    : <ActionBox label="Record Biopsy Data" onClick={openBiopsyModal} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {getBiopsy(BIK.TUBING_RATE) && <Chip label={`Tubing: ${getBiopsy(BIK.TUBING_RATE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 600 }} />}
                      {getBiopsy(BIK.PGT_TYPE) && <Chip label={`PGT: ${getBiopsy(BIK.PGT_TYPE).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />}
                      {getBiopsy(BIK.EUPLOIDY_RATE) && <Chip label={`Euploidy: ${getBiopsy(BIK.EUPLOIDY_RATE).indicator_value}%`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />}
                    </Box>
                    {getBiopsy(BIK.TUBING_RATE)?.remarks && <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', fontStyle: 'italic' }}>Tubing: {getBiopsy(BIK.TUBING_RATE).remarks}</Typography>}
                    {getBiopsy(BIK.EUPLOIDY_RATE)?.remarks && <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', fontStyle: 'italic' }}>Euploidy: {getBiopsy(BIK.EUPLOIDY_RATE).remarks}</Typography>}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

          {/* ── Embryo Transfer Card ─────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ border: `1px solid ${hasTransferData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1, background: hasTransferData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <TransferIcon sx={{ color: pc, fontSize: 17 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc }}>Embryo Transfer</Typography>
                  <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Transfer details &amp; grade</Typography>
                </Box>
                {hasTransferData ? (
                  <IconButton size="small" onClick={openTransferModal} sx={{ color: pc }}>
                    {isReadOnly ? <ViewIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                ) : !isReadOnly ? (
                  <Link component="button" type="button" onClick={openTransferModal} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, flexShrink: 0 }}>+ Add</Link>
                ) : null}
              </Box>
              <Box sx={{ p: 1.5, minHeight: 100 }}>
                {!hasTransferData ? (
                  isReadOnly
                    ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No data recorded</Typography></Box>
                    : <ActionBox label="Record Transfer Data" onClick={openTransferModal} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {getTransfer(TIK.EMBRYO_COUNT) && <Chip label={`${getTransfer(TIK.EMBRYO_COUNT).indicator_value} Embryo${getTransfer(TIK.EMBRYO_COUNT).indicator_value !== '1' ? 's' : ''}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 700 }} />}
                      {getTransfer(TIK.EMBRYO_DAYS) && <Chip label={getTransfer(TIK.EMBRYO_DAYS).indicator_value} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />}
                      {getTransfer(TIK.EMBRYO_TYPE) && <Chip label={getTransfer(TIK.EMBRYO_TYPE).indicator_value} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: getTransfer(TIK.EMBRYO_TYPE).indicator_value === 'Fresh' ? '#e3f2fd' : '#f3e5f5', color: getTransfer(TIK.EMBRYO_TYPE).indicator_value === 'Fresh' ? '#1565c0' : '#6a1b9a', fontWeight: 600 }} />}
                      {getTransfer(TIK.EMBRYO_GRADE) && <Chip label={getTransfer(TIK.EMBRYO_GRADE).indicator_value} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
                      {getTransfer(TIK.TRANSFER_PROCESS) && <Chip label={getTransfer(TIK.TRANSFER_PROCESS).indicator_value} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: getTransfer(TIK.TRANSFER_PROCESS).indicator_value === 'Smooth' ? '#e8f5e9' : '#fce4ec', color: getTransfer(TIK.TRANSFER_PROCESS).indicator_value === 'Smooth' ? '#2e7d32' : '#c62828', fontWeight: 700 }} />}
                      {getTransfer(TIK.TRANSFER_TECHNIQUE) && <Chip label={getTransfer(TIK.TRANSFER_TECHNIQUE).indicator_value} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#f5f5f5', color: '#555' }} />}
                    </Box>
                    {(getTransfer(TIK.EMBRYO_COUNT)?.remarks || getTransfer(TIK.EMBRYO_DAYS)?.remarks || getTransfer(TIK.EMBRYO_TYPE)?.remarks || getTransfer(TIK.EMBRYO_GRADE)?.remarks) && (
                      <Box sx={{ mt: 0.5 }}>
                        {getTransfer(TIK.EMBRYO_COUNT)?.remarks && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e', fontStyle: 'italic' }}>Count: {getTransfer(TIK.EMBRYO_COUNT).remarks}</Typography>}
                        {getTransfer(TIK.EMBRYO_DAYS)?.remarks && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e', fontStyle: 'italic' }}>Day: {getTransfer(TIK.EMBRYO_DAYS).remarks}</Typography>}
                        {getTransfer(TIK.EMBRYO_TYPE)?.remarks && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e', fontStyle: 'italic' }}>Type: {getTransfer(TIK.EMBRYO_TYPE).remarks}</Typography>}
                        {getTransfer(TIK.EMBRYO_GRADE)?.remarks && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e', fontStyle: 'italic' }}>Grade: {getTransfer(TIK.EMBRYO_GRADE).remarks}</Typography>}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
          {/* ── Implementation Card ──────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ border: `1px solid ${hasImplData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 1, background: hasImplData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ImplementationIcon sx={{ color: pc, fontSize: 17 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc }}>Implantation</Typography>
                  <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Embryo transfer outcome</Typography>
                </Box>
                {hasImplData ? (
                  <IconButton size="small" onClick={openImplModal} sx={{ color: pc }}>
                    {isReadOnly ? <ViewIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                ) : !isReadOnly ? (
                  <Link component="button" type="button" onClick={openImplModal} underline="always" sx={{ fontSize: '0.78rem', fontWeight: 600, color: pc, flexShrink: 0 }}>+ Add</Link>
                ) : null}
              </Box>
              <Box sx={{ p: 1.5, minHeight: 100 }}>
                {!hasImplData ? (
                  isReadOnly
                    ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No data recorded</Typography></Box>
                    : <ActionBox label="Record Implantation" onClick={openImplModal} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {getImpl(IIK.EMBRYOS_TRANSFERRED) && <Chip label={`Transferred: ${getImpl(IIK.EMBRYOS_TRANSFERRED).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}12`, color: pc, fontWeight: 700 }} />}
                      {getImpl(IIK.EMBRYOS_IMPLANTED) && <Chip label={`Implanted: ${getImpl(IIK.EMBRYOS_IMPLANTED).indicator_value}`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />}
                      {getImpl(IIK.IMPLEMENTATION) && (
                        <Chip label={getImpl(IIK.IMPLEMENTATION).indicator_value}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700,
                            backgroundColor: getImpl(IIK.IMPLEMENTATION).indicator_value === 'Successful' ? '#e8f5e9' : getImpl(IIK.IMPLEMENTATION).indicator_value === 'Failed' ? '#fce4ec' : '#fff3e0',
                            color: getImpl(IIK.IMPLEMENTATION).indicator_value === 'Successful' ? '#2e7d32' : getImpl(IIK.IMPLEMENTATION).indicator_value === 'Failed' ? '#c62828' : '#e65100',
                          }} />
                      )}
                    </Box>
                    {getImpl(IIK.REMARKS)?.indicator_value && (
                      <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', fontStyle: 'italic', mt: 0.25 }}>{getImpl(IIK.REMARKS).indicator_value}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
          {/* ── Outcome Card ──────────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ border: `1px solid ${hasOutcomeData ? pc + '30' : '#e9ecef'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${hasOutcomeData ? pc + '20' : '#e9ecef'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, background: hasOutcomeData ? `linear-gradient(135deg, ${pc}10, transparent)` : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1, backgroundColor: `${pc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <OutcomeIcon sx={{ color: pc, fontSize: 17 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: tc, lineHeight: 1.1 }}>Outcome</Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Pregnancy outcome records</Typography>
                  </Box>
                </Box>
                {!isReadOnly && (
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={openOutcomeModal}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: `${pc}10` } }}>
                    Add
                  </Button>
                )}
              </Box>
              <Box sx={{ p: 1.5 }}>
                {outcomeRecords.length === 0 ? (
                  isReadOnly
                    ? <Box sx={{ textAlign: 'center', py: 3 }}><Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>No outcomes recorded</Typography></Box>
                    : <ActionBox label="Record Outcome" onClick={openOutcomeModal} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 220, overflowY: 'auto' }}>
                    {outcomeRecords.map((rec, idx) => {
                      const p = parseOutcome(rec);
                      const subColMap = {
                        'Live Birth': { bg: '#e8f5e9', color: '#2e7d32' },
                        Miscarriage: { bg: '#fce4ec', color: '#c62828' },
                        'Multiple Gestation': { bg: '#e3f2fd', color: '#1565c0' },
                        Singleton: { bg: '#fff3e0', color: '#e65100' },
                        Biochemical: { bg: '#f3e5f5', color: '#6a1b9a' },
                      };
                      const subCol = subColMap[p.subType] || { bg: '#f5f5f5', color: '#555' };
                      return (
                        <Box key={`oc-${idx}`} sx={{ border: `1px solid ${pc}20`, borderRadius: 1.5, p: 1.25, backgroundColor: `${pc}05` }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <OutcomeIcon sx={{ fontSize: 13, color: pc, flexShrink: 0, mt: 0.3 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.4 }}>
                                {p.pregnancyType && (
                                  <Chip label={p.pregnancyType} size="small"
                                    sx={{ fontSize: '0.6rem', height: 18, backgroundColor: `${pc}15`, color: pc, fontWeight: 700 }} />
                                )}
                                {p.subType && p.subType !== p.pregnancyType && (
                                  <Chip label={p.subType} size="small"
                                    sx={{ fontSize: '0.6rem', height: 18, backgroundColor: subCol.bg, color: subCol.color, fontWeight: 600 }} />
                                )}
                                {p.miscarriageWeeks && (
                                  <Chip label={`${p.miscarriageWeeks} wks`} size="small"
                                    sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#fce4ec', color: '#c62828', fontWeight: 600 }} />
                                )}
                              </Box>
                              {rec.remarks && (
                                <Typography sx={{ fontSize: '0.68rem', color: tc, lineHeight: 1.4, wordBreak: 'break-word' }}>{rec.remarks}</Typography>
                              )}
                              <Typography sx={{ fontSize: '0.58rem', color: '#9e9e9e', mt: 0.3 }}>
                                {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                              </Typography>
                            </Box>
                            {!isReadOnly && (
                              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                <IconButton size="small" onClick={() => openEditOutcomeModal(rec)} sx={{ p: 0.4, color: pc, '&:hover': { backgroundColor: `${pc}15` } }}>
                                  <EditIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => deleteOutcome(rec)} sx={{ p: 0.4, color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}>
                                  <DeleteIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

        </Grid>

        {/* ═══════════════════════════════════════════════════════════════
            Lab Performance Modal — Day-wise sections
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={labModalOpen} onClose={() => !anyDaySaving && setLabModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          {/* Modal header bar — floats above dialog top edge */}
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChartIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Lab Performance — Day-wise Entry</Typography>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3, pb: 1 }}>

            {/* ── DAY 0 ─────────────────────────────────────────────────── */}
            <Accordion defaultExpanded sx={{ mb: 1.5, border: `1px solid ${hasDay(0) ? pc + '40' : '#e9ecef'}`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <DayHeader day={0} primaryColor={pc} hasData={hasDay(0)} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>

                {/* RI section */}
                <SectionBadge label="Reference Indicator (RI)" color="#1565c0" />
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="No of Follicles above 14mm"
                      value={day0Form.folliclesAbove14mm}
                      onChange={makeIntChange(setDay0Form, 'folliclesAbove14mm')}
                      inputProps={{ inputMode: 'numeric', min: 0 }}
                      helperText="Only numbers"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="No of Oocyte Retrieved"
                      value={day0Form.oocyteRetrieved}
                      onChange={makeIntChange(setDay0Form, 'oocyteRetrieved')}
                      inputProps={{ inputMode: 'numeric', min: 0 }}
                      helperText="Only numbers"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" label="Oocyte Recovery Rate (%)"
                      value={(day0Form.oocyteRetrieved || day0Form.folliclesAbove14mm) ? String(day0OocyteRecoveryRate) : ''}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                      helperText=" "
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={9}>
                    <TextField fullWidth size="small" label="Remarks"
                      value={day0Form.oocyteRecoveryRemarks}
                      onChange={makeRemarksChange(setDay0Form, 'oocyteRecoveryRemarks')}
                      multiline maxRows={2}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="No of MII Oocyte"
                      value={day0Form.miiOocyteCount}
                      onChange={makeIntChange(setDay0Form, 'miiOocyteCount')}
                      inputProps={{ inputMode: 'numeric', min: 0 }}
                      helperText="Only numbers"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" label="MII Oocyte Proportion (%)"
                      value={(day0Form.miiOocyteCount || day0Form.oocyteRetrieved) ? String(day0MiiProportion) : ''}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                      helperText=" "
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={9}>
                    <TextField fullWidth size="small" label="Remarks"
                      value={day0Form.miiOocyteRemarks}
                      onChange={makeRemarksChange(setDay0Form, 'miiOocyteRemarks')}
                      multiline maxRows={2}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {/* Insemination Method — moved above PI so it's decided before
                    the post-wash sperm/semen-prep fields that follow it */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.25 }}>
                    Insemination Method
                    <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControlLabel
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', m: 0 }}
                      control={
                        <Checkbox size="small"
                          checked={day0Form.inseminationConventional}
                          onChange={(e) => setDay0Form((p) => ({ ...p, inseminationConventional: e.target.checked }))}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.82rem', display: 'inline' }}>Conventional</Typography>}
                    />
                    <FormControlLabel
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', m: 0 }}
                      control={
                        <Checkbox size="small"
                          checked={day0Form.inseminationIcsi}
                          onChange={(e) => setDay0Form((p) => ({ ...p, inseminationIcsi: e.target.checked }))}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.82rem', display: 'inline' }}>ICSI</Typography>}
                    />
                  </Box>
                </Box>

                {/* Type of Semen User — mandatory. Donor forces Type of Semen
                    to Frozen (read-only); Self requires an explicit pick. */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.25 }}>
                    Type of Semen User
                    <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                  </Typography>
                  <RadioGroup row
                    value={day0Form.semenUserType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDay0Form((p) => ({
                        ...p,
                        semenUserType: val,
                        semenType: val === 'Donor' ? 'Frozen' : (p.semenUserType === 'Donor' ? '' : p.semenType),
                      }));
                    }}
                  >
                    {['Self', 'Donor'].map((opt) => (
                      <FormControlLabel key={opt} value={opt}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mr: 2.5, ml: 0 }}
                        control={<Radio size="small" />}
                        label={<Typography sx={{ fontSize: '0.82rem', display: 'inline' }}>{opt}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                {day0Form.semenUserType && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.25 }}>
                      Type of Semen
                      <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                      {day0Form.semenUserType === 'Donor' && (
                        <Typography component="span" sx={{ color: '#7b809a', ml: 1, fontSize: '0.68rem', fontStyle: 'italic' }}>
                          (Auto-selected for Donor)
                        </Typography>
                      )}
                    </Typography>
                    <RadioGroup row
                      value={day0Form.semenType}
                      onChange={(e) => setDay0Form((p) => ({ ...p, semenType: e.target.value }))}
                    >
                      {['Fresh', 'Frozen'].map((opt) => (
                        <FormControlLabel key={opt} value={opt}
                          disabled={day0Form.semenUserType === 'Donor'}
                          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mr: 2.5, ml: 0 }}
                          control={<Radio size="small" />}
                          label={<Typography sx={{ fontSize: '0.82rem', display: 'inline' }}>{opt}</Typography>}
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                )}

                {/* Semen analysis — Concentration/Motility/Morphology, optional */}
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <PctField label="Semen Concentration" valueKey="semenConcentration" remarksKey="semenConcentrationRemarks"
                    form={day0Form} onValueChange={makeNumChange(setDay0Form, 'semenConcentration')} onRemarksChange={makeRemarksChange(setDay0Form, 'semenConcentrationRemarks')} unit="million/ml" />
                </Grid>
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <PctField label="Semen Motility" valueKey="semenMotilityRate" remarksKey="semenMotilityRemarks"
                    form={day0Form} onValueChange={makePctChange(setDay0Form, 'semenMotilityRate')} onRemarksChange={makeRemarksChange(setDay0Form, 'semenMotilityRemarks')} />
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <PctField label="Semen Morphology" valueKey="semenMorphologyRate" remarksKey="semenMorphologyRemarks"
                    form={day0Form} onValueChange={makePctChange(setDay0Form, 'semenMorphologyRate')} onRemarksChange={makeRemarksChange(setDay0Form, 'semenMorphologyRemarks')} />
                </Grid>

                {/* Method of Semen Preparation — mandatory; remarks optional */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.25 }}>
                    Method of Semen Preparation
                    <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                  </Typography>
                  <RadioGroup row
                    value={day0Form.semenPrepMethod}
                    onChange={(e) => setDay0Form((p) => ({ ...p, semenPrepMethod: e.target.value }))}
                  >
                    {SEMEN_PREP_METHOD_OPTIONS.map((opt) => (
                      <FormControlLabel key={opt} value={opt}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mr: 2.5, ml: 0 }}
                        control={<Radio size="small" />}
                        label={<Typography sx={{ fontSize: '0.82rem', display: 'inline' }}>{opt}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                  <TextField fullWidth size="small" label="Remarks" sx={{ mt: 1, '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    value={day0Form.semenPrepRemarks}
                    onChange={makeRemarksChange(setDay0Form, 'semenPrepRemarks')}
                    multiline maxRows={2}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* PI section */}
                <SectionBadge label="Performance Indicator (PI)" color="#2e7d32" />
                {/* Post Wash Motile Sperm Count — numeric value with a selectable
                    UOM, or the qualitative "FEW" toggle (locks the number field,
                    "Clear" re-enables it and un-selects FEW). */}
                {(() => {
                  const isFew = day0Form.postWashSpermCount === POST_WASH_SPERM_FEW;
                  return (
                    <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            sx={{ flex: '1 1 140px', minWidth: 140 }}
                            label="Post Wash Motile Sperm Count"
                            type={isFew ? 'text' : 'number'}
                            value={day0Form.postWashSpermCount}
                            onChange={makeNumChange(setDay0Form, 'postWashSpermCount')}
                            InputProps={{ readOnly: isFew }}
                            inputProps={{ step: '0.01', min: 0 }}
                            helperText={isFew ? 'Qualitative — FEW' : `Value in ${day0Form.postWashSpermUom}, max 2 decimal`}
                          />
                          <FormControl size="small" sx={{ minWidth: 128 }} disabled={isFew}>
                            <InputLabel>UOM</InputLabel>
                            <Select
                              label="UOM"
                              value={day0Form.postWashSpermUom}
                              onChange={(e) => setDay0Form((p) => ({ ...p, postWashSpermUom: e.target.value }))}
                            >
                              {POST_WASH_SPERM_UOM_OPTIONS.map((u) => (
                                <MenuItem key={u} value={u}>{u}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant={isFew ? 'contained' : 'outlined'}
                            onClick={() => setDay0Form((p) => ({
                              ...p,
                              postWashSpermCount: p.postWashSpermCount === POST_WASH_SPERM_FEW ? '' : POST_WASH_SPERM_FEW,
                            }))}
                            sx={{
                              textTransform: 'none', minWidth: 60, height: 40,
                              backgroundColor: isFew ? pc : 'transparent',
                              color: isFew ? 'white !important' : pc,
                              borderColor: pc,
                              '&:hover': { backgroundColor: isFew ? pc : `${pc}14`, borderColor: pc },
                            }}
                          >
                            FEW
                          </Button>
                        </Box>
                        {isFew && (
                          <Button
                            size="small"
                            onClick={() => setDay0Form((p) => ({ ...p, postWashSpermCount: '' }))}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#7b809a', mt: 0.25, px: 0.5, minWidth: 0 }}
                          >
                            Clear
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth size="small" label="Remarks"
                          value={day0Form.postWashSpermRemarks}
                          onChange={makeRemarksChange(setDay0Form, 'postWashSpermRemarks')}
                          multiline maxRows={2}
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                          sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                        />
                      </Grid>
                    </Grid>
                  );
                })()}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <PctField label="Post Wash Motility Rate" valueKey="postWashMotilityRate" remarksKey="postWashMotilityRemarks"
                    form={day0Form} onValueChange={makePctChange(setDay0Form, 'postWashMotilityRate')} onRemarksChange={makeRemarksChange(setDay0Form, 'postWashMotilityRemarks')} />
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {/* KPI section */}
                <SectionBadge label="Key Performance Indicator (KPI)" color={pc} />

                {day0Form.inseminationIcsi && (
                  <>
                    <SectionBadge label="ICSI" color={pc} />
                    <Grid container spacing={2} sx={{ mb: 1.5 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="No of MII Oocyte Injected"
                          value={day0Form.oocyteInjected}
                          onChange={makeIntChange(setDay0Form, 'oocyteInjected')}
                          inputProps={{ inputMode: 'numeric', min: 0 }}
                          helperText="Only numbers"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="No of Oocyte Damaged"
                          value={day0Form.oocyteDamaged}
                          onChange={makeIntChange(setDay0Form, 'oocyteDamaged')}
                          inputProps={{ inputMode: 'numeric', min: 0 }}
                          helperText="Only numbers"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth size="small" label="Oocyte Damage Rate following ICSI (%)"
                          value={(day0Form.oocyteDamaged || day0Form.oocyteInjected) ? String(day0DamageRate) : ''}
                          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                          sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                          helperText=" "
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={8}>
                        <TextField fullWidth size="small" label="Remarks"
                          value={day0Form.oocyteDamageRateIcsiRemarks}
                          onChange={makeRemarksChange(setDay0Form, 'oocyteDamageRateIcsiRemarks')}
                          multiline maxRows={2}
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                          sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {day0Form.inseminationConventional && (
                  <>
                    <SectionBadge label="Conventional" color="#e65100" />
                    <Grid container spacing={2} sx={{ mb: 1.5 }}>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="No of MII Oocyte Inseminated"
                          value={day0Form.oocyteInseminatedConventional}
                          onChange={makeIntChange(setDay0Form, 'oocyteInseminatedConventional')}
                          inputProps={{ inputMode: 'numeric', min: 0 }}
                          helperText="Only numbers"
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid container>
                  {!isReadOnly && <SaveRow onSave={() => saveDay(0)} saving={savingDay[0]} label="Save DAY 0" onCancel={() => setLabModalOpen(false)} />}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── DAY 1 ─────────────────────────────────────────────────── */}
            <Accordion sx={{ mb: 1.5, border: `1px solid ${hasDay(1) ? pc + '40' : '#e9ecef'}`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&.Mui-expanded': { borderBottom: '1px solid #e9ecef' } }}>
                <DayHeader day={1} primaryColor={pc} hasData={hasDay(1)} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                {!day0Form.inseminationConventional && !day0Form.inseminationIcsi ? (
                  <Box sx={{ border: '1px dashed #e9ecef', borderRadius: 2, p: 2.5, textAlign: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#7b809a' }}>
                      Select an Insemination Method (Conventional or ICSI) in DAY 0 first — DAY 1 opens the matching form(s) based on that.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {day0Form.inseminationConventional && (
                      <>
                        <SectionBadge label="Performance Indicator (PI) — Conventional" color="#2e7d32" />
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of MII Oocyte Inseminated"
                              value={day1ConventionalBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 0"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 0PN Oocyte"
                              value={day1Form.pn0CountConventional}
                              onChange={makeIntChange(setDay1Form, 'pn0CountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 1PN Oocyte"
                              value={day1Form.pn1CountConventional}
                              onChange={makeIntChange(setDay1Form, 'pn1CountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 2PN Oocyte"
                              value={day1Form.pn2CountConventional}
                              onChange={makeIntChange(setDay1Form, 'pn2CountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 3PN Oocyte"
                              value={day1Form.pn3CountConventional}
                              onChange={makeIntChange(setDay1Form, 'pn3CountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="0PN Rate (%)"
                              value={day1ConventionalBase ? String(day1Pn0RateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn0RemarksConventional}
                              onChange={makeRemarksChange(setDay1Form, 'pn0RemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="1PN Rate (%)"
                              value={day1ConventionalBase ? String(day1Pn1RateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn1RemarksConventional}
                              onChange={makeRemarksChange(setDay1Form, 'pn1RemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="2PN Rate (%)"
                              value={day1ConventionalBase ? String(day1Pn2RateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn2RemarksConventional}
                              onChange={makeRemarksChange(setDay1Form, 'pn2RemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="3PN Rate (%)"
                              value={day1ConventionalBase ? String(day1Pn3RateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn3RemarksConventional}
                              onChange={makeRemarksChange(setDay1Form, 'pn3RemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {day0Form.inseminationIcsi && (
                      <>
                        <SectionBadge label="Performance Indicator (PI) — ICSI" color={pc} />
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of MII Oocyte Injected"
                              value={day1IcsiBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 0"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 0PN Oocyte"
                              value={day1Form.pn0CountIcsi}
                              onChange={makeIntChange(setDay1Form, 'pn0CountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 1PN Oocyte"
                              value={day1Form.pn1CountIcsi}
                              onChange={makeIntChange(setDay1Form, 'pn1CountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 2PN Oocyte"
                              value={day1Form.pn2CountIcsi}
                              onChange={makeIntChange(setDay1Form, 'pn2CountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="No of 3PN Oocyte"
                              value={day1Form.pn3CountIcsi}
                              onChange={makeIntChange(setDay1Form, 'pn3CountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="0PN Rate (%)"
                              value={day1IcsiBase ? String(day1Pn0RateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn0RemarksIcsi}
                              onChange={makeRemarksChange(setDay1Form, 'pn0RemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="1PN Rate (%)"
                              value={day1IcsiBase ? String(day1Pn1RateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn1RemarksIcsi}
                              onChange={makeRemarksChange(setDay1Form, 'pn1RemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="2PN Rate (%)"
                              value={day1IcsiBase ? String(day1Pn2RateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn2RemarksIcsi}
                              onChange={makeRemarksChange(setDay1Form, 'pn2RemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="3PN Rate (%)"
                              value={day1IcsiBase ? String(day1Pn3RateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day1Form.pn3RemarksIcsi}
                              onChange={makeRemarksChange(setDay1Form, 'pn3RemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {day1ShowTotal && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <SectionBadge label="Final — Conventional + ICSI Combined (Auto-calculated)" color="#6a1b9a" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Total Oocyte Inseminated/Injected"
                              value={day1TotalBase ? String(day1TotalBase) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Total 0PN Oocyte"
                              value={day1TotalPn0 ? String(day1TotalPn0) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Total 1PN Oocyte"
                              value={day1TotalPn1 ? String(day1TotalPn1) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Total 2PN Oocyte"
                              value={day1TotalPn2 ? String(day1TotalPn2) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Total 3PN Oocyte"
                              value={day1TotalPn3 ? String(day1TotalPn3) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth size="small" label="Total 0PN Rate (%)"
                              value={day1TotalBase ? String(day1TotalPn0Rate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth size="small" label="Total 1PN Rate (%)"
                              value={day1TotalBase ? String(day1TotalPn1Rate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth size="small" label="Total 2PN Rate (%)"
                              value={day1TotalBase ? String(day1TotalPn2Rate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth size="small" label="Total 3PN Rate (%)"
                              value={day1TotalBase ? String(day1TotalPn3Rate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid container>
                  {!isReadOnly && <SaveRow onSave={() => saveDay(1)} saving={savingDay[1]} label="Save DAY 1" />}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── DAY 3 ─────────────────────────────────────────────────── */}
            <Accordion sx={{ mb: 1.5, border: `1px solid ${hasDay(3) ? pc + '40' : '#e9ecef'}`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&.Mui-expanded': { borderBottom: '1px solid #e9ecef' } }}>
                <DayHeader day={3} primaryColor={pc} hasData={hasDay(3)} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                {!day0Form.inseminationConventional && !day0Form.inseminationIcsi ? (
                  <Box sx={{ border: '1px dashed #e9ecef', borderRadius: 2, p: 2.5, textAlign: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#7b809a' }}>
                      Select an Insemination Method (Conventional or ICSI) in DAY 0 first — DAY 3 opens the matching form(s) based on that.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {day0Form.inseminationConventional && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — Conventional" color="#2e7d32" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of 2PN Oocyte"
                              value={day3ConventionalBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 1"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Embryos"
                              value={day3Form.embryoCountConventional}
                              onChange={makeIntChange(setDay3Form, 'embryoCountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Embryo Formation Rate (%)"
                              value={(day3Form.embryoCountConventional || day3ConventionalBase) ? String(day3EmbryoFormationRateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day3Form.embryoFormationRemarksConventional}
                              onChange={makeRemarksChange(setDay3Form, 'embryoFormationRemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Good Quality Embryo"
                              value={day3Form.goodQualityEmbryoCountConventional}
                              onChange={makeIntChange(setDay3Form, 'goodQualityEmbryoCountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Good Quality Embryo Rate (%)"
                              value={(day3Form.goodQualityEmbryoCountConventional || day3Form.embryoCountConventional) ? String(day3GoodQualityRateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day3Form.goodQualityEmbryoRateRemarksConventional}
                              onChange={makeRemarksChange(setDay3Form, 'goodQualityEmbryoRateRemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {day0Form.inseminationIcsi && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — ICSI" color={pc} />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of 2PN Oocyte"
                              value={day3IcsiBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 1"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Embryos"
                              value={day3Form.embryoCountIcsi}
                              onChange={makeIntChange(setDay3Form, 'embryoCountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Embryo Formation Rate (%)"
                              value={(day3Form.embryoCountIcsi || day3IcsiBase) ? String(day3EmbryoFormationRateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day3Form.embryoFormationRemarksIcsi}
                              onChange={makeRemarksChange(setDay3Form, 'embryoFormationRemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Good Quality Embryo"
                              value={day3Form.goodQualityEmbryoCountIcsi}
                              onChange={makeIntChange(setDay3Form, 'goodQualityEmbryoCountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Good Quality Embryo Rate (%)"
                              value={(day3Form.goodQualityEmbryoCountIcsi || day3Form.embryoCountIcsi) ? String(day3GoodQualityRateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day3Form.goodQualityEmbryoRateRemarksIcsi}
                              onChange={makeRemarksChange(setDay3Form, 'goodQualityEmbryoRateRemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {bothMethodsSelected && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <SectionBadge label="Final — Conventional + ICSI Combined (Auto-calculated)" color="#6a1b9a" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total 2PN Oocyte"
                              value={day1TotalPn2 ? String(day1TotalPn2) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total No of Embryos"
                              value={day3TotalEmbryoCount ? String(day3TotalEmbryoCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Embryo Formation Rate (%)"
                              value={day1TotalPn2 ? String(day3TotalEmbryoFormationRate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Total No of Good Quality Embryo"
                              value={day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Total Good Quality Embryo Rate (%)"
                              value={day3TotalEmbryoCount ? String(day3TotalGoodQualityEmbryoRate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid container>
                  {!isReadOnly && <SaveRow onSave={() => saveDay(3)} saving={savingDay[3]} label="Save DAY 3" />}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── DAY 4 ─────────────────────────────────────────────────── */}
            <Accordion sx={{ mb: 1.5, border: `1px solid ${hasDay(4) ? pc + '40' : '#e9ecef'}`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&.Mui-expanded': { borderBottom: '1px solid #e9ecef' } }}>
                <DayHeader day={4} primaryColor={pc} hasData={hasDay(4)} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                {!day0Form.inseminationConventional && !day0Form.inseminationIcsi ? (
                  <Box sx={{ border: '1px dashed #e9ecef', borderRadius: 2, p: 2.5, textAlign: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#7b809a' }}>
                      Select an Insemination Method (Conventional or ICSI) in DAY 0 first — DAY 4 opens the matching form(s) based on that.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {day0Form.inseminationConventional && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — Conventional" color="#2e7d32" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of DAY 3 Good Quality Embryos"
                              value={day4ConventionalBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 3"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Compacted Embryos"
                              value={day4Form.compactedEmbryoCountConventional}
                              onChange={makeIntChange(setDay4Form, 'compactedEmbryoCountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Compaction Rate (%)"
                              value={(day4Form.compactedEmbryoCountConventional || day4ConventionalBase) ? String(day4CompactionRateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day4Form.compactionRemarksConventional}
                              onChange={makeRemarksChange(setDay4Form, 'compactionRemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {day0Form.inseminationIcsi && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — ICSI" color={pc} />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of DAY 3 Good Quality Embryos"
                              value={day4IcsiBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 3"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Compacted Embryos"
                              value={day4Form.compactedEmbryoCountIcsi}
                              onChange={makeIntChange(setDay4Form, 'compactedEmbryoCountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Compaction Rate (%)"
                              value={(day4Form.compactedEmbryoCountIcsi || day4IcsiBase) ? String(day4CompactionRateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day4Form.compactionRemarksIcsi}
                              onChange={makeRemarksChange(setDay4Form, 'compactionRemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {bothMethodsSelected && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <SectionBadge label="Final — Conventional + ICSI Combined (Auto-calculated)" color="#6a1b9a" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total DAY 3 Good Quality Embryos"
                              value={day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Compacted Embryos"
                              value={day4TotalCompactedEmbryoCount ? String(day4TotalCompactedEmbryoCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Compaction Rate (%)"
                              value={day3TotalGoodQualityEmbryoCount ? String(day4TotalCompactionRate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid container>
                  {!isReadOnly && <SaveRow onSave={() => saveDay(4)} saving={savingDay[4]} label="Save DAY 4" />}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── DAY 5 ─────────────────────────────────────────────────── */}
            <Accordion sx={{ mb: 1, border: `1px solid ${hasDay(5) ? pc + '40' : '#e9ecef'}`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&.Mui-expanded': { borderBottom: '1px solid #e9ecef' } }}>
                <DayHeader day={5} primaryColor={pc} hasData={hasDay(5)} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                {!day0Form.inseminationConventional && !day0Form.inseminationIcsi ? (
                  <Box sx={{ border: '1px dashed #e9ecef', borderRadius: 2, p: 2.5, textAlign: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#7b809a' }}>
                      Select an Insemination Method (Conventional or ICSI) in DAY 0 first — DAY 5 opens the matching form(s) based on that.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {day0Form.inseminationConventional && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — Conventional" color="#2e7d32" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Blastocyst"
                              value={day5Form.blastocystCountConventional}
                              onChange={makeIntChange(setDay5Form, 'blastocystCountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of DAY 3 Good Quality Embryos"
                              value={day5ConventionalBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 3"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Blastocyst Formation Rate (%)"
                              value={(day5Form.blastocystCountConventional || day5ConventionalBase) ? String(day5BlastocystRateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day5Form.blastocystFormationRemarksConventional}
                              onChange={makeRemarksChange(setDay5Form, 'blastocystFormationRemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Usable Blastocyst"
                              value={day5Form.usableBlastocystCountConventional}
                              onChange={makeIntChange(setDay5Form, 'usableBlastocystCountConventional')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Usable Blastocyst Formation Rate (%)"
                              value={(day5Form.usableBlastocystCountConventional || day5ConventionalBase) ? String(day5UsableBlastocystRateConventional) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day5Form.usableBlastocystFormationRemarksConventional}
                              onChange={makeRemarksChange(setDay5Form, 'usableBlastocystFormationRemarksConventional')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {day0Form.inseminationIcsi && (
                      <>
                        <SectionBadge label="Key Performance Indicator (KPI) — ICSI" color={pc} />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Blastocyst"
                              value={day5Form.blastocystCountIcsi}
                              onChange={makeIntChange(setDay5Form, 'blastocystCountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of DAY 3 Good Quality Embryos"
                              value={day5IcsiBase}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="From DAY 3"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Blastocyst Formation Rate (%)"
                              value={(day5Form.blastocystCountIcsi || day5IcsiBase) ? String(day5BlastocystRateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day5Form.blastocystFormationRemarksIcsi}
                              onChange={makeRemarksChange(setDay5Form, 'blastocystFormationRemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="No of Usable Blastocyst"
                              value={day5Form.usableBlastocystCountIcsi}
                              onChange={makeIntChange(setDay5Form, 'usableBlastocystCountIcsi')}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                              helperText="Only numbers"
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" label="Usable Blastocyst Formation Rate (%)"
                              value={(day5Form.usableBlastocystCountIcsi || day5IcsiBase) ? String(day5UsableBlastocystRateIcsi) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField fullWidth size="small" label="Remarks"
                              value={day5Form.usableBlastocystFormationRemarksIcsi}
                              onChange={makeRemarksChange(setDay5Form, 'usableBlastocystFormationRemarksIcsi')}
                              multiline maxRows={2}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {bothMethodsSelected && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <SectionBadge label="Final — Conventional + ICSI Combined (Auto-calculated)" color="#6a1b9a" />
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total DAY 3 Good Quality Embryos"
                              value={day3TotalGoodQualityEmbryoCount ? String(day3TotalGoodQualityEmbryoCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Blastocyst"
                              value={day5TotalBlastocystCount ? String(day5TotalBlastocystCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Blastocyst Formation Rate (%)"
                              value={day3TotalGoodQualityEmbryoCount ? String(day5TotalBlastocystRate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Usable Blastocyst"
                              value={day5TotalUsableBlastocystCount ? String(day5TotalUsableBlastocystCount) : ''}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Total Usable Blastocyst Formation Rate (%)"
                              value={day3TotalGoodQualityEmbryoCount ? String(day5TotalUsableBlastocystRate) : ''}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText=" "
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid container>
                  {!isReadOnly && <SaveRow onSave={() => saveDay(5)} saving={savingDay[5]} label="Save DAY 5" />}
                </Grid>
              </AccordionDetails>
            </Accordion>

          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button onClick={() => setLabModalOpen(false)} disabled={anyDaySaving} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Lab Condition Deviation Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={labConditionModalOpen} onClose={() => { if (!savingLabCondition) { setLabConditionModalOpen(false); setLcEditId(null); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BiotechIcon sx={{ color: 'white', fontSize: 20 }} />
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{lcEditId ? 'Edit Lab Condition' : 'Add Lab Condition'}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>Lab Culture Condition Record</Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" error={!!lcFieldErrors.deviationType} sx={{ '& .MuiInputBase-root': { height: 40 }, '& .MuiSelect-select': { py: '8.5px !important' } }}>
                  <InputLabel sx={{ top: '-2px', '&.MuiInputLabel-shrink': { top: 0 } }}>Lab Condition</InputLabel>
                  <Select
                    size="small"
                    value={lcForm.deviationType}
                    onChange={(e) => handleLcTypeChange(e.target.value)}
                    label="Lab Condition"
                    MenuProps={{ PaperProps: { sx: { maxHeight: 220, mt: 0.5 } } }}
                  >
                    {LAB_CONDITION_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem', py: 0.75 }}>{opt}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{lcFieldErrors.deviationType ? 'Select a lab condition type' : 'Select the type of lab condition being recorded'}</FormHelperText>
                </FormControl>
              </Grid>

              {lcForm.deviationType && (
                <Grid item xs={12} sm={6}>
                  <MDDateTimePicker label="Record Time *" value={lcForm.recordTime} onChange={(val) => setLcFV('recordTime', val)} />
                  {lcFieldErrors.recordTime && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Record time is required.</Typography>}
                </Grid>
              )}

              {lcForm.deviationType === 'INCUBATOR' && (
                <>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="CO2 *" value={lcForm.co2} onChange={setLcF('co2')} error={!!lcFieldErrors.co2} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Temperature (°C) *" value={lcForm.temperature} onChange={setLcF('temperature')} error={!!lcFieldErrors.temperature} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="O2" value={lcForm.o2} onChange={setLcF('o2')} error={!!lcFieldErrors.o2} />
                  </Grid>
                  {(lcFieldErrors.co2 || lcFieldErrors.temperature) && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f' }}>CO2 and Temperature are required for Incubator.</Typography>
                    </Grid>
                  )}
                </>
              )}

              {lcForm.deviationType === 'LAB ENVIRONMENT' && (
                <>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="VOC *" value={lcForm.voc} onChange={setLcF('voc')} error={!!lcFieldErrors.voc} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Humidity *" value={lcForm.humidity} onChange={setLcF('humidity')} error={!!lcFieldErrors.humidity} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" type="number" label="Temperature (°C) *" value={lcForm.temperature} onChange={setLcF('temperature')} error={!!lcFieldErrors.temperature} />
                  </Grid>
                  {(lcFieldErrors.voc || lcFieldErrors.humidity || lcFieldErrors.temperature) && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f' }}>VOC, Humidity and Temperature are all required for Lab Environment.</Typography>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label={lcForm.deviationType === 'OTHERS' ? 'Remarks *' : 'Remarks'}
                  value={lcForm.remarks}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setLcForm((p) => ({ ...p, remarks: v }));
                    setLcFieldErrors((p) => ({ ...p, remarks: false }));
                  }}
                  multiline minRows={3} maxRows={5}
                  error={!!lcFieldErrors.remarks}
                  helperText={lcFieldErrors.remarks ? 'Remarks are required for Others' : 'Optional (auto-uppercase)'}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setLabConditionModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setLabConditionModalOpen(false)} disabled={savingLabCondition} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button
                  onClick={saveLabCondition} variant="contained" disabled={savingLabCondition}
                  startIcon={savingLabCondition ? null : <SaveIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, '& .MuiButton-startIcon': { color: 'white !important' }, fontWeight: 600, minWidth: 140 }}
                >
                  {savingLabCondition ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Cryopreservation Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={cryoModalOpen} onClose={() => !Object.values(savingCryo).some(Boolean) && setCryoModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CryoIcon sx={{ color: 'white', fontSize: 20 }} />
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Cryopreservation</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>{cryoModalTab === 'semen' ? 'Semen freezing records' : 'Embryo & oocyte freezing records'}</Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3, pb: 1 }}>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {[{ key: 'embryo', label: 'Embryo / Oocyte' }, { key: 'semen', label: 'Semen Preservation' }].map((tab) => {
                const active = cryoModalTab === tab.key;
                return (
                  <Box
                    key={tab.key}
                    onClick={() => setCryoModalTab(tab.key)}
                    sx={{
                      px: 2, py: 0.7, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700,
                      border: `1.5px solid ${active ? pc : '#e0e0e0'}`,
                      backgroundColor: active ? `${pc}12` : 'transparent',
                      color: active ? pc : '#666',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: pc },
                    }}
                  >
                    {tab.label}
                  </Box>
                );
              })}
            </Box>

            {cryoModalTab === 'embryo' && (
            <>
            {/* ── 0. Cryopreservation / Thawing Dates (optional) ─────────── */}
            <Accordion defaultExpanded sx={{ mb: 1.5, border: `1px solid #e9ecef`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: `${pc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 15, color: pc }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Cryopreservation / Thawing Dates</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDDateTimePicker
                      label="Cryopreservation Date"
                      value={cryoForm.cryoDate}
                      onChange={(val) => setCFV('cryoDate', val)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Remarks (Cryopreservation Date)"
                      value={cryoForm.cryoDateRemarks} onChange={setCF('cryoDateRemarks')}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDDateTimePicker
                      label="Thawing Date"
                      value={cryoForm.thawDate}
                      onChange={(val) => setCFV('thawDate', val)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Remarks (Thawing Date)"
                      value={cryoForm.thawDateRemarks} onChange={setCF('thawDateRemarks')}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoDates} saving={savingCryo.dates} label="Save Dates" /></Grid>}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 1. Cryo Storage ───────────────────────────────────────── */}
            <Accordion defaultExpanded sx={{ mb: 1.5, border: `1px solid #e9ecef`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: `${pc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CryoIcon sx={{ fontSize: 15, color: pc }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Cryo Storage</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  {/* Media */}
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#344767', mb: 1 }}>Media</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {CRYO_MEDIA_OPTIONS.map((opt) => (
                            <Box key={opt} onClick={() => setCFV('csMedia', opt)} sx={{ px: 2, py: 0.6, borderRadius: 1, cursor: 'pointer', fontSize: '0.8rem', border: `1.5px solid ${cryoForm.csMedia === opt ? pc : '#e0e0e0'}`, backgroundColor: cryoForm.csMedia === opt ? `${pc}12` : 'transparent', color: cryoForm.csMedia === opt ? pc : '#666', fontWeight: cryoForm.csMedia === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}>
                              {opt}
                            </Box>
                          ))}
                        </Box>
                        {cryoForm.csMedia === 'Others' && (
                          <TextField fullWidth size="small" label="Specify Media" value={cryoForm.csMediaOthers} onChange={setCF('csMediaOthers')} sx={{ mt: 1.5 }} />
                        )}
                      </Box>
                      <TextField size="small" label="Remarks (Media)" value={cryoForm.csMediaRemarks} onChange={setCF('csMediaRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ flex: 1, '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                    </Box>
                  </Grid>
                  {/* Liquid Nitrogen Level */}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Liquid Nitrogen Level" value={cryoForm.csLnLevel} onChange={setCF('csLnLevel')} helperText="Free text (e.g. 85%, Full, Low)" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Remarks (LN Level)" value={cryoForm.csLnLevelRemarks} onChange={setCF('csLnLevelRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoStorage} saving={savingCryo.storage} label="Save Cryo Storage" /></Grid>}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 2. Vitrification ─────────────────────────────────────── */}
            <Accordion defaultExpanded sx={{ mb: 1.5, border: `1px solid #e9ecef`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ScienceIcon sx={{ fontSize: 15, color: '#1565c0' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Vitrification</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>

                {/* Vitrification entries — repeatable "Add More" rows.
                    Each: Type (Oocyte/Embryo/Blastocyst) · Day No · No per
                    Straw · No Reserved · No Rescued (Thawing) · % (auto). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#344767' }}>Vitrification Entries</Typography>
                  {!isReadOnly && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={addVitriEntry} sx={{ textTransform: 'none', fontSize: '0.72rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: `${pc}10` } }}>
                      Add More
                    </Button>
                  )}
                </Box>

                {vitriEntries.length === 0 ? (
                  <Typography sx={{ color: '#9e9e9e', fontSize: '0.78rem', textAlign: 'center', py: 2 }}>
                    No vitrification entries yet.{!isReadOnly && ' Click "Add More".'}
                  </Typography>
                ) : (
                  vitriEntries.map((row, idx) => {
                    const noun = row.type || 'Oocyte/Embryo/Blastocyst';
                    const pct = (row.reserved || row.rescued) ? String(computeRate(row.rescued, row.reserved)) : '';
                    return (
                      <Box key={row._k || idx} sx={{ border: '1px solid #e9ecef', borderRadius: 1.5, p: 1.5, mb: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#7b809a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Entry {idx + 1}</Typography>
                          {!isReadOnly && (
                            <IconButton size="small" onClick={() => removeVitriEntry(idx)} sx={{ p: 0.4, color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={isReadOnly}>
                              <InputLabel>Type</InputLabel>
                              <Select label="Type" value={row.type} onChange={(e) => setVitriField(idx, 'type', e.target.value)}>
                                {VITRI_TYPE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Day No" value={row.dayNo} onChange={setVitriIntField(idx, 'dayNo')} InputProps={{ readOnly: isReadOnly }} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="No per Straw" value={row.noPerStraw} onChange={setVitriIntField(idx, 'noPerStraw')} InputProps={{ readOnly: isReadOnly }} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label={`No of ${noun} Reserved`} value={row.reserved} onChange={setVitriIntField(idx, 'reserved')} InputProps={{ readOnly: isReadOnly }} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label={`No of ${noun} Rescued (Thawing)`} value={row.rescued} onChange={setVitriIntField(idx, 'rescued')} InputProps={{ readOnly: isReadOnly }} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Percentage"
                              value={pct}
                              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                              sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
                              helperText="Auto: Rescued / Reserved"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Remarks" value={row.remarks} onChange={(e) => setVitriField(idx, 'remarks', e.target.value.toUpperCase())} InputProps={{ readOnly: isReadOnly }} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })
                )}

                {!isReadOnly && vitriEntries.length > 0 && (
                  <Grid container sx={{ mt: 0.5 }}><SaveRow onSave={saveVitriEntries} saving={savingVitri} label="Save Vitrification" /></Grid>
                )}

                {/* DAY-3/4/5 manual Cryo Preserved/Rescued/Refroze form —
                    disabled 2026-07-23: this data is now fully captured
                    per-straw (mark-used modal takes rescuedDay + rescued
                    count, rolled up into the "Straw Rescue Rate" section
                    below), so keeping both was pure duplication. Left
                    commented out (not deleted) in case it's needed again —
                    state/save handlers (cryoForm.vD3EmbryoCryo etc,
                    saveCryoVitriD3/4/5) are untouched. */}
                {false && (
                <>
                <Divider sx={{ my: 2 }} />

                {/* DAY-3 */}
                <SectionBadge label="DAY-3" color="#2e7d32" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Cryo Preserved" value={cryoForm.vD3EmbryoCryo} onChange={makeIntChange(setCryoForm, 'vD3EmbryoCryo')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Rescued After Thawing" value={cryoForm.vD3Rescued} onChange={makeIntChange(setCryoForm, 'vD3Rescued')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Refroze" value={cryoForm.vD3Refroze} onChange={makeIntChange(setCryoForm, 'vD3Refroze')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Embryo Rescued Proportion Rate (%)"
                      value={(cryoForm.vD3EmbryoCryo || cryoForm.vD3Rescued) ? String(computeRate(cryoForm.vD3Rescued, cryoForm.vD3EmbryoCryo)) : ''}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }} helperText=" " />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Remarks" value={cryoForm.vD3RescuedRemarks} onChange={setCF('vD3RescuedRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText=" " />
                  </Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoVitriD3} saving={savingCryo.vitriD3} label="Save DAY-3" /></Grid>}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* DAY-4 */}
                <SectionBadge label="DAY-4" color="#e65100" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Cryo Preserved" value={cryoForm.vD4EmbryoCryo} onChange={makeIntChange(setCryoForm, 'vD4EmbryoCryo')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Rescued After Thawing" value={cryoForm.vD4Rescued} onChange={makeIntChange(setCryoForm, 'vD4Rescued')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Refroze" value={cryoForm.vD4Refroze} onChange={makeIntChange(setCryoForm, 'vD4Refroze')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Embryo Rescued Proportion Rate (%)"
                      value={(cryoForm.vD4EmbryoCryo || cryoForm.vD4Rescued) ? String(computeRate(cryoForm.vD4Rescued, cryoForm.vD4EmbryoCryo)) : ''}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }} helperText=" " />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Remarks" value={cryoForm.vD4RescuedRemarks} onChange={setCF('vD4RescuedRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText=" " />
                  </Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoVitriD4} saving={savingCryo.vitriD4} label="Save DAY-4" /></Grid>}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* DAY-5 */}
                <SectionBadge label="DAY-5" color="#6a1b9a" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Cryo Preserved" value={cryoForm.vD5EmbryoCryo} onChange={makeIntChange(setCryoForm, 'vD5EmbryoCryo')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Embryos Rescued After Thawing" value={cryoForm.vD5Rescued} onChange={makeIntChange(setCryoForm, 'vD5Rescued')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="No of Refroze" value={cryoForm.vD5Refroze} onChange={makeIntChange(setCryoForm, 'vD5Refroze')} inputProps={{ inputMode: 'numeric', min: 0 }} helperText="Only numbers" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Embryo Rescued Proportion Rate (%)"
                      value={(cryoForm.vD5EmbryoCryo || cryoForm.vD5Rescued) ? String(computeRate(cryoForm.vD5Rescued, cryoForm.vD5EmbryoCryo)) : ''}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.78rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }} helperText=" " />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Remarks" value={cryoForm.vD5RescuedRemarks} onChange={setCF('vD5RescuedRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText=" " />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {!isReadOnly && <Grid container sx={{ mt: 0.5 }}><SaveRow onSave={saveCryoVitriD5} saving={savingCryo.vitriD5} label="Save DAY-5" /></Grid>}
                </>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* CAN → Canister → CANE → Straw hierarchy */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#344767' }}>CAN / Canister / CANE / Straw</Typography>
                  {!isReadOnly && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={openCanModal} sx={{ textTransform: 'none', fontSize: '0.72rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: `${pc}10` } }}>
                      Add CAN
                    </Button>
                  )}
                </Box>
                {canRecords.length === 0 ? (
                  <Typography sx={{ color: '#9e9e9e', fontSize: '0.78rem', textAlign: 'center', py: 2 }}>No CAN recorded yet.</Typography>
                ) : (
                  canRecords.map((can) => {
                    const isCanOpen = expandedCanId === can.id;
                    const canes = canesForCan(can.id);
                    return (
                      <Box key={can.id} sx={{ border: '1px solid #e9ecef', borderRadius: 1.5, mb: 1.25, overflow: 'hidden' }}>
                        <Box
                          onClick={() => setExpandedCanId(isCanOpen ? null : can.id)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, cursor: 'pointer', backgroundColor: isCanOpen ? `${pc}08` : '#fcfcfd', '&:hover': { backgroundColor: `${pc}08` } }}
                        >
                          <ExpandMoreIcon sx={{ fontSize: 18, color: '#7b809a', transform: isCanOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                          <Chip label={can.target || 'CAN'} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}15`, color: pc, fontWeight: 700 }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767' }}>Canister: {can.indicator_value || '—'}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', flex: 1 }}>{canes.length} CANE{canes.length === 1 ? '' : 's'}</Typography>
                          {!isReadOnly && (
                            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                              <IconButton size="small" onClick={() => openEditCanModal(can)} sx={{ p: 0.4, color: pc, '&:hover': { backgroundColor: `${pc}15` } }}><EditIcon sx={{ fontSize: 13 }} /></IconButton>
                              <IconButton size="small" onClick={() => deleteCan(can)} sx={{ p: 0.4, color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}><DeleteIcon sx={{ fontSize: 13 }} /></IconButton>
                            </Box>
                          )}
                        </Box>
                        {isCanOpen && (
                          <Box sx={{ p: 1.25, pt: 1, borderTop: '1px solid #e9ecef' }}>
                            {can.remarks && <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', fontStyle: 'italic', mb: 1 }}>{can.remarks}</Typography>}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#344767', textTransform: 'uppercase', letterSpacing: '0.4px' }}>CANEs</Typography>
                              {!isReadOnly && (
                                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 11 }} />} onClick={() => openCaneModal(can.id)} sx={{ textTransform: 'none', fontSize: '0.68rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.3, '&:hover': { backgroundColor: `${pc}10` } }}>
                                  Add CANE
                                </Button>
                              )}
                            </Box>
                            {canes.length === 0 ? (
                              <Typography sx={{ color: '#9e9e9e', fontSize: '0.72rem', textAlign: 'center', py: 1 }}>No CANE recorded for this CAN.</Typography>
                            ) : (
                              canes.map((cane) => {
                                const isCaneOpen = expandedCaneId === cane.id;
                                const caneT = parseTarget(cane);
                                const straws = strawsForCane(cane.id);
                                const usedCount = straws.filter((s) => parseTarget(s).used).length;
                                return (
                                  <Box key={cane.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1.25, mb: 1, ml: 1, overflow: 'hidden' }}>
                                    <Box
                                      onClick={() => setExpandedCaneId(isCaneOpen ? null : cane.id)}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, cursor: 'pointer', backgroundColor: isCaneOpen ? '#f7f7fb' : '#fff', '&:hover': { backgroundColor: '#f7f7fb' } }}
                                    >
                                      <ExpandMoreIcon sx={{ fontSize: 16, color: '#9e9e9e', transform: isCaneOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#344767' }}>CANE {cane.indicator_value || '—'}</Typography>
                                      {caneT.tag && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: CRYO_COLOR_MAP[caneT.tag] || '#ccc' }} />
                                          <Typography sx={{ fontSize: '0.65rem', color: '#7b809a' }}>{caneT.tag}</Typography>
                                        </Box>
                                      )}
                                      {caneT.goblet && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: CRYO_COLOR_MAP[caneT.goblet] || '#ccc' }} />
                                          <Typography sx={{ fontSize: '0.65rem', color: '#7b809a' }}>{caneT.goblet}</Typography>
                                        </Box>
                                      )}
                                      <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', flex: 1 }}>{straws.length} straw{straws.length === 1 ? '' : 's'} · {usedCount} used</Typography>
                                      {!isReadOnly && (
                                        <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                                          <IconButton size="small" onClick={() => openEditCaneModal(cane)} sx={{ p: 0.3, color: pc }}><EditIcon sx={{ fontSize: 12 }} /></IconButton>
                                          <IconButton size="small" onClick={() => deleteCane(cane)} sx={{ p: 0.3, color: '#d32f2f' }}><DeleteIcon sx={{ fontSize: 12 }} /></IconButton>
                                        </Box>
                                      )}
                                    </Box>
                                    {isCaneOpen && (
                                      <Box sx={{ p: 1, pt: 0.75, borderTop: '1px solid #f0f0f0' }}>
                                        {cane.remarks && <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', fontStyle: 'italic', mb: 0.75 }}>{cane.remarks}</Typography>}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#344767', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Straws</Typography>
                                          {!isReadOnly && (
                                            <Button size="small" startIcon={<AddIcon sx={{ fontSize: 10 }} />} onClick={() => openAddStrawsModal(cane.id)} sx={{ textTransform: 'none', fontSize: '0.64rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 0.75, py: 0.2, '&:hover': { backgroundColor: `${pc}10` } }}>
                                              Add Straws
                                            </Button>
                                          )}
                                        </Box>
                                        {straws.length === 0 ? (
                                          <Typography sx={{ color: '#9e9e9e', fontSize: '0.68rem', textAlign: 'center', py: 0.75 }}>No straws recorded for this CANE.</Typography>
                                        ) : (
                                          straws.map((straw) => {
                                            const st = parseTarget(straw);
                                            return (
                                              <Box key={straw.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ml: 1, py: 0.5, borderBottom: '1px dashed #f0f0f0' }}>
                                                <Checkbox size="small" checked={!!st.used} onChange={() => openMarkUsedModal(straw)} disabled={isReadOnly || st.used} sx={{ p: 0.3, mt: -0.3 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {st.color && <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: CRYO_COLOR_MAP[st.color] || '#ccc', flexShrink: 0 }} />}
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#344767' }}>Straw {straw.indicator_value || '—'}</Typography>
                                                    {hasVal(st.embryoCount) && (
                                                      <Chip label={`${st.embryoCount} embryo${Number(st.embryoCount) === 1 ? '' : 's'}`} size="small" sx={{ fontSize: '0.6rem', height: 17, backgroundColor: '#ede7f6', color: '#5e35b1', fontWeight: 600 }} />
                                                    )}
                                                    {st.used ? (
                                                      <Chip
                                                        label={`Used${st.usedAt ? ` — ${fmtDT(st.usedAt)}` : ''}${hasVal(st.rescuedDay) ? ` · DAY ${st.rescuedDay}` : ''}${hasVal(st.rescuedCount) ? ` · ${st.rescuedCount} rescued` : ''}`}
                                                        size="small" sx={{ fontSize: '0.6rem', height: 17, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }}
                                                      />
                                                    ) : (
                                                      <Chip label="Available" size="small" sx={{ fontSize: '0.6rem', height: 17, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                                    )}
                                                  </Box>
                                                  {st.preservedAt && <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>Preserved: {fmtDT(st.preservedAt)}</Typography>}
                                                  {straw.remarks && <Typography sx={{ fontSize: '0.63rem', color: '#9e9e9e', fontStyle: 'italic' }}>{straw.remarks}</Typography>}
                                                </Box>
                                                {!isReadOnly && (
                                                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                                    <IconButton size="small" onClick={() => openEditStrawModal(straw)} sx={{ p: 0.3, color: pc }}><EditIcon sx={{ fontSize: 12 }} /></IconButton>
                                                    {!st.used && (
                                                      <IconButton size="small" onClick={() => deleteStraw(straw)} sx={{ p: 0.3, color: '#d32f2f' }}><DeleteIcon sx={{ fontSize: 12 }} /></IconButton>
                                                    )}
                                                  </Box>
                                                )}
                                              </Box>
                                            );
                                          })
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </AccordionDetails>
            </Accordion>
            </>
            )}

            {cryoModalTab === 'semen' && (
            <>
                {/* Patient & Spouse always shown; Donor only when DAY 0's
                    Type of Semen User is 'Donor' — read live from saved
                    records, not the (possibly-unopened) DAY 0 form state. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                  <PersonDemoCard label="Patient" person={patientPerson} />
                  <PersonDemoCard label="Spouse" person={spousePerson} />
                  {semenUserType === 'Donor' && <PersonDemoCard label="Donor" person={donorPerson} />}
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#344767' }}>Preservation Entries</Typography>
                  {!isReadOnly && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={openSemenEntryModal} sx={{ textTransform: 'none', fontSize: '0.72rem', color: pc, border: `1px solid ${pc}50`, borderRadius: 1, px: 1, py: 0.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: `${pc}10` } }}>
                      Add Entry
                    </Button>
                  )}
                </Box>
                {semenEntryRecords.length === 0 ? (
                  <Typography sx={{ color: '#9e9e9e', fontSize: '0.78rem', textAlign: 'center', py: 2 }}>No semen preservation entry recorded yet.</Typography>
                ) : (
                  semenEntryRecords.map((entry) => {
                    const isEntryOpen = expandedSemenEntryId === entry.id;
                    const et = parseTarget(entry);
                    const vials = vialsForEntry(entry.id);
                    const usedCount = vials.filter((v) => parseTarget(v).used).length;
                    return (
                      <Box key={entry.id} sx={{ border: '1px solid #e9ecef', borderRadius: 1.5, mb: 1.25, overflow: 'hidden' }}>
                        <Box
                          onClick={() => setExpandedSemenEntryId(isEntryOpen ? null : entry.id)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, cursor: 'pointer', backgroundColor: isEntryOpen ? `${pc}08` : '#fcfcfd', '&:hover': { backgroundColor: `${pc}08` } }}
                        >
                          <ExpandMoreIcon sx={{ fontSize: 18, color: '#7b809a', transform: isEntryOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                          <Chip label={fmtMedia(entry.indicator_value) || 'Media —'} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: `${pc}15`, color: pc, fontWeight: 700 }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767' }}>CAN {et.canNo || '—'} · Canister {et.canisterNo || '—'}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', flex: 1 }}>{usedCount}/{vials.length} vials used</Typography>
                          {!isReadOnly && (
                            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                              <IconButton size="small" onClick={() => openEditSemenEntryModal(entry)} sx={{ p: 0.4, color: pc, '&:hover': { backgroundColor: `${pc}15` } }}><EditIcon sx={{ fontSize: 13 }} /></IconButton>
                              <IconButton size="small" onClick={() => deleteSemenEntry(entry)} sx={{ p: 0.4, color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}><DeleteIcon sx={{ fontSize: 13 }} /></IconButton>
                            </Box>
                          )}
                        </Box>
                        {isEntryOpen && (
                          <Box sx={{ p: 1.25, pt: 1, borderTop: '1px solid #e9ecef' }}>
                            {et.preservedAt && <Typography sx={{ fontSize: '0.68rem', color: '#7b809a', mb: 0.5 }}>Preserved: {fmtDT(et.preservedAt)}</Typography>}
                            {(hasVal(et.beforeCount) || hasVal(et.beforeMotility)) && (
                              <Typography sx={{ fontSize: '0.68rem', color: '#7b809a', mb: 0.5 }}>
                                Before cryo:{hasVal(et.beforeCount) ? ` ${et.beforeCount} million/ml` : ''}{hasVal(et.beforeCount) && hasVal(et.beforeMotility) ? ' ·' : ''}{hasVal(et.beforeMotility) ? ` ${et.beforeMotility}% motility` : ''}
                              </Typography>
                            )}
                            {entry.remarks && <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', fontStyle: 'italic', mb: 1 }}>{entry.remarks}</Typography>}
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#344767', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.75 }}>Vials</Typography>
                            {vials.map((vial) => {
                              const vt = parseTarget(vial);
                              return (
                                <Box key={vial.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.5, borderBottom: '1px dashed #f0f0f0' }}>
                                  <Checkbox size="small" checked={!!vt.used} onChange={() => openMarkVialUsedModal(vial)} disabled={isReadOnly || vt.used} sx={{ p: 0.3, mt: -0.3 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#344767' }}>Vial {vial.indicator_value || '—'}</Typography>
                                      {vt.used ? (
                                        <Chip
                                          label={`Used${vt.usedAt ? ` — ${fmtDT(vt.usedAt)}` : ''}${vt.prepMethod ? ` · ${vt.prepMethod}` : ''}`}
                                          size="small" sx={{ fontSize: '0.6rem', height: 17, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }}
                                        />
                                      ) : (
                                        <Chip label="Available" size="small" sx={{ fontSize: '0.6rem', height: 17, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                      )}
                                    </Box>
                                    {(hasVal(vt.postWashCount) || hasVal(vt.afterMotility)) && (
                                      <Typography sx={{ fontSize: '0.63rem', color: '#7b809a' }}>
                                        After thawing:{hasVal(vt.postWashCount) ? ` ${vt.postWashCount} million/ml` : ''}{hasVal(vt.postWashCount) && hasVal(vt.afterMotility) ? ' ·' : ''}{hasVal(vt.afterMotility) ? ` ${vt.afterMotility}% motility` : ''}
                                      </Typography>
                                    )}
                                    {vial.remarks && <Typography sx={{ fontSize: '0.63rem', color: '#9e9e9e', fontStyle: 'italic' }}>{vial.remarks}</Typography>}
                                  </Box>
                                  {!isReadOnly && (
                                    <IconButton size="small" onClick={() => openViewVialModal(vial)} sx={{ p: 0.3, color: pc, flexShrink: 0 }}><EditIcon sx={{ fontSize: 12 }} /></IconButton>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
            </>
            )}

            {/* ── 3. Warming / Cryo Survival Rate ──────────────────────── (disabled)
            <Accordion sx={{ mb: 1.5, border: `1px solid #e9ecef`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShowChartIcon sx={{ fontSize: 15, color: '#c62828' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Warming / Cryo Survival Rate</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Oocyte (%)" value={cryoForm.wOocyte} onChange={setCF('wOocyte')} inputProps={{ min: 0, max: 100, step: '0.01' }} /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.wOocyteRemarks} onChange={setCF('wOocyteRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Embryo (%)" value={cryoForm.wEmbryo} onChange={setCF('wEmbryo')} inputProps={{ min: 0, max: 100, step: '0.01' }} /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.wEmbryoRemarks} onChange={setCF('wEmbryoRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Blastocyst (%)" value={cryoForm.wBlasto} onChange={setCF('wBlasto')} inputProps={{ min: 0, max: 100, step: '0.01' }} /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.wBlastoRemarks} onChange={setCF('wBlastoRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoWarming} saving={savingCryo.warming} label="Save Warming Rate" /></Grid>}
                </Grid>
              </AccordionDetails>
            </Accordion>
            */}

            {/* ── 4. Cryo Survival Rate ─────────────────────────────────── (disabled)
            <Accordion sx={{ mb: 1.5, border: `1px solid #e9ecef`, boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', '&.Mui-expanded': { borderBottom: '1px solid #e9ecef', borderRadius: '8px 8px 0 0' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShowChartIcon sx={{ fontSize: 15, color: '#2e7d32' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Cryo Survival Rate</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Oocyte (%)" value={cryoForm.srOocyte} onChange={setCF('srOocyte')} inputProps={{ min: 0, max: 100, step: '0.01' }} /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.srOocyteRemarks} onChange={setCF('srOocyteRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Embryo (%)" value={cryoForm.srEmbryo} onChange={setCF('srEmbryo')} inputProps={{ min: 0, max: 100, step: '0.01' } } /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.srEmbryoRemarks} onChange={setCF('srEmbryoRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Blastocyst (%)" value={cryoForm.srBlasto} onChange={setCF('srBlasto')} inputProps={{ min: 0, max: 100, step: '0.01' }} /></Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Remarks" value={cryoForm.srBlastoRemarks} onChange={setCF('srBlastoRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} /></Grid>
                  {!isReadOnly && <Grid item xs={12}><SaveRow onSave={saveCryoSurvival} saving={savingCryo.survival} label="Save Survival Rate" /></Grid>}
                </Grid>
              </AccordionDetails>
            </Accordion>
            */}

          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button onClick={() => setCryoModalOpen(false)} disabled={Object.values(savingCryo).some(Boolean)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ── CAN Modal ────────────────────────────────────────────────── */}
        <Dialog open={canModalOpen} onClose={() => { if (!savingCan) setCanModalOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{canEditId ? 'Edit CAN' : 'Add CAN'}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>One canister per CAN</Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>CAN Type *</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {CRYO_CAN_OPTIONS.map((opt) => (
                    <Box
                      key={opt} onClick={() => setCanForm((p) => ({ ...p, canType: opt }))}
                      sx={{ px: 2, py: 0.6, borderRadius: 1, cursor: 'pointer', fontSize: '0.8rem', border: `1.5px solid ${canForm.canType === opt ? pc : '#e0e0e0'}`, backgroundColor: canForm.canType === opt ? `${pc}12` : 'transparent', color: canForm.canType === opt ? pc : '#666', fontWeight: canForm.canType === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}
                    >
                      {opt}
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Canister No." value={canForm.canisterNo} onChange={(e) => setCanForm((p) => ({ ...p, canisterNo: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remarks" value={canForm.remarks}
                  onChange={(e) => setCanForm((p) => ({ ...p, remarks: e.target.value.toUpperCase() }))}
                  multiline minRows={2} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setCanModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setCanModalOpen(false)} disabled={savingCan} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveCan} variant="contained" disabled={savingCan} startIcon={savingCan ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 120 }}>
                  {savingCan ? 'Saving...' : 'Save CAN'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ── CANE Modal ───────────────────────────────────────────────── */}
        <Dialog open={caneModalOpen} onClose={() => { if (!savingCane) setCaneModalOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{caneEditId ? 'Edit CANE' : 'Add CANE'}</Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="CANE Number" value={caneForm.caneNo} onChange={(e) => setCaneForm((p) => ({ ...p, caneNo: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tag Color</InputLabel>
                  <Select
                    value={caneForm.tag} onChange={(e) => setCaneForm((p) => ({ ...p, tag: e.target.value }))} label="Tag Color"
                    sx={{ '& .MuiSelect-select': { display: 'flex !important', alignItems: 'center', py: '7.5px !important', minHeight: '36px !important', boxSizing: 'border-box' } }}
                    renderValue={(v) => v ? <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, lineHeight: 1 }}><Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[v] || '#ccc', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{v}</Box> : ''}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 220, overflowY: 'auto' } } }}
                  >
                    {CRYO_TAG_COLORS.map((c) => <MenuItem key={c} value={c} dense><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[c], border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{c}</Box></MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Goblet Color</InputLabel>
                  <Select
                    value={caneForm.goblet} onChange={(e) => setCaneForm((p) => ({ ...p, goblet: e.target.value }))} label="Goblet Color"
                    sx={{ '& .MuiSelect-select': { display: 'flex !important', alignItems: 'center', py: '7.5px !important', minHeight: '36px !important', boxSizing: 'border-box' } }}
                    renderValue={(v) => v ? <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, lineHeight: 1 }}><Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[v] || '#ccc', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{v}</Box> : ''}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 220, overflowY: 'auto' } } }}
                  >
                    {CRYO_TAG_COLORS.map((c) => <MenuItem key={c} value={c} dense><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[c], border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{c}</Box></MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remarks" value={caneForm.remarks}
                  onChange={(e) => setCaneForm((p) => ({ ...p, remarks: e.target.value.toUpperCase() }))}
                  multiline minRows={2} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setCaneModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setCaneModalOpen(false)} disabled={savingCane} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveCane} variant="contained" disabled={savingCane} startIcon={savingCane ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 120 }}>
                  {savingCane ? 'Saving...' : 'Save CANE'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Add Straws Modal (bulk) ─────────────────────────────────── */}
        <Dialog open={strawModalOpen} onClose={() => { if (!savingStrawBatch) setStrawModalOpen(false); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>Add Straws</Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2} sx={{ mb: strawBatchRows.length > 0 ? 1 : 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Number of Straws" value={strawBatchCount}
                  onChange={handleStrawBatchCountChange} inputProps={{ inputMode: 'numeric' }}
                  helperText={`Up to ${MAX_STRAW_BATCH} at a time`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDDateTimePicker
                  label="Preservation Date *" value={strawBatchPreservedAt}
                  onChange={(val) => { setStrawBatchPreservedAt(val); setStrawBatchPreserveError(false); }}
                />
                {strawBatchPreserveError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Preservation date is required.</Typography>}
              </Grid>
            </Grid>
            {strawBatchRows.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {strawBatchRows.map((row, idx) => (
                  <Grid key={idx} container spacing={1} alignItems="center" sx={{ border: '1px solid #f0f0f0', borderRadius: 1, m: 0, p: 0.75 }}>
                    <Grid item xs={3}>
                      <TextField fullWidth size="small" label="Straw No" value={row.strawNo} onChange={setStrawBatchRowField(idx, 'strawNo')} />
                    </Grid>
                    <Grid item xs={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Color</InputLabel>
                        <Select
                          value={row.color} onChange={setStrawBatchRowField(idx, 'color')} label="Color"
                          sx={{ '& .MuiSelect-select': { display: 'flex !important', alignItems: 'center', py: '7.5px !important', minHeight: '36px !important', boxSizing: 'border-box' } }}
                          renderValue={(v) => v ? <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, lineHeight: 1 }}><Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[v] || '#ccc', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{v}</Box> : ''}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 220, overflowY: 'auto' } } }}
                        >
                          {CRYO_TAG_COLORS.map((c) => <MenuItem key={c} value={c} dense><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[c], border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{c}</Box></MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="No of Embryos" value={row.embryoCount}
                        onChange={setStrawBatchRowField(idx, 'embryoCount')} inputProps={{ inputMode: 'numeric' }}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setStrawModalOpen(false)} disabled={savingStrawBatch} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
            <Button onClick={saveStrawBatch} variant="contained" disabled={savingStrawBatch || strawBatchRows.length === 0} startIcon={savingStrawBatch ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 140 }}>
              {savingStrawBatch ? 'Saving...' : `Save ${strawBatchRows.length || ''} Straw${strawBatchRows.length === 1 ? '' : 's'}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit / Mark-Used Straw Modal ─────────────────────────────── */}
        <Dialog open={strawEditModalOpen} onClose={() => { if (!savingStraw) setStrawEditModalOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>Edit Straw</Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Straw Number" value={strawForm.strawNo} onChange={(e) => setStrawForm((p) => ({ ...p, strawNo: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={strawForm.color} onChange={(e) => setStrawForm((p) => ({ ...p, color: e.target.value }))} label="Color"
                    sx={{ '& .MuiSelect-select': { display: 'flex !important', alignItems: 'center', py: '7.5px !important', minHeight: '36px !important', boxSizing: 'border-box' } }}
                    renderValue={(v) => v ? <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, lineHeight: 1 }}><Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[v] || '#ccc', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{v}</Box> : ''}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 220, overflowY: 'auto' } } }}
                  >
                    {CRYO_TAG_COLORS.map((c) => <MenuItem key={c} value={c} dense><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: CRYO_COLOR_MAP[c], border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />{c}</Box></MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="No of Embryos" value={strawForm.embryoCount}
                  onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*$/.test(v)) setStrawForm((p) => ({ ...p, embryoCount: v })); }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDDateTimePicker
                  label="Preservation Date *" value={strawForm.preservedAt}
                  onChange={(val) => { setStrawForm((p) => ({ ...p, preservedAt: val })); setStrawPreserveError(false); }}
                />
                {strawPreserveError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Preservation date is required.</Typography>}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={strawForm.used}
                      disabled={strawLockedUsed}
                      onChange={(e) => { setStrawForm((p) => ({ ...p, used: e.target.checked, usedAt: e.target.checked ? (p.usedAt || dayjs().format('YYYY-MM-DD HH:mm')) : null })); setStrawFieldError(false); }}
                    />
                  )}
                  label={strawLockedUsed ? 'Used (cannot be reverted)' : 'Mark as used'}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: 0 }}
                />
              </Grid>
              {strawForm.used && (
                <>
                  <Grid item xs={12}>
                    <MDDateTimePicker
                      label="Thawing (Used On) *" value={strawForm.usedAt} disabled={strawLockedUsed}
                      onChange={(val) => { setStrawForm((p) => ({ ...p, usedAt: val })); setStrawFieldError(false); }}
                    />
                    {/* Prefilled to now when "Mark as used" is checked — still
                        a free date/time picker, so it can be moved earlier or
                        later to match when the straw was actually thawed. */}
                    {strawFieldError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Used-on date/time is required.</Typography>}
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Day</InputLabel>
                      <Select
                        value={strawForm.rescuedDay} label="Day"
                        onChange={(e) => setStrawForm((p) => ({ ...p, rescuedDay: e.target.value }))}
                        sx={{ '& .MuiSelect-select': { minHeight: '36px !important', boxSizing: 'border-box', display: 'flex', alignItems: 'center' } }}
                      >
                        {STRAW_RESCUE_DAY_OPTIONS.map((d) => <MenuItem key={d} value={d}>DAY {d}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    {/* Not locked by strawLockedUsed — the rescue outcome is
                        usually only known after checking under the scope,
                        so this stays editable after "used" is saved. */}
                    <TextField fullWidth size="small" label="No of Rescued Embryos" value={strawForm.rescuedCount}
                      onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*$/.test(v)) setStrawForm((p) => ({ ...p, rescuedCount: v })); }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remarks" value={strawForm.remarks}
                  onChange={(e) => setStrawForm((p) => ({ ...p, remarks: e.target.value.toUpperCase() }))}
                  multiline minRows={2} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText="Optional"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setStrawEditModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setStrawEditModalOpen(false)} disabled={savingStraw} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveStraw} variant="contained" disabled={savingStraw} startIcon={savingStraw ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 120 }}>
                  {savingStraw ? 'Saving...' : 'Save Straw'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Semen Preservation Entry Modal ──────────────────────────────── */}
        <Dialog open={semenEntryModalOpen} onClose={() => { if (!savingSemenEntry) setSemenEntryModalOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{semenEntryEditId ? 'Edit Semen Preservation Entry' : 'Add Semen Preservation Entry'}</Typography>
            {!semenEntryEditId && <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>Choose how many vials to create below</Typography>}
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>Media Used for Cryopreservation</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {CRYO_MEDIA_OPTIONS.map((opt) => (
                    <Box key={opt} onClick={() => setSemenEntryForm((p) => ({ ...p, mediaUsed: opt }))}
                      sx={{ px: 2, py: 0.6, borderRadius: 1, cursor: 'pointer', fontSize: '0.8rem', border: `1.5px solid ${semenEntryForm.mediaUsed === opt ? pc : '#e0e0e0'}`, backgroundColor: semenEntryForm.mediaUsed === opt ? `${pc}12` : 'transparent', color: semenEntryForm.mediaUsed === opt ? pc : '#666', fontWeight: semenEntryForm.mediaUsed === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}>
                      {opt}
                    </Box>
                  ))}
                </Box>
                {semenEntryForm.mediaUsed === 'Others' && (
                  <TextField fullWidth size="small" label="Specify Media" value={semenEntryForm.mediaUsedOthers}
                    onChange={(e) => setSemenEntryForm((p) => ({ ...p, mediaUsedOthers: e.target.value }))} sx={{ mt: 1.5 }}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Can No" value={semenEntryForm.canNo}
                  onChange={(e) => setSemenEntryForm((p) => ({ ...p, canNo: e.target.value.toUpperCase() }))}
                  inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Canister No" value={semenEntryForm.canisterNo}
                  onChange={(e) => setSemenEntryForm((p) => ({ ...p, canisterNo: e.target.value.toUpperCase() }))}
                  inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDDateTimePicker
                  label="Date of Preservation *" value={semenEntryForm.preservedAt}
                  onChange={(val) => { setSemenEntryForm((p) => ({ ...p, preservedAt: val })); setSemenEntryPreserveError(false); }}
                />
                {semenEntryPreserveError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Date of Preservation is required.</Typography>}
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>Before Cryopreservation</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Count" value={semenEntryForm.beforeCount}
                  onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*(\.\d{0,2})?$/.test(v)) setSemenEntryForm((p) => ({ ...p, beforeCount: v })); }}
                  InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem', color: '#7b809a', whiteSpace: 'nowrap' }}>million/ml</Typography></InputAdornment> }}
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Motility" value={semenEntryForm.beforeMotility}
                  onChange={(e) => { const v = e.target.value; if (v === '' || (/^\d*(\.\d{0,2})?$/.test(v) && Number(v) <= 100)) setSemenEntryForm((p) => ({ ...p, beforeMotility: v })); }}
                  InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                  helperText="Optional"
                />
              </Grid>
              {!semenEntryEditId && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>No of Vials *</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[{ value: '1', label: 'Vial 1' }, { value: '2', label: 'Vial 1 & 2' }].map((opt) => (
                      <Box key={opt.value}
                        onClick={() => { setSemenEntryForm((p) => ({ ...p, vialCount: opt.value })); setSemenEntryVialCountError(false); }}
                        sx={{ px: 2, py: 0.6, borderRadius: 1, cursor: 'pointer', fontSize: '0.8rem', border: `1.5px solid ${semenEntryForm.vialCount === opt.value ? pc : '#e0e0e0'}`, backgroundColor: semenEntryForm.vialCount === opt.value ? `${pc}12` : 'transparent', color: semenEntryForm.vialCount === opt.value ? pc : '#666', fontWeight: semenEntryForm.vialCount === opt.value ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}>
                        {opt.label}
                      </Box>
                    ))}
                  </Box>
                  {semenEntryVialCountError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Select how many vials to create.</Typography>}
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remarks" value={semenEntryForm.remarks}
                  onChange={(e) => setSemenEntryForm((p) => ({ ...p, remarks: e.target.value.toUpperCase() }))}
                  multiline minRows={2} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText="Optional"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setSemenEntryModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setSemenEntryModalOpen(false)} disabled={savingSemenEntry} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveSemenEntry} variant="contained" disabled={savingSemenEntry} startIcon={savingSemenEntry ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 120 }}>
                  {savingSemenEntry ? 'Saving...' : 'Save Entry'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Semen Vial Modal (view / mark used) ─────────────────────────── */}
        <Dialog open={semenVialModalOpen} onClose={() => { if (!savingSemenVial) setSemenVialModalOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>
          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>Vial {semenVialForm.vialNo || '—'}</Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={semenVialForm.used}
                      disabled={semenVialLockedUsed}
                      onChange={(e) => { setSemenVialForm((p) => ({ ...p, used: e.target.checked, usedAt: e.target.checked ? (p.usedAt || dayjs().format('YYYY-MM-DD HH:mm')) : null })); setSemenVialFieldError(false); }}
                    />
                  )}
                  label={semenVialLockedUsed ? 'Used (cannot be reverted)' : 'Mark as used'}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: 0 }}
                />
              </Grid>
              {semenVialForm.used && (
                <>
                  <Grid item xs={12}>
                    <MDDateTimePicker
                      label="Thawing Date *" value={semenVialForm.usedAt} disabled={semenVialLockedUsed}
                      onChange={(val) => { setSemenVialForm((p) => ({ ...p, usedAt: val })); setSemenVialFieldError(false); }}
                    />
                    {semenVialFieldError && <Typography sx={{ fontSize: '0.68rem', color: '#d32f2f', mt: 0.5 }}>Thawing date/time is required.</Typography>}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>Method of Semen Preparation</Typography>
                    <RadioGroup row
                      value={semenVialForm.prepMethod}
                      onChange={(e) => setSemenVialForm((p) => ({ ...p, prepMethod: e.target.value }))}
                    >
                      {SEMEN_PREP_METHOD_OPTIONS.map((opt) => (
                        <FormControlLabel key={opt} value={opt}
                          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mr: 2, ml: 0 }}
                          control={<Radio size="small" />}
                          label={<Typography sx={{ fontSize: '0.78rem', display: 'inline' }}>{opt}</Typography>}
                        />
                      ))}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#344767', mb: 1 }}>After Thawing</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Post Wash Motile Sperm Count" value={semenVialForm.postWashCount}
                      onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*(\.\d{0,2})?$/.test(v)) setSemenVialForm((p) => ({ ...p, postWashCount: v })); }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem', color: '#7b809a', whiteSpace: 'nowrap' }}>million/ml</Typography></InputAdornment> }}
                      helperText="After-thawing count"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Motility" value={semenVialForm.afterMotility}
                      onChange={(e) => { const v = e.target.value; if (v === '' || (/^\d*(\.\d{0,2})?$/.test(v) && Number(v) <= 100)) setSemenVialForm((p) => ({ ...p, afterMotility: v })); }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem', color: '#7b809a' }}>%</Typography></InputAdornment> }}
                      helperText="After-thawing motility"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remarks" value={semenVialForm.remarks}
                  onChange={(e) => setSemenVialForm((p) => ({ ...p, remarks: e.target.value.toUpperCase() }))}
                  multiline minRows={2} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} helperText="Optional"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            {isReadOnly ? (
              <Button onClick={() => setSemenVialModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setSemenVialModalOpen(false)} disabled={savingSemenVial} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveSemenVial} variant="contained" disabled={savingSemenVial} startIcon={savingSemenVial ? null : <SaveIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, fontWeight: 600, minWidth: 120 }}>
                  {savingSemenVial ? 'Saving...' : 'Save Vial'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Embryo Biopsy Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={biopsyModalOpen} onClose={() => !savingBiopsy && setBiopsyModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BiopsyIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Embryo Biopsy</Typography>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3.5, pb: 1 }}>
            <Grid container spacing={2.5}>

              {/* Tubing Rate */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 1 }}>Tubing Rate</Typography>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)" value={biopsyForm.tubingRate}
                      onChange={setCFBiopsy('tubingRate')} inputProps={{ min: 0, max: 100, step: 0.1 }}
                      InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>%</Typography> }}
                    />
                  </Grid>
                  <Grid item xs={7}>
                    <TextField fullWidth size="small" label="Remarks (Optional)" value={biopsyForm.tubingRateRemarks} onChange={setCFBiopsy('tubingRateRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* Type of PGT */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 1 }}>Type of PGT</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {PGT_OPTIONS.map((opt) => (
                    <Box key={opt} onClick={() => setBFV('pgtType', biopsyForm.pgtType === opt ? '' : opt)}
                      sx={{ px: 2.5, py: 0.8, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', border: `1.5px solid ${biopsyForm.pgtType === opt ? pc : '#e0e0e0'}`, backgroundColor: biopsyForm.pgtType === opt ? `${pc}12` : 'transparent', color: biopsyForm.pgtType === opt ? pc : '#666', fontWeight: biopsyForm.pgtType === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}>
                      {opt}
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* Rate of EUPLOIDY */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 1 }}>Rate of EUPLOIDY</Typography>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)" value={biopsyForm.euploidyRate}
                      onChange={setCFBiopsy('euploidyRate')} inputProps={{ min: 0, max: 100, step: 0.1 }}
                      InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>%</Typography> }}
                    />
                  </Grid>
                  <Grid item xs={7}>
                    <TextField fullWidth size="small" label="Remarks (Optional)" value={biopsyForm.euploidyRateRemarks} onChange={setCFBiopsy('euploidyRateRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            {isReadOnly ? (
              <Button onClick={() => setBiopsyModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setBiopsyModalOpen(false)} disabled={savingBiopsy} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveBiopsy} disabled={savingBiopsy} variant="contained" sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, minWidth: 100 }}>
                  {savingBiopsy ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Embryo Transfer Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={transferModalOpen} onClose={() => !savingTransfer && setTransferModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TransferIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Embryo Transfer</Typography>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3.5, pb: 1 }}>
            <Grid container spacing={0}>

              {/* ── No. of Embryos ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>No. of Embryos</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {EMBRYO_COUNT_OPTIONS.map((opt) => (
                        <Box key={opt} onClick={() => setTFV('embryoCount', transferForm.embryoCount === opt ? '' : opt)}
                          sx={{ width: 38, height: 38, borderRadius: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.88rem', border: `1.5px solid ${transferForm.embryoCount === opt ? pc : '#e0e0e0'}`, backgroundColor: transferForm.embryoCount === opt ? pc : 'transparent', color: transferForm.embryoCount === opt ? 'white' : '#666', fontWeight: 700, transition: 'all 0.15s', userSelect: 'none' }}>
                          {opt}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={transferForm.embryoCountRemarks} onChange={setCFTransfer('embryoCountRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Days of Embryos ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Day of Embryos</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {EMBRYO_DAYS_OPTIONS.map((opt) => (
                        <Box key={opt} onClick={() => setTFV('embryoDays', transferForm.embryoDays === opt ? '' : opt)}
                          sx={{ px: 1.5, py: 0.6, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.78rem', border: `1.5px solid ${transferForm.embryoDays === opt ? pc : '#e0e0e0'}`, backgroundColor: transferForm.embryoDays === opt ? `${pc}12` : 'transparent', color: transferForm.embryoDays === opt ? pc : '#666', fontWeight: transferForm.embryoDays === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: pc } }}>
                          {opt}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={transferForm.embryoDaysRemarks} onChange={setCFTransfer('embryoDaysRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Type of Embryo ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Type of Embryo</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {EMBRYO_TYPE_OPTIONS.map((opt) => (
                        <Box key={opt} onClick={() => setTFV('embryoType', transferForm.embryoType === opt ? '' : opt)}
                          sx={{ px: 2, py: 0.7, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', border: `1.5px solid ${transferForm.embryoType === opt ? (opt === 'Fresh' ? '#1565c0' : '#6a1b9a') : '#e0e0e0'}`, backgroundColor: transferForm.embryoType === opt ? (opt === 'Fresh' ? '#e3f2fd' : '#f3e5f5') : 'transparent', color: transferForm.embryoType === opt ? (opt === 'Fresh' ? '#1565c0' : '#6a1b9a') : '#666', fontWeight: transferForm.embryoType === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none' }}>
                          {opt}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={transferForm.embryoTypeRemarks} onChange={setCFTransfer('embryoTypeRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Grade of Embryos ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Grade of Embryos</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {EMBRYO_GRADE_OPTIONS.map((opt) => (
                        <Box key={opt} onClick={() => setTFV('embryoGrade', transferForm.embryoGrade === opt ? '' : opt)}
                          sx={{ px: 2, py: 0.7, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', border: `1.5px solid ${transferForm.embryoGrade === opt ? '#e65100' : '#e0e0e0'}`, backgroundColor: transferForm.embryoGrade === opt ? '#fff3e0' : 'transparent', color: transferForm.embryoGrade === opt ? '#e65100' : '#666', fontWeight: transferForm.embryoGrade === opt ? 700 : 400, transition: 'all 0.15s', userSelect: 'none' }}>
                          {opt}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={transferForm.embryoGradeRemarks} onChange={setCFTransfer('embryoGradeRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Transfer Process ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Transfer Process</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {TRANSFER_PROCESS_OPTIONS.map((opt) => {
                    const isSmooth = opt === 'Smooth';
                    const active = transferForm.transferProcess === opt;
                    return (
                      <Box key={opt}
                        onClick={() => {
                          const next = active ? '' : opt;
                          const autoTech = next === 'Smooth' ? 'Quick and Uneventful' : '';
                          setTransferForm((p) => ({ ...p, transferProcess: next, transferTechnique: autoTech }));
                        }}
                        sx={{ px: 2.5, py: 0.8, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', border: `1.5px solid ${active ? (isSmooth ? '#2e7d32' : '#c62828') : '#e0e0e0'}`, backgroundColor: active ? (isSmooth ? '#e8f5e9' : '#fce4ec') : 'transparent', color: active ? (isSmooth ? '#2e7d32' : '#c62828') : '#666', fontWeight: active ? 700 : 400, transition: 'all 0.15s', userSelect: 'none' }}>
                        {opt}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* ── Transfer Technique ── */}
              <Grid item xs={12} sx={{ pt: 1.75 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                  Transfer Technique
                  {transferForm.transferProcess && <Typography component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: '#c62828' }}>*</Typography>}
                </Typography>
                {!transferForm.transferProcess ? (
                  <Typography sx={{ fontSize: '0.78rem', color: '#bdbdbd', fontStyle: 'italic' }}>Select Transfer Process first</Typography>
                ) : (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Select Technique</InputLabel>
                    <Select
                      value={transferForm.transferTechnique}
                      onChange={(e) => setTFV('transferTechnique', e.target.value)}
                      label="Select Technique"
                      sx={{ '& .MuiSelect-select': { display: 'flex !important', alignItems: 'center', py: '7.5px !important', minHeight: '36px !important', boxSizing: 'border-box' } }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: 'auto' } } }}
                    >
                      {(TRANSFER_TECHNIQUE_MAP[transferForm.transferProcess] || []).map((opt) => (
                        <MenuItem key={opt} value={opt} dense sx={{ fontSize: '0.82rem', whiteSpace: 'normal', lineHeight: 1.4, py: 0.75 }}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            {isReadOnly ? (
              <Button onClick={() => setTransferModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setTransferModalOpen(false)} disabled={savingTransfer} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveTransfer} disabled={savingTransfer} variant="contained" sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, minWidth: 100 }}>
                  {savingTransfer ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Implementation Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={implModalOpen} onClose={() => !savingImpl && setImplModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImplementationIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Implantation</Typography>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3.5, pb: 1 }}>
            <Grid container spacing={0}>

              {/* ── No. of Embryos Transferred ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>No. of Embryos Transferred</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Count"
                      value={implForm.embryosTransferred} onChange={setCFImpl('embryosTransferred')}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={implForm.embryosTransferredRemarks} onChange={setCFImpl('embryosTransferredRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── No. of Embryos Implanted ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>No. of Embryos Implanted</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Count"
                      value={implForm.embryosImplanted} onChange={setCFImpl('embryosImplanted')}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                    <TextField fullWidth size="small" placeholder="Optional" value={implForm.embryosImplantedRemarks} onChange={setCFImpl('embryosImplantedRemarks')} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }} />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Implantation ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Implantation</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {IMPL_OPTIONS.map((opt) => {
                    const active = implForm.implementation === opt;
                    const colMap = { Successful: { border: '#2e7d32', bg: '#e8f5e9', text: '#2e7d32' }, Failed: { border: '#c62828', bg: '#fce4ec', text: '#c62828' }, Partial: { border: '#e65100', bg: '#fff3e0', text: '#e65100' } };
                    const col = colMap[opt] || {};
                    return (
                      <Box key={opt} onClick={() => setIFV('implementation', active ? '' : opt)}
                        sx={{ px: 2.5, py: 0.8, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem', border: `1.5px solid ${active ? col.border : '#e0e0e0'}`, backgroundColor: active ? col.bg : 'transparent', color: active ? col.text : '#666', fontWeight: active ? 700 : 400, transition: 'all 0.15s', userSelect: 'none', '&:hover': { borderColor: col.border } }}>
                        {opt}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* ── Remarks ── */}
              <Grid item xs={12} sx={{ pt: 1.75 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Remarks</Typography>
                <TextField
                  fullWidth size="small" multiline rows={3} placeholder="Optional — additional notes about implementation"
                  value={implForm.remarks} onChange={setCFImpl('remarks')}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            {isReadOnly ? (
              <Button onClick={() => setImplModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setImplModalOpen(false)} disabled={savingImpl} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveImpl} disabled={savingImpl} variant="contained" sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, minWidth: 100 }}>
                  {savingImpl ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Outcome Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={outcomeModalOpen} onClose={() => { if (!savingOutcome) { setOutcomeModalOpen(false); setOutcomeEditId(null); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <OutcomeIcon sx={{ color: 'white', fontSize: 20 }} />
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>{outcomeEditId ? 'Edit Outcome' : 'Add Outcome'}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>{outcomeEditId ? 'Update pregnancy outcome record' : 'Record a new pregnancy outcome'}</Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3.5, pb: 1 }}>
            <Grid container spacing={0}>

              {/* ── Pregnancy Type ── */}
              <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                  Pregnancy Type
                  <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {PREGNANCY_TYPE_OPTIONS.map((opt) => {
                    const active = outcomeForm.pregnancyType === opt;
                    return (
                      <Box key={opt}
                        onClick={() => {
                          const next = active ? '' : opt;
                          setOutcomeForm((p) => ({
                            ...p,
                            pregnancyType: next,
                            subType: next === 'Biochemical' ? 'Biochemical' : '',
                            miscarriageWeeks: '',
                          }));
                        }}
                        sx={{
                          px: 2.5, py: 0.8, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem',
                          border: `1.5px solid ${active ? pc : '#e0e0e0'}`,
                          backgroundColor: active ? `${pc}15` : 'transparent',
                          color: active ? pc : '#666',
                          fontWeight: active ? 700 : 400,
                          transition: 'all 0.15s', userSelect: 'none',
                          '&:hover': { borderColor: pc },
                        }}>
                        {opt}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* ── Sub Type ── */}
              {outcomeForm.pregnancyType && (
                <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                    Sub Type
                    {outcomeForm.pregnancyType === 'Clinical' && (
                      <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                    )}
                  </Typography>
                  {outcomeForm.pregnancyType === 'Biochemical' ? (
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75,
                      px: 2.5, py: 0.8, borderRadius: 1.5,
                      border: `1.5px solid ${'#6a1b9a'}`, backgroundColor: '#f3e5f5', color: '#6a1b9a',
                      fontSize: '0.82rem', fontWeight: 700,
                    }}>
                      Biochemical
                      <Typography component="span" sx={{ fontSize: '0.62rem', color: '#9c27b0', fontWeight: 400 }}>(auto-selected)</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {CLINICAL_SUBTYPE_OPTIONS.map((opt) => {
                        const active = outcomeForm.subType === opt;
                        const colMap = {
                          'Live Birth': { border: '#2e7d32', bg: '#e8f5e9', text: '#2e7d32' },
                          Miscarriage: { border: '#c62828', bg: '#fce4ec', text: '#c62828' },
                          'Multiple Gestation': { border: '#1565c0', bg: '#e3f2fd', text: '#1565c0' },
                          Singleton: { border: '#e65100', bg: '#fff3e0', text: '#e65100' },
                        };
                        const col = colMap[opt] || { border: pc, bg: `${pc}15`, text: pc };
                        return (
                          <Box key={opt}
                            onClick={() => setOutcomeForm((p) => ({ ...p, subType: active ? '' : opt, miscarriageWeeks: '' }))}
                            sx={{
                              px: 1.5, py: 0.7, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.78rem',
                              border: `1.5px solid ${active ? col.border : '#e0e0e0'}`,
                              backgroundColor: active ? col.bg : 'transparent',
                              color: active ? col.text : '#666',
                              fontWeight: active ? 700 : 400,
                              transition: 'all 0.15s', userSelect: 'none',
                              '&:hover': { borderColor: col.border },
                            }}>
                            {opt}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              )}

              {/* ── Miscarriage Weeks (conditional) ── */}
              {outcomeForm.subType === 'Miscarriage' && (
                <Grid item xs={12} sx={{ py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                    Gestational Weeks
                    <Typography component="span" sx={{ color: '#c62828', ml: 0.5, fontSize: '0.75rem' }}>*</Typography>
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Weeks"
                    value={outcomeForm.miscarriageWeeks}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 42)) setOFV('miscarriageWeeks', v);
                    }}
                    inputProps={{ min: 1, max: 42, step: 1 }}
                    sx={{ width: 180 }}
                    error={!outcomeForm.miscarriageWeeks}
                    helperText={!outcomeForm.miscarriageWeeks ? 'Required — enter weeks (1–42)' : `Week ${outcomeForm.miscarriageWeeks}`}
                  />
                </Grid>
              )}

              {/* ── Remarks ── */}
              <Grid item xs={12} sx={{ pt: 1.75 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.5 }}>
                  Remarks
                  <Typography component="span" sx={{ fontSize: '0.65rem', color: '#9e9e9e', ml: 0.75, fontWeight: 400 }}>(Optional)</Typography>
                </Typography>
                <TextField
                  fullWidth size="small" multiline rows={3}
                  placeholder="Additional notes about the outcome..."
                  value={outcomeForm.remarks}
                  onChange={setCFOutcome('remarks')}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            {isReadOnly ? (
              <Button onClick={() => { setOutcomeModalOpen(false); setOutcomeEditId(null); }} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => { setOutcomeModalOpen(false); setOutcomeEditId(null); }} disabled={savingOutcome} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveOutcome} disabled={savingOutcome} variant="contained" sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, minWidth: 100 }}>
                  {savingOutcome ? 'Saving…' : (outcomeEditId ? 'Update' : 'Save')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
            Andrology Modal
         ═══════════════════════════════════════════════════════════════ */}
        <Dialog open={andrologyModalOpen} onClose={() => !savingAndrology && setAndrologyModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible', mt: 8 } }}>

          <Box sx={{ mx: 2.5, mt: '-28px', py: 1.5, px: 2.5, background: `linear-gradient(195deg, ${pc}, ${pc}cc)`, borderRadius: '8px', boxShadow: `0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px ${pc}80`, zIndex: 10, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AndrologyIcon sx={{ color: 'white', fontSize: 20 }} />
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>Andrology</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>Semen analysis parameters</Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ pt: 3.5, pb: 1 }}>
            <Grid container spacing={0}>

              {/* ── Section label: Basic Sperm Parameters ── */}
              <Grid item xs={12} sx={{ pb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 3, height: 14, borderRadius: 1, backgroundColor: pc }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: pc, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Basic Sperm Parameters</Typography>
                </Box>
              </Grid>

              {/* Count */}
              <Grid item xs={12} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Count</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Value"
                      value={andrologyForm.count}
                      onChange={makeNumAndrology('count')}
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#7b809a', whiteSpace: 'nowrap' }}>Million/ml</Typography></InputAdornment> }}
                      helperText="Sperm concentration"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Motility */}
              <Grid item xs={12} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Motility</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)"
                      value={andrologyForm.motility}
                      onChange={makePctAndrology('motility')}
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      helperText="Value in %, max 100"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Progressive Motility (optional) */}
              <Grid item xs={12} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Progressive Motility</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)"
                      value={andrologyForm.progressiveMotility}
                      onChange={makePctAndrology('progressiveMotility')}
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      helperText="Optional — value in %, max 100"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Morphology */}
              <Grid item xs={12} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>Morphology</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)"
                      value={andrologyForm.morphology}
                      onChange={makePctAndrology('morphology')}
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      helperText="Value in %, max 100"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Section label: SP DNA Fragmentation ── */}
              <Grid item xs={12} sx={{ pt: 2, pb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 3, height: 14, borderRadius: 1, backgroundColor: '#e65100' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#e65100', letterSpacing: '0.5px', textTransform: 'uppercase' }}>SP DNA Fragmentation</Typography>
                </Box>
              </Grid>

              {/* DFI value + live badge */}
              <Grid item xs={12} sx={{ py: 1.5, borderBottom: andrologyForm.dfi && getDFIClass(andrologyForm.dfi)?.label === 'High' ? '1px solid #f0f0f0' : 'none' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>DNA Fragmentation Index</Typography>
                    <TextField
                      fullWidth size="small" type="number" label="Value (%)"
                      value={andrologyForm.dfi}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || (/[eE+-]/.test(v) === false && /^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 100)) {
                          setAndrologyForm((p) => ({ ...p, dfi: v, ...(parseFloat(v) < 22 ? { dfiBeforeTreatment: '', dfiAfterTreatment: '', remarks: '' } : {}) }));
                        }
                      }}
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      helperText="Value in %, max 100"
                    />
                  </Grid>
                  {andrologyForm.dfi !== '' && getDFIClass(andrologyForm.dfi) && (
                    <Grid item xs={12} sm={7} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 0.5, sm: '36px !important' } }}>
                      {(() => {
                        const cls = getDFIClass(andrologyForm.dfi);
                        return (
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 2, py: 0.7,
                            borderRadius: 1.5, border: `1.5px solid ${cls.border}`, backgroundColor: cls.bg,
                          }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cls.color }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: cls.color }}>{cls.label}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: cls.color, opacity: 0.8 }}>
                              {parseFloat(andrologyForm.dfi) <= 15 ? '(≤ 15%)' : parseFloat(andrologyForm.dfi) <= 21 ? '(16–21%)' : '(≥ 22%)'}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Before/After Treatment + Remarks — only if DFI >= 22 */}
              {getDFIClass(andrologyForm.dfi)?.label === 'High' && (
                <>
                  <Grid item xs={12} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={5}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                          Before Treatment
                          <Typography component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: '#7b809a' }}>(%)</Typography>
                        </Typography>
                        <TextField
                          fullWidth size="small" type="number" label="Value (%)"
                          value={andrologyForm.dfiBeforeTreatment}
                          onChange={makePctAndrology('dfiBeforeTreatment')}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                          helperText="DFI before treatment"
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                          After Treatment
                          <Typography component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: '#7b809a' }}>(%)</Typography>
                        </Typography>
                        <TextField
                          fullWidth size="small" type="number" label="Value (%)"
                          value={andrologyForm.dfiAfterTreatment}
                          onChange={makePctAndrology('dfiAfterTreatment')}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                          helperText="DFI after treatment"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sx={{ pt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#344767', mb: 0.75 }}>
                      Remarks
                      <Typography component="span" sx={{ fontSize: '0.65rem', color: '#9e9e9e', ml: 0.75, fontWeight: 400 }}>(Optional)</Typography>
                    </Typography>
                    <TextField
                      fullWidth size="small" multiline rows={2}
                      placeholder="Notes about treatment..."
                      value={andrologyForm.remarks}
                      onChange={setCFAndrology('remarks')}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                </>
              )}

              {/* ── Section label: Reports ── */}
              <Grid item xs={12} sx={{ pt: 2, pb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 3, height: 14, borderRadius: 1, backgroundColor: '#6a1b9a' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6a1b9a', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Reports</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ borderBottom: '1px solid #f0f0f0' }}>
                <AndrologyReportUpload
                  label="Semen Analysis Report"
                  files={andrologyFiles.semen_analysis}
                  uploading={uploadingAndrology.semen_analysis}
                  onUpload={(file) => handleUploadAndrologyFile('semen_analysis', file)}
                />
              </Grid>
              <Grid item xs={12}>
                <AndrologyReportUpload
                  label="DNA Fragmentation Report"
                  files={andrologyFiles.dna_fragmentation}
                  uploading={uploadingAndrology.dna_fragmentation}
                  onUpload={(file) => handleUploadAndrologyFile('dna_fragmentation', file)}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            {isReadOnly ? (
              <Button onClick={() => setAndrologyModalOpen(false)} sx={{ textTransform: 'none', color: '#7b809a' }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => setAndrologyModalOpen(false)} disabled={savingAndrology} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
                <Button onClick={saveAndrology} disabled={savingAndrology} variant="contained" sx={{ textTransform: 'none', backgroundColor: pc, color: 'white !important', '&:hover': { backgroundColor: pc }, minWidth: 100 }}>
                  {savingAndrology ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Back */}
        <Box sx={{ mt: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ivf')} sx={{ color: '#7b809a', textTransform: 'none', fontWeight: 500, '&:hover': { color: pc } }}>
            Back to IVF Procedures
          </Button>
        </Box>

      </MDBox>
      <LoaderOverlay open={loading} />
      <Footer />

      {/* Hidden print component — rendered off-screen for useReactToPrint */}
      <IVFLabPrint
        ref={printRef}
        patientName={patientName}
        spouseName={spouseName}
        patientCode={patientCode}
        coupleCode={coupleCode}
        spouseCode={spouseCode}
        procedureCode={procedureCode}
        procedureDate={procedureDate}
        procedureCategory={procedureCategory}
        procedureSubCategory={procedureSubCategory}
        procedureStatus={procedureStatus}
        mobile={mobile}
        allRecords={allRecords}
        patientPerson={patientPerson}
        spousePerson={spousePerson}
        donorPerson={donorPerson}
        semenUserType={semenUserType}
      />

    </DashboardLayout>
  );
}

export default IVFLabPerformance;
